// Main Canvas Editor Hook
// Adapted from Canva Clone - Apache 2.0 License

import { API_BASE } from '../../../../config.js';
import {
  ActiveSelection, Circle, CircleBrush, config, Ellipse, Gradient, FabricImage,
  FabricObject, Group, Line, Path, Pattern, PatternBrush, PencilBrush, Point,
  Polygon, Rect, Shadow, SprayBrush, Textbox, Triangle, filters, iMatrix,
  loadSVGFromString, util,
} from 'fabric';
import { useCallback, useState, useMemo, useRef, useEffect } from 'react';
import i18n from '../../../../i18n';
import { initAligningGuidelines, initLockBadges } from '../lib/aligningGuidelines';
import { ensureFont, ensureFontsForDesign } from '../fonts/fontLoader';
// Poster-project smart objects: parametric recipes that emit plain fabric
// Groups tagged with {tool, version, params} on `data` (already in JSON_KEYS
// so re-editability survives save/reload). Importing registers all recipes.
import {
  generateSmartObject,
  regenerateSmartObject,
  smartObjectData,
  getRecipe,
  resolveFill,
  resolveFillSync,
} from '../../../../posterTools';
// Text-inspector engine: the effect slot model lives in effectDefs (single
// source of truth shared with the Style tab's tiles/option cards, so preview
// and canvas can never drift); texture fills persist as IDs and re-derive
// their URL through fillPresets.textureUrl.
import {
  EFFECT_DEFS,
  SLOT_RESET,
  contourOutlineWanted,
  diecutStrokeReach,
  effectDefaults,
  luminance,
  rgba,
  solidFromFabricFill,
} from '../inspector/text/effectDefs';
// Universal effects (U1): the capability matrix is the ONE gate table for
// the object-generic verbs (applyEffect family) — kind × effect → native |
// fx | unavailable. Text keeps its own path (applyTextEffect, byte-
// unchanged); the bag dual-reads via effectsBagOf.
import {
  CONTOUR_FX_STEP_ID,
  effectCapability,
  effectsBagOf,
  objectKindOf,
} from '../lib/fx/capabilityMatrix';
import { textureUrl } from '../inspector/text/fillPresets';
// The forward (derive/resolve) half of a style application — shared with the
// style-preview renderer so thumbnails can never drift from the canvas.
import { composeRecipePatch } from '../inspector/text/composeRecipePatch';
// Phonics colour-coding: PURE range computation (no fabric), so the whole
// heuristic is unit-testable without a canvas. See lib/phonics.js for the
// fabric index-space evidence that its flat ranges are what setSelectionStyles
// consumes.
import { phonicsPlan, PHONICS_MODES } from '../lib/phonics';
// Live effects engine: per-INSTANCE _render interception (fxRender is the ONE
// module that touches fabric internals). The recipe rides data.fx — data is
// in JSON_KEYS, so it round-trips; the instance patch does not (fx re-attach
// walks run on every path that rebuilds instances: load, undo/redo, clone).
// fxBevel/fxExtrude imports register the effects (extrude is kind:'source' —
// its three.js payload stays behind a dynamic import inside applyAsync).
import {
  ensureFxRender, removeFxRender, renderDiecutPlain, renderContourPlain, fxNotifyViewportChange,
} from '../lib/fx/fxRender';
import { getFx } from '../lib/fx/fxRegistry';
import { POSTER_LIGHT_DEFAULTS, sanitizePosterLight } from '../lib/fx/fxPosterLight';
import '../lib/fx/fxBevel';
import '../lib/fx/fxExtrude';
// Sticker shape modes + the sticker's own bevel (sticker-shapes lane): a
// kind:'filter' step the engine mirrors from the sticker recipe's
// shape/beveled params — see syncShapeFxStep.
import '../lib/fx/fxStickerShape';
// Runtime resolution ceilings + the lazy baker map (re-bake-on-scale hook).
import { getRenderCaps, BAKE_STAND_CAPS } from '../lib/renderCaps';
import { bakerFor } from '../lib/exportUtils';
// On-canvas 3D trackball (fx-extrude objects + baked text3d images): a
// runtime-only custom control — attached wherever the fx walkers re-attach
// seams and on selection, driven through the facade slice handed over in
// setJoystick3dContext. Never serialized, never exported.
import {
  ensureJoystick3d,
  syncJoystick3dSelection,
  setJoystick3dContext,
  teardownJoystick3d,
} from '../lib/fx/joystick3d';

// Rival-displacement map for live effects. EMPTY since 2026-07-16: bevel and
// extrude now COMPOSE instead of displacing each other — both steps ride
// data.fx together, the extrude bake consumes the bevel's look as geometric
// edge rounding (fxExtrude consumes:['bevel']; the seam skips the consumed
// 2D filter), and dropping extrude to 0 brings the untouched 2D bevel back.
// The mechanism stays for future genuinely-exclusive pairs; extrude↔curve
// clearing is separate (inline in patchTextFx — curves still can't be 3D).
// Module scope on purpose: patchTextFx is a hoisted internal that runs
// before the statements after the editor literal's `return` would.
const FX_EXCLUSIVE = {};

// Constants
const JSON_KEYS = [
  "name",
  "gradientAngle",
  "selectable",
  "hasControls",
  "linkData",
  "editable",
  "extensionType",
  "extension",
  // Icon metadata — without these in toJSON, a saved-then-reloaded icon
  // came back as a plain image: the recolor control disappeared and the
  // photo-only filter/crop toolbar wrongly appeared on it.
  "isIcon",
  "iconName",
  "iconColor",
  // Text outline renders the stroke around the glyphs instead of eating
  // into them; must round-trip with the design.
  "paintFirst",
  // Lock state: fabric's default toObject() omits every lock* prop, so a
  // locked object silently unlocked after save/reload without these.
  "locked",
  "lockMovementX",
  "lockMovementY",
  "lockScalingX",
  "lockScalingY",
  "lockRotation",
  "hoverCursor",
  // Curved text: the slider re-hydrates from curveAmount, the tiles from
  // curveShape; the path props control how glyphs sit on the curve (path
  // itself serializes natively). Without curveShape a saved circle/wave
  // re-derived as an arc on the next geometry refresh.
  "curveAmount",
  "curveShape",
  "pathSide",
  "pathStartOffset",
  "pathAlign",
  // Frame masks (image-in-shape): the clipPath serializes natively; this
  // remembers WHICH frame so the picker re-hydrates.
  "frameShape",
  // Smart-object / template metadata (poster-project integration): data
  // carries {tool, version, params} for parametric re-edit; role + slotId
  // are the template slot contract. Without these, one save cycle strips
  // re-editability from every smart object and template slot.
  "data",
  "role",
  "slotId"
];

// Per-character style keys the rich-text UI owns. Every one is in fabric's
// own styleProperties whitelist (anything outside it is ignored per char);
// deltaY rides along so "Reset character styling" also lifts super/subscript.
// fontFamily joined 2026-07-19 (ELA-tools lane): the standard controls are
// selection-aware now, so per-char fonts exist and must clear/mirror too.
// NOT in JSON_KEYS on purpose: Text.toObject() always emits `styles` itself as
// a {start,end,style} range array.
const RICH_TEXT_STYLE_KEYS = [
  'fill',
  'fontFamily',
  'fontSize',
  'fontWeight',
  'fontStyle',
  'underline',
  'linethrough',
  'textBackgroundColor',
  'deltaY',
];

// Per-char keys that still mean something on a live-3D (fx-extrude) text:
// the bake maps per-glyph FILLS into per-glyph materials, but it cuts ONE
// font at ONE size on ONE baseline — metric keys route whole-object there
// (the re-bake picks the new object prop up), exactly the gate the old
// Emphasize section enforced with disabled buttons.
const SELECTION_3D_SAFE_KEYS = new Set(['fill', 'textBackgroundColor']);

// Auto-fit search bounds (px).
const AUTOFIT_MIN = 6;
const AUTOFIT_MAX = 400;

// Locking keeps the object SELECTABLE (so clicking it offers Unlock) but
// freezes movement/scale/rotation. The old scheme (selectable:false) made
// locked objects unreachable outside the Layers panel — teachers locked a
// background and couldn't figure out why it wouldn't respond.
function setObjectLocked(object, locked) {
  object.set({
    locked: !!locked,
    lockMovementX: !!locked,
    lockMovementY: !!locked,
    lockScalingX: !!locked,
    lockScalingY: !!locked,
    lockRotation: !!locked,
    hasControls: !locked,
    hoverCursor: locked ? 'not-allowed' : null,
    selectable: true,
    evented: true,
  });
}

// Frame-mask geometry (image-in-shape). Pure: (crop-window dims, shape id) →
// centered relative clipPath. Shared by applyFrameShape and
// replaceActiveImage (a replaced photo keeps its frame, re-fit to the new
// source's crop window).
function makeFrameClip(w, h, shapeId) {
  const size = Math.min(w, h);
  const common = { originX: 'center', originY: 'center' };
  switch (shapeId) {
    case 'circle':
      return new Circle({ ...common, radius: size / 2 });
    case 'rounded':
      return new Rect({ ...common, width: w, height: h, rx: size * 0.15, ry: size * 0.15 });
    case 'hexagon': {
      const pts = [];
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI / 3) * i - Math.PI / 2;
        pts.push({ x: (size / 2) * Math.cos(a), y: (size / 2) * Math.sin(a) });
      }
      return new Polygon(pts, { ...common });
    }
    case 'star': {
      const pts = [];
      const outer = size / 2;
      const inner = size / 5;
      for (let i = 0; i < 10; i++) {
        const r = i % 2 === 0 ? outer : inner;
        const a = (Math.PI / 5) * i - Math.PI / 2;
        pts.push({ x: r * Math.cos(a), y: r * Math.sin(a) });
      }
      return new Polygon(pts, { ...common });
    }
    case 'heart': {
      const path = new Path(
        'M 150 30 C 100 0, 0 50, 0 120 C 0 200, 150 280, 150 280 C 150 280, 300 200, 300 120 C 300 50, 200 0, 150 30 Z',
        { ...common },
      );
      const s = size / 300;
      path.set({ scaleX: s, scaleY: s });
      return path;
    }
    default:
      return null;
  }
}

// Swappable shape silhouettes (owner directive §3 — "Change shape"). ONE pure
// factory: a shape-type id → a bare PLAIN fabric primitive at natural geometry
// (no paint / position — swapShapeType carries those from the object being
// replaced). Type ids match the left Elements panel's shape set 1:1, so the
// picker (ObjectSwapModal) and the swap agree. Exportability law: every branch
// returns a STOCK fabric class (Rect / Circle / Ellipse / Triangle / Polygon /
// Path) — no custom subclass ever rides into a design.
function swapPolygonPoints(sides, radius, cx, cy) {
  const pts = [];
  for (let i = 0; i < sides; i += 1) {
    const a = ((2 * Math.PI) / sides) * i - Math.PI / 2;
    pts.push({ x: cx + radius * Math.cos(a), y: cy + radius * Math.sin(a) });
  }
  return pts;
}
function swapStarPoints(spikes, outerR, innerR, cx, cy) {
  const pts = [];
  for (let i = 0; i < spikes * 2; i += 1) {
    const r = i % 2 === 0 ? outerR : innerR;
    const a = (Math.PI / spikes) * i - Math.PI / 2;
    pts.push({ x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) });
  }
  return pts;
}
function buildSwapShape(type) {
  switch (type) {
    case 'rectangle': return new Rect({ width: 200, height: 200 });
    case 'softRect': return new Rect({ width: 200, height: 200, rx: 30, ry: 30 });
    case 'circle': return new Circle({ radius: 100 });
    case 'ellipse': return new Ellipse({ rx: 120, ry: 80 });
    case 'triangle': return new Triangle({ width: 200, height: 200 });
    case 'invTriangle': return new Polygon([{ x: 0, y: 0 }, { x: 200, y: 0 }, { x: 100, y: 200 }], {});
    case 'diamond': return new Polygon([{ x: 100, y: 0 }, { x: 200, y: 100 }, { x: 100, y: 200 }, { x: 0, y: 100 }], {});
    case 'pentagon': return new Polygon(swapPolygonPoints(5, 100, 100, 100), {});
    case 'hexagon': return new Polygon(swapPolygonPoints(6, 100, 100, 100), {});
    case 'octagon': return new Polygon(swapPolygonPoints(8, 100, 100, 100), {});
    case 'star4': return new Path('M 100 0 L 120 80 L 200 100 L 120 120 L 100 200 L 80 120 L 0 100 L 80 80 Z', {});
    case 'star': return new Polygon(swapStarPoints(5, 100, 40, 100, 100), {});
    case 'star6': return new Polygon(swapStarPoints(6, 100, 50, 100, 100), {});
    case 'star8': return new Polygon(swapStarPoints(8, 100, 50, 100, 100), {});
    case 'heart': return new Path('M 150 30 C 100 0, 0 50, 0 120 C 0 200, 150 280, 150 280 C 150 280, 300 200, 300 120 C 300 50, 200 0, 150 30 Z', {});
    case 'cross': return new Path('M 100 0 L 200 0 L 200 100 L 300 100 L 300 200 L 200 200 L 200 300 L 100 300 L 100 200 L 0 200 L 0 100 L 100 100 Z', {});
    case 'checkmark': return new Path('M 20 100 L 70 150 L 180 30 L 160 10 L 70 110 L 40 80 Z', {});
    case 'xmark': return new Path('M 40 0 L 100 60 L 160 0 L 200 40 L 140 100 L 200 160 L 160 200 L 100 140 L 40 200 L 0 160 L 60 100 L 0 40 Z', {});
    case 'cloud': return new Path('M 60 120 C 20 120 0 90 20 60 C 0 30 30 0 70 10 C 90 -10 130 0 140 30 C 170 10 210 30 200 60 C 230 70 230 110 200 120 Z', {});
    case 'lightning': return new Path('M 100 0 L 60 80 L 90 80 L 50 160 L 120 70 L 90 70 L 130 0 Z', {});
    case 'crescent': return new Path('M 100 0 C 155 0 200 45 200 100 C 200 155 155 200 100 200 C 120 180 130 145 130 100 C 130 55 120 20 100 0 Z', {});
    case 'speechBubble': return new Path('M 50 0 L 250 0 Q 300 0, 300 50 L 300 150 Q 300 200, 250 200 L 100 200 L 50 250 L 75 200 L 50 200 Q 0 200, 0 150 L 0 50 Q 0 0, 50 0 Z', {});
    case 'parallelogram': return new Path('M 50 0 L 250 0 L 200 100 L 0 100 Z', {});
    case 'trapezoid': return new Path('M 50 0 L 200 0 L 250 100 L 0 100 Z', {});
    case 'arrow': return new Path('M 0 50 L 250 50 L 250 25 L 300 50 L 250 75 L 250 50 Z', {});
    case 'doubleArrow': return new Path('M 0 50 L 50 25 L 50 40 L 250 40 L 250 25 L 300 50 L 250 75 L 250 60 L 50 60 L 50 75 Z', {});
    default: return null;
  }
}

// Legacy designs saved locks as selectable:false — migrate them to the new
// selectable-but-frozen scheme when a design loads.
/**
 * Smart objects scale proportionally by default: hide the side-stretch
 * handles unless the user explicitly unlocked (data.aspectLocked === false).
 * Non-smart objects are left untouched.
 */
function applySmartAspectLock(obj) {
  const data = smartObjectData(obj);
  if (!data) return;
  const locked = data.aspectLocked !== false;
  obj.setControlsVisibility({ ml: !locked, mr: !locked, mt: !locked, mb: !locked });
}

/**
 * Live on-canvas cell editing (smart tables): the table recipe tags its text
 * children with data.tableCell = {kind:"title"|"header"|"rowLabel"|"cell",
 * row?, col?}. Fabric 6 only reports group children in event subTargets when
 * subTargetCheck is true — enable it on the group (and every nested group,
 * so the target search can descend all the way to the tagged text) whenever
 * any descendant carries a tableCell tag. Groups without tagged children are
 * left untouched (no behavior change for non-table smart objects).
 */
function enableTableCellHitTesting(group) {
  if (typeof group?.getObjects !== 'function') return;
  const groups = [group];
  let found = false;
  for (let i = 0; i < groups.length; i++) {
    groups[i].getObjects().forEach((child) => {
      if (child?.data?.tableCell) found = true;
      if (typeof child?.getObjects === 'function') groups.push(child);
    });
  }
  if (!found) return;
  groups.forEach((g) => { g.subTargetCheck = true; });
}

// One-stop smart-object preparation, run wherever smart objects enter the
// canvas (insert / load / regenerate): proportional-scale lock + table-cell
// child hit-testing. Non-smart objects pass through untouched.
function prepareSmartObject(obj) {
  applySmartAspectLock(obj);
  if (smartObjectData(obj)) enableTableCellHitTesting(obj);
}

function migrateLegacyLocks(canvas) {
  canvas.getObjects().forEach((obj) => {
    if (obj.name === 'clip') return;
    if (obj.locked === true || obj.selectable === false) {
      setObjectLocked(obj, true);
    }
  });
}

const FILL_COLOR = "rgba(0,0,0,1)";
const STROKE_COLOR = "rgba(0,0,0,1)";
const STROKE_WIDTH = 2;
const STROKE_DASH_ARRAY = [];
const FONT_FAMILY = "Arial";
const FONT_SIZE = 32;
const FONT_WEIGHT = 400;

// Default canvas size - 24x36 inches at 72 DPI
const DEFAULT_WIDTH = 1728;
const DEFAULT_HEIGHT = 2592;

const CIRCLE_OPTIONS = {
  radius: 150,
  left: 100,
  top: 100,
  fill: FILL_COLOR,
  stroke: STROKE_COLOR,
  strokeWidth: STROKE_WIDTH,
};

const RECTANGLE_OPTIONS = {
  left: 100,
  top: 100,
  fill: FILL_COLOR,
  stroke: STROKE_COLOR,
  strokeWidth: STROKE_WIDTH,
  width: 300,
  height: 300,
  angle: 0,
};

const TRIANGLE_OPTIONS = {
  left: 100,
  top: 100,
  fill: FILL_COLOR,
  stroke: STROKE_COLOR,
  strokeWidth: STROKE_WIDTH,
  width: 300,
  height: 300,
  angle: 0,
};

const DIAMOND_OPTIONS = {
  left: 100,
  top: 100,
  fill: FILL_COLOR,
  stroke: STROKE_COLOR,
  strokeWidth: STROKE_WIDTH,
  width: 400,
  height: 400,
  angle: 0,
};

const TEXT_OPTIONS = {
  // NOTE: no `type` key — v6 made .type a getter-only accessor; passing it in
  // constructor options throws (v5 silently stored it, it was never needed).
  left: 100,
  top: 100,
  fill: FILL_COLOR,
  fontSize: FONT_SIZE,
  fontFamily: FONT_FAMILY,
};

// Utility functions
function isTextType(type) {
  // v6 live objects report 'itext' (no hyphen); legacy raw JSON says 'i-text'.
  const t = String(type || '').toLowerCase();
  return t === "text" || t === "i-text" || t === "itext" || t === "textbox";
}

// ---------- Text-inspector engine helpers ----------
// Module-level: shared by the editor API, the canvas event wiring and both
// design-load paths. Recipes ride the data bag (JSON_KEYS carries `data`):
//   data.textFill          FillValue — the fill's single source of truth
//   data.textFillLastSolid last solid color, so the Color pill can restore it
//   data.textEffects       { shadow: {id,params}|null, stroke: {id,params}|null }
//   data.highlight         { color, fade }|null
//   data.letterCase / data.originalText  reversible case

// Texture PORTABILITY LAW: data.textFill stores {type:'texture', id} — an ID,
// never a URL (URLs are deployment-specific). The URL is re-derived here at
// apply/load time so a saved design renders its textures in any deployment.
// Print-sharpness base for texture tiles. The workspace is 72dpi-based
// (getEffectiveDPI: 72/scale) and print exports rasterize at 300 — patterns
// repeat in OBJECT space, so the export multiplier (300/72) magnifies tile
// pixels ~4.2×. Shrinking the tile by 72/300 makes one tile pixel ≈ one
// print pixel at "Pattern size 100%": a 1024 tile repeats every ~3.4" of
// print at full 300dpi instead of rendering at a soft ~72dpi.
const TEXTURE_PRINT_BASE_SCALE = 72 / 300;

function resolveTextFillPaint(fillValue) {
  // Two texture shapes by law: manifest {id} re-derives its URL here (the
  // ids-not-URLs portability law) and user-imported {src} carries its own
  // data URL (PaintPicker v2 Import Texture — deployment-independent by
  // construction, so the same portability holds).
  if (fillValue?.type === 'texture' && (fillValue.id || fillValue.src)) {
    return resolveFill({
      type: 'texture',
      url: fillValue.src ?? textureUrl(fillValue.id),
      scale: (fillValue.scale ?? 1) * TEXTURE_PRINT_BASE_SCALE,
    });
  }
  return resolveFill(fillValue);
}

/** First solid color hiding in a FillValue recipe (string | solid | gradient). */
function solidFromFillValue(fillValue) {
  if (typeof fillValue === 'string') return fillValue;
  if (fillValue?.type === 'solid') return fillValue.color;
  return fillValue?.stops?.[0]?.color ?? null;
}

/** Best-effort FillValue for a live fabric fill that predates the inspector
 * (legacy gradients keep their stops; the angle is not recoverable). */
function fillValueFromLive(fill) {
  if (fill?.colorStops?.length) {
    return {
      type: fill.type === 'radial' ? 'radial-gradient' : 'linear-gradient',
      stops: fill.colorStops.map((s) => ({ offset: s.offset, color: s.color })),
    };
  }
  return {
    type: 'solid',
    color: typeof fill === 'string' && fill !== 'transparent' ? fill : '#000000',
  };
}

/** derive(params, fontSize, ctx) context: solid approximation of the object's
 * paint (Echo inherits it, Outline auto-contrasts against it). While Hollow
 * routes the recipe to the stroke the live fill is 'transparent' — fall back
 * to the recipe's solid. */
function textEffectCtx(obj) {
  const live = obj.fill;
  const liveSolid = (typeof live === 'string' && live !== 'transparent') || live?.colorStops?.length
    ? solidFromFabricFill(live)
    : null;
  return { fillColor: liveSolid ?? solidFromFillValue(obj.data?.textFill) ?? '#000000' };
}

/** Put the recipe's paint back on `fill` (hollow removal / displacement).
 * Async because textures resolve; a dead texture falls back to a solid —
 * never leave the fill 'transparent' behind. */
/** Does this effect take the object's own fill away while it's active?
 * Two flavors, both of which must hand the fill back on removal:
 *   fillRouting:'stroke' — hollow: the fill recipe is PAINTED onto the stroke.
 *   clearsFill           — trace: the fill is blanked to 'transparent' and the
 *                          effect owns its stroke paint outright (a tracing
 *                          guide must not inherit the headline's gradient).
 * Gating the restore on fillRouting alone left trace-removal with a
 * transparent = invisible object. */
function holdsFillHostage(def) {
  return def?.fillRouting === 'stroke' || !!def?.clearsFill;
}

/** The FillValue held by the active stroke effect's OWN paint param, if it
 * declares one (sticker's `borderColor`). This is the sibling of
 * fillRouting:'stroke': fillRouting paints data.textFill (the TEXT's recipe)
 * onto the stroke, whereas a strokeFillParam effect owns paint that is
 * independent of the text fill — a foil border around plain black letters.
 *
 * Keyed off the DEF MARKER, never off an effect id, and it reads a plain data
 * bag, so it works identically on serialized JSON (pre-parse retarget) and on
 * a live fabric object (post-load rehydrate).
 * Returns null unless the value is a live texture recipe worth resolving. */
function strokeFillTextureOf(data) {
  // Dual-read (universal-effects U1). Inert for non-text stickers today —
  // their plate paints its own texture in the fx step, no native stroke
  // Pattern to retarget — but the read must never miss a future carrier.
  const entry = (data?.effects ?? data?.textEffects)?.stroke;
  const def = EFFECT_DEFS[entry?.id];
  if (!def?.strokeFillParam) return null;
  const value = entry?.params?.[def.strokeFillParam];
  // manifest {id} or user-imported {src} — both are live texture recipes
  return value?.type === 'texture' && (value.id || value.src) ? value : null;
}

async function restoreTextFill(object) {
  let paint;
  try {
    paint = await resolveTextFillPaint(object.data?.textFill);
  } catch {
    paint = undefined;
  }
  object.set('fill',
    paint
    ?? solidFromFillValue(object.data?.textFill)
    ?? object.data?.textFillLastSolid
    ?? '#000000');
}

// Reversible letter case: transforms always run over data.originalText (same
// regexes as the legacy destructive transformTextCase, which stays for the
// toolbar — the panel never calls it).
function transformCase(text, mode) {
  switch (mode) {
    case 'upper': return String(text).toUpperCase();
    case 'lower': return String(text).toLowerCase();
    case 'title': return String(text).replace(/\b\w/g, (c) => c.toUpperCase());
    default: return text;
  }
}

// ---------- Lists ----------
// Fabric has no list model, so a list is a DERIVATION over the text: the
// teacher's authored lines ride data.listSource and obj.text renders them with
// prefixes. Same reversible contract as letter case (data.originalText).
// Modes (R2 ribbon lane added the two teaching flavors, concept §3):
//   'bullet'   • item            'number'  1. item
//   'checkbox' □ item (routines) 'letter'  A. item (choices)
const LIST_MODES = ['bullet', 'number', 'checkbox', 'letter'];
const BULLET = '• ';
// U+25A1 WHITE SQUARE — broad glyph coverage in the school faces (U+2610
// BALLOT BOX is spottier); visually the unchecked routine box.
const CHECKBOX = '□ ';
// Only the prefixes WE emit (plus the marks a teacher plausibly typed in
// their place: bullets, checked/unchecked boxes, check marks). Deliberately
// excludes '-' / '*': stripping those would make List → None lossy for text
// we never prefixed.
const LIST_PREFIX_RE = /^[ \t]*(?:[•‣▪□☐☑✓]|\d+[.)])[ \t]+/;
// Letter prefixes ("A." / "b)" / "AA.") strip ONLY while the letter mode is
// in play: unlike •/□/1., a leading "A. " is plausible CONTENT ("A. Lincoln")
// — stripping it under every mode would make Bullets lossy. Two-letter runs
// must be uppercase (the shape we emit past Z); "It." stays untouched.
const LETTER_PREFIX_RE = /^[ \t]*(?:[A-Za-z]|[A-Z]{2})[.)][ \t]+/;

function stripListPrefix(line, mode) {
  const base = line.replace(LIST_PREFIX_RE, '');
  return mode === 'letter' ? base.replace(LETTER_PREFIX_RE, '') : base;
}

/** 1 → A, 26 → Z, 27 → AA … (spreadsheet columns — uppercase by design). */
function letterFor(n) {
  let s = '';
  while (n > 0) {
    n -= 1;
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26);
  }
  return s;
}

/** Prefix each non-blank line. Blank lines stay blank and don't consume a
 * number/letter — teachers use them as spacers between items. */
function renderList(source, mode) {
  if (!LIST_MODES.includes(mode)) return String(source);
  let n = 0;
  return String(source)
    .split('\n')
    .map((line) => {
      const clean = stripListPrefix(line, mode);
      if (!clean.trim()) return clean;
      if (mode === 'bullet') return `${BULLET}${clean}`;
      if (mode === 'checkbox') return `${CHECKBOX}${clean}`;
      if (mode === 'letter') return `${letterFor(++n)}. ${clean}`;
      return `${++n}. ${clean}`;
    })
    .join('\n');
}

/** The authored text behind a rendered list: strip one prefix per line, so
 * re-applying can't double it and the numbers/letters re-derive from scratch.
 * `mode` = the list mode whose prefixes the text carries (letter prefixes are
 * only stripped under 'letter' — see LETTER_PREFIX_RE). */
function listSourceFrom(text, mode) {
  return String(text ?? '')
    .split('\n')
    .map((line) => stripListPrefix(line, mode))
    .join('\n');
}

// ---------- Phonics (colour-coding) ----------
// MODULE level, like every other helper the canvas event handlers reach for
// (transformCase/renderList/refreshTextGeometryEffects): the handlers are
// registered from a different useEffect closure than buildEditor, so an
// internal there would be out of scope for them.

/** The ranges a given mode paints on the object's CURRENT text.
 * Hands phonicsPlan fabric's `_text` (the GRAPHEME array) rather than the raw
 * string: flat grapheme offsets are exactly the index space setSelectionStyles
 * consumes, and `text.length` would drift from it on emoji/combining marks —
 * the same reason textSelectionRange measures with `_text`. */
function phonicsRangesFor(object, mode) {
  if (!mode || mode === 'none') return [];
  const chars = object._text?.length ? object._text : Array.from(object.text || '');
  return phonicsPlan(mode, chars, object.data?.phonics?.colors);
}

/** Re-derive the colour-coding from data.phonics + the CURRENT text.
 *
 * Clearing is SURGICAL, not a nuke: the set of characters phonics painted is a
 * pure function of (mode, text), so replaying the PREVIOUS mode's plan over the
 * current text finds exactly those characters again and lifts only their
 * `fill`. A teacher's manual Emphasize fill on any other character survives, as
 * does every non-fill override (bold/size/highlight) everywhere.
 *
 * KNOWN, DOCUMENTED LIMIT: fabric stores one `fill` per character with no room
 * for provenance, so where a manual fill OVERLAPS a phonics-painted character
 * the two are genuinely indistinguishable — phonics wins while it is on, and
 * turning it Off clears that character rather than restoring the manual colour
 * underneath. Re-picking the colour in Emphasize restores it.
 *
 * What to clear is read from `__phonicsPainted`, the ranges the LAST paint
 * actually wrote — NOT a re-derivation over the current text. The difference
 * bites whenever the text changed underneath: replaying the new text's plan
 * leaves a stale fill on every character the old plan painted that the new one
 * does not (a letter retyped into a space kept its colour — caught by the
 * 'ship shop' probe). Ranges are clamped to the current length, so a shortened
 * text cannot address past its end.
 *
 * On a freshly loaded object there is no runtime record, so we fall back to the
 * plan for the persisted recipe — exactly right there, since the saved glyphs
 * carry precisely what data.phonics describes. */
function repaintPhonics(object, prevModeArg) {
  const len = object._text?.length ?? (object.text || '').length;
  const prevMode = prevModeArg
    ?? object.__phonicsMode
    ?? object.data?.phonics?.mode
    ?? 'none';
  const prevPainted = object.__phonicsPainted
    ?? phonicsRangesFor(object, prevMode).map((r) => [r.start, r.end]);
  for (const [s, e] of prevPainted) {
    const a = Math.min(s, len);
    const b = Math.min(e, len);
    // fabric's _extendStyles pickBy-drops undefined, so this DELETES the fill
    // override rather than writing an undefined one (same trick as
    // clearSelectionTextStyle) — stylesToArray then omits emptied chars.
    if (b > a) object.setSelectionStyles({ fill: undefined }, a, b);
  }
  const nextMode = object.data?.phonics?.mode || 'none';
  const plan = phonicsRangesFor(object, nextMode);
  for (const r of plan) {
    object.setSelectionStyles({ fill: r.color }, r.start, r.end);
  }
  object.__phonicsMode = nextMode;
  object.__phonicsPainted = plan.map((r) => [r.start, r.end]);
  // Same post-write re-measure as restyleText() (the buildEditor internal we
  // cannot reach from module scope): per-char styles change glyph metrics.
  object.dirty = true;
  object.initDimensions?.();
  object.setCoords();
}

// ---------- Curved text ----------
// Shapes: 'arc' (the original quadratic arch, default) | 'circle' | 'wave'.
const CURVE_SHAPES = ['arc', 'circle', 'wave'];

/** Live text width every curve shape is scaled from. */
function curveTextWidth(obj) {
  return Math.max(
    (typeof obj.calcTextWidth === 'function' && obj.calcTextWidth()) || obj.width || 100,
    10,
  );
}

/** Ring radius for 'circle': the circumference is the text width at |amt|=100
 * (the string closes the ring exactly) and grows as |amt| falls, so |amt| is
 * literally the percentage of the ring the words wrap around. */
function curveRingRadius(w, amt) {
  return (w * (100 / Math.abs(amt))) / (2 * Math.PI);
}

/** Curved text is a SINGLE-LINE feature: fabric's text-on-path has no line
 * concept — every rendered line (hard break OR box wrap) is laid on the same
 * path, overlapping the glyphs. Gate on the rendered line count so a text that
 * wraps after an edit/resize falls back to straight and curves again by itself
 * once it fits on one line. */
function isCurveEligible(obj) {
  if (String(obj.text ?? '').includes('\n')) return false;
  return (obj._textLines?.length ?? 1) <= 1;
}

/** Slider domain: −100…100, integral. */
function clampCurveAmount(amount) {
  return Math.max(-100, Math.min(100, Math.round(amount || 0)));
}

/** Curve path from LIVE metrics — extracted from changeTextCurve so geometry
 * refreshes (edit / resize) re-derive identically. */
function deriveCurvePath(obj, amount = obj.curveAmount, shape = obj.curveShape) {
  const amt = clampCurveAmount(amount);
  if (!amt) return null;
  const w = curveTextWidth(obj);
  if (shape === 'circle') {
    const r = curveRingRadius(w, amt);
    // sweep 1 (amt > 0) runs clockwise over the TOP: glyphs face outward, the
    // badge-top look. sweep 0 runs under the BOTTOM: glyphs face inward, which
    // is what makes a seal's lower text read right-side-up.
    const sweep = amt > 0 ? 1 : 0;
    return new Path(
      `M ${-r} 0 A ${r} ${r} 0 0 ${sweep} ${r} 0 A ${r} ${r} 0 0 ${sweep} ${-r} 0 Z`,
      { fill: '', stroke: '' },
    );
  }
  if (shape === 'wave') {
    // Two full periods across the text width; each half-period is one cubic
    // whose control height (4/3 × amplitude) puts its apex exactly at the
    // amplitude — the standard sine approximation. amt = amplitude.
    const seg = w / 4;
    const k = ((amt / 100) * w * 0.15 * 4) / 3;
    const half = (dir) => `c ${seg / 3} ${-k * dir} ${(seg * 2) / 3} ${-k * dir} ${seg} 0`;
    return new Path(
      `M ${-w / 2} 0 ${half(1)} ${half(-1)} ${half(1)} ${half(-1)}`,
      { fill: '', stroke: '' },
    );
  }
  const k = -(amt / 100) * w * 0.5; // negative y = arch upward
  return new Path(`M ${-w / 2} 0 Q 0 ${k} ${w / 2} 0`, { fill: '', stroke: '' });
}

/** The path PLUS the pathAlign/pathSide/pathStartOffset trio the shape needs —
 * one source for changeTextCurve, the geometry refresh and the load-time
 * rehydrate, so the three can never drift. */
function deriveCurveProps(obj, amount = obj.curveAmount, shape = obj.curveShape) {
  const path = deriveCurvePath(obj, amount, shape);
  // Cache OFF while curved: Text.initDimensions adopts its path's bbox, but
  // Textbox.initDimensions keeps the wrap box — so glyphs land outside the
  // object's own bounds and its cache canvas SLICES them (a ring at |amt| < 100
  // came back with its top cut off along a straight line). Verified in fabric
  // 6.9.1 that an uncached curve renders identically, just uncut; the flag goes
  // back on the moment the curve does.
  const props = { path, pathAlign: 'center', pathSide: 'left', pathStartOffset: 0, objectCaching: !path };
  if (path && shape === 'circle') {
    // Fabric lays the line out from the path START (shifted by textAlign), and
    // this path starts at the ring's 9 o'clock — back the anchor out so the
    // string's midpoint lands a quarter-length in: the ring's top for the
    // outward sweep, its bottom for the inward one. The ring's length is known
    // analytically (2πr), so this needs no path measurement pass.
    const w = curveTextWidth(obj);
    const L = 2 * Math.PI * curveRingRadius(w, clampCurveAmount(amount));
    const base = obj.textAlign === 'center'
      ? (L - w) / 2
      : obj.textAlign === 'right' ? L - w : 0;
    props.pathStartOffset = L / 4 - w / 2 - base;
  }
  return props;
}

/**
 * Re-derive geometry-coupled text state from live metrics: the curve path
 * (calcTextWidth changes with every edit — the stale-arc bug) and the active
 * effects (fontSize-relative, so a corner-drag resize re-runs each slot's
 * derive — Photoshop-style proportional layer effects). Never touches paint:
 * hollow's stroke keeps its already-resolved fill. Callers render/save.
 *
 * VERIFIED for strokeFillParam (sticker's textured border): this needs NO
 * stroke work and must not grow any. The re-derive below is the exact reason
 * derive omits `stroke` for a texture — it runs on every resize and text edit,
 * and object.set() only writes the keys a patch actually carries, so an omitted
 * `stroke` leaves the engine's resolved Pattern in place while strokeWidth
 * re-derives proportionally. Re-resolving the texture here would also be wrong
 * on its own terms: this function is SYNC (mid-drag, per frame) and a Pattern
 * needs an await. Solid/gradient borders come back from derive as before.
 */
function refreshTextGeometryEffects(obj) {
  if (!obj || !isTextType(obj.type)) return;
  const fx = obj.data?.textEffects;
  const hasFx = !!(fx && (fx.shadow?.id || fx.stroke?.id));
  if (!obj.curveAmount && !hasFx) return; // zero-cost for plain text
  if (obj.curveAmount && !obj.isEditing) {
    // Editing runs straight (v6 caret-on-path is flaky) — never re-curve
    // mid-edit. Text that an edit/resize pushed onto a second line renders
    // straight too (curveAmount is KEPT): fabric would overlap the lines on the
    // path, and re-widening the box brings the curve back by itself.
    obj.set(isCurveEligible(obj) ? deriveCurveProps(obj) : { path: null, objectCaching: true });
  }
  if (hasFx) {
    const applied = new Set();
    for (const slot of ['shadow', 'stroke']) {
      const entry = fx[slot];
      if (!entry?.id || applied.has(entry.id)) continue; // sticker spans both slots
      applied.add(entry.id);
      const def = EFFECT_DEFS[entry.id];
      if (def) {
        obj.set(def.derive({ ...effectDefaults(def), ...entry.params }, obj.fontSize, textEffectCtx(obj)));
      }
    }
  }
  obj.set({ dirty: true });
  obj.initDimensions?.();
  obj.setCoords();
}

/** Pre-parse half of the texture portability law: serialized Pattern fills
 * carry the SAVING deployment's source URL — retarget them to this
 * deployment's copy of the texture id BEFORE enlivening, because v6
 * loadFromJSON rejects outright on any dead resource (which would fail the
 * whole design load, not just the texture). */
function retargetTexturePatterns(data) {
  // Recursive: grouped text serializes as obj.objects children, and one
  // missed dead Pattern URL fails the WHOLE load.
  const walk = (objs) => (objs || []).forEach((obj) => {
    if (Array.isArray(obj?.objects)) walk(obj.objects);
    const recipe = obj?.data?.textFill;
    // id-based recipes only: a user-imported {src} texture serialized its
    // data-URL source, which is valid in every deployment — nothing to
    // retarget (and nothing that can be dead).
    if (recipe?.type === 'texture' && recipe.id) {
      const url = textureUrl(recipe.id);
      if (obj.fill && typeof obj.fill === 'object' && obj.fill.source) obj.fill.source = url;
      if (obj.stroke && typeof obj.stroke === 'object' && obj.stroke.source) obj.stroke.source = url;
    }
    // A border texture (sticker's borderColor) is the stroke's OWN paint, so
    // the same portability law applies to it: the serialized Pattern carries
    // the SAVING deployment's URL and must be retargeted before enliven.
    // Runs AFTER the textFill block on purpose — when the text is texture-
    // filled AND sticker-bordered, the block above retargets any stroke Pattern
    // to the FILL's texture (it cannot tell the two apart); the stroke's real
    // owner gets the last word here. src-based (user-imported) borders carry
    // a data URL that is valid in every deployment — retarget to it too so
    // the fill block above can't leave the wrong source behind.
    const border = strokeFillTextureOf(obj?.data);
    if (border && obj.stroke && typeof obj.stroke === 'object' && obj.stroke.source) {
      obj.stroke.source = border.src ?? textureUrl(border.id);
    }
    // Workspace background texture (data.bgRecipe): the serialize seam keeps
    // the workspace's JSON fill FLAT, so normally there is nothing here to
    // retarget — this covers designs saved through a seam failure (stock
    // Pattern output), whose dead id-derived URL would otherwise reject the
    // whole load. src-based recipes carry a data URL — valid everywhere.
    const bg = obj?.name === 'clip' ? obj?.data?.bgRecipe : null;
    if (bg?.type === 'texture' && bg.id
      && obj.fill && typeof obj.fill === 'object' && obj.fill.source) {
      obj.fill.source = textureUrl(bg.id);
    }
  });
  walk(data?.objects);
  // canvas.clipPath is a bare clone() of the workspace (no data bag) — under
  // the same seam-failure posture it could carry the dead Pattern too, and a
  // dead clipPath resource rejects the load exactly like a dead object's.
  const wsBg = (data?.objects || []).find((o) => o?.name === 'clip')?.data?.bgRecipe;
  if (wsBg?.type === 'texture' && wsBg.id
    && data?.clipPath?.fill && typeof data.clipPath.fill === 'object' && data.clipPath.fill.source) {
    data.clipPath.fill.source = textureUrl(wsBg.id);
  }
}

// ---------- sticker stroke seam (per-instance, exportability-safe) ----------
// fabric 6.9.1 ships two defects that a wide die-cut border (sticker at
// border 200 → strokeWidth 0.6·fontSize) makes owner-visible. Both are
// patched per INSTANCE — never a class, and never serialized — so a saved
// design stays a plain stock textbox (exportability law; same posture as
// fxRender's seam, whose conventions this block copies).
//
//  1. PAINT CLIP — FabricText.handleFiller (dist/index.mjs ~19795) routes any
//     TRANSFORMED stroke filler — a texture Pattern with patternTransform, or
//     a percentage-unit Gradient, i.e. BOTH of sticker's rich border paints —
//     through _applyPatternGradientTransformText (~19772), which bakes the
//     paint into a `no-repeat` pattern canvas sized (width+strokeWidth) ×
//     (height+strokeWidth) anchored at (-width/2, -height/2): the LAYOUT box
//     corner. Coverage ends exactly at the layout box's left/top edge, so the
//     border's own half-stroke spill (SW/2) and the ring above an i-dot paint
//     NOTHING there, while right/bottom get a useless +SW of slack. On canvas:
//     the border sheared vertically flat at the box's left edge, its crowns
//     shaved flat along the top — the owner's screenshot. Solid borders never
//     enter that branch, which is why only textured/gradient stickers show it.
//  2. BOX UNDER-REPORT — _getTransformedDimensions (~6639) adds strokeWidth
//     POST-scale when strokeUniform is true, but the Text classes IGNORE
//     strokeUniform when painting: Text._setStrokeStyles (~19833) sets
//     ctx.lineWidth = strokeWidth with no 1/scale compensation anywhere in the
//     strokeText path (fabric's own "TODO: verify compatibility with
//     strokeUniform" marks the gap). Painted ink spans (width+SW)·scale; the
//     reported box only grows by +SW. At scale 1 they coincide; a scaled
//     sticker (multi-select / group resize leaves scale on text) pokes
//     SW·(s−1)/2 outside its own selection box, cache and alignment bounds.
//
// The seam: instance `handleFiller` rebuilds the transformed-filler pattern
// with real coverage (± strokeWidth/2 + fontSize/2 beyond the layout box —
// the same overhang budget fabric's own Text cache pads by, grown to the path
// bbox for curved text), phase-identical to stock everywhere stock painted;
// instance `_getTransformedDimensions` reports the stroke the way text
// actually paints it (pre-scale), which getBoundingRect, setCoords, controls,
// the object cache, fxGeometry and the print export all inherit — the box now
// CONTAINS the border at any scale (the owner's ask).
//
// Failure posture: any throw falls back to the prototype's behavior (stock
// fabric — possibly clipped, never broken). A stock-fabric consumer of the
// saved JSON renders its own stock result; nothing here reaches the JSON.
let stickerSeamWarned = false;
function stickerSeamLog(err) {
  if (stickerSeamWarned) return;
  stickerSeamWarned = true;
  console.error('[stickerSeam] padded stroke paint failed — stock (clipped) rendering', err || '');
}

/** Coverage half-extents in object units: layout box + painted stroke spill +
 * fabric's own glyph-overhang budget (fontSize/2 per side, exactly what the
 * Text cache pads), stretched to the path bbox for text-on-path (mirrors
 * fxGeometry's curve rule). */
function stickerCoverage(obj) {
  const sw = obj.strokeWidth || 0;
  const fs = obj.fontSize || 0;
  let halfW = obj.width / 2 + sw / 2 + fs / 2;
  let halfH = obj.height / 2 + sw / 2 + fs / 2;
  const path = obj.path;
  if (path && typeof path.width === 'number') {
    halfW = Math.max(halfW, path.width / 2 + fs + sw / 2);
    halfH = Math.max(halfH, path.height / 2 + fs + sw / 2);
  }
  return { halfW, halfH };
}

/** Instance handleFiller: identical contract to FabricText.prototype's —
 * translate the ctx, install the paint, return the offsets _renderChar
 * subtracts — but the no-repeat pattern for transformed fillers covers the
 * whole stroked+overhang box instead of stopping at the layout box's
 * left/top edge. Content phase matches stock exactly (tile origin stays at
 * layout-left + SW/2), so already-visible pixels do not shift. */
function stickerHandleFiller(ctx, property, filler) {
  const base = this.__stickerBaseHandleFiller || Object.getPrototypeOf(this).handleFiller;
  const transformed = filler && typeof filler === 'object'
    && (filler.gradientUnits === 'percentage' || filler.gradientTransform || filler.patternTransform);
  if (!transformed) return base.call(this, ctx, property, filler);
  try {
    const sw = this.strokeWidth || 0;
    const { halfW, halfH } = stickerCoverage(this);
    const W = Math.ceil(halfW * 2);
    const H = Math.ceil(halfH * 2);
    if (W <= 0 || H <= 0 || W > 8192 || H > 8192) return base.call(this, ctx, property, filler);
    const pCanvas = document.createElement('canvas');
    pCanvas.width = W;
    pCanvas.height = H;
    const pCtx = pCanvas.getContext('2d');
    if (!pCtx) return base.call(this, ctx, property, filler);
    pCtx.beginPath();
    pCtx.moveTo(0, 0);
    pCtx.lineTo(W, 0);
    pCtx.lineTo(W, H);
    pCtx.lineTo(0, H);
    pCtx.closePath();
    // Stock anchors its (w+SW)-sized canvas at the layout corner, so its
    // transform origin sits at object (SW/2, SW/2); ours is centered on the
    // object, so shift by +SW/2 to keep the tile phase byte-identical to
    // stock everywhere stock actually painted.
    pCtx.translate(W / 2 + sw / 2, H / 2 + sw / 2);
    pCtx.fillStyle = filler.toLive(pCtx);
    this._applyPatternGradientTransform(pCtx, filler);
    pCtx.fill();
    const offsetX = -halfW;
    const offsetY = -halfH;
    ctx.translate(offsetX, offsetY);
    ctx[property] = pCtx.createPattern(pCanvas, 'no-repeat');
    return { offsetX, offsetY };
  } catch (e) {
    stickerSeamLog(e);
    return base.call(this, ctx, property, filler);
  }
}

/** Instance _getTransformedDimensions: report the stroke as text PAINTS it —
 * in object units, scaling with the object — by running the prototype's math
 * with strokeUniform's dims special-case suppressed (the flag itself is
 * untouched in props/JSON; only this object's geometry REPORTING changes).
 * Everything downstream now agrees with the ink: aCoords/getBoundingRect,
 * selection controls, the cache canvas, fxGeometry's offscreen and the
 * export raster. */
function stickerGetTransformedDimensions() {
  const options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const base = this.__stickerBaseDims || Object.getPrototypeOf(this)._getTransformedDimensions;
  if (!this.strokeUniform) return base.call(this, options);
  this.strokeUniform = false;
  try {
    return base.call(this, options);
  } finally {
    this.strokeUniform = true;
  }
}

/** Idempotently install the sticker stroke seam on ONE live text object.
 * Prototype methods are captured (never obj.*) so re-attach after clone/load
 * can never double-wrap — fxRender's exact convention. */
function ensureStickerSeam(obj) {
  if (!obj || obj.__stickerSeam) return;
  const proto = Object.getPrototypeOf(obj);
  if (typeof proto.handleFiller !== 'function'
    || typeof proto._getTransformedDimensions !== 'function'
    || typeof proto._applyPatternGradientTransform !== 'function') {
    stickerSeamLog(new Error('fabric internals missing on prototype'));
    return;
  }
  obj.__stickerSeam = true;
  obj.__stickerBaseHandleFiller = proto.handleFiller;
  obj.__stickerBaseDims = proto._getTransformedDimensions;
  obj.handleFiller = stickerHandleFiller;
  obj._getTransformedDimensions = stickerGetTransformedDimensions;
  obj.dirty = true;
}

/** Remove the seam and its private props — the object is indistinguishable
 * from a never-touched fabric object afterwards. */
function removeStickerSeam(obj) {
  if (!obj || !obj.__stickerSeam) return;
  delete obj.handleFiller;
  delete obj._getTransformedDimensions;
  delete obj.__stickerSeam;
  delete obj.__stickerBaseHandleFiller;
  delete obj.__stickerBaseDims;
  obj.dirty = true;
}

/** The seam follows the DATA, not an effect id: any stroke-slot effect whose
 * def declares strokeFillParam (sticker today — the "border is its own paint"
 * family) owns a wide painted border that fabric's stock math mis-boxes and
 * mis-paints. Keyed off the def MARKER per the effectDefs law. */
function stickerSeamNeeded(data) {
  return !!EFFECT_DEFS[data?.textEffects?.stroke?.id]?.strokeFillParam;
}

// ---------- plain-path render lane (per-instance, exportability-safe) ----------
// ONE instance `_render` seam hosting the plain-path (no data.fx) render
// bodies for two disjoint stroke families — fx-attached objects get both
// behaviors inside the fx render stack instead:
//   - die-cut closing (renderDiecutPlain): the pocket-above-the-i fix for
//     PLAIN border effects (sticker/outline) — pooled offscreen →
//     morphological close + border-paint inpaint → draw back; a shape with
//     nothing to bridge falls through to the stock vector render untouched.
//   - union-contour band (renderContourPlain, 2026-07-20): hollow/trace
//     stroke the glyphs' TRUE UNION silhouette instead of the raw path with
//     its internal construction seams; a degrade (blank/curved-uncached/
//     hiccup) falls through to the stock vector render.
// Same JSON purity as the stroke-paint seam: instance props only, nothing
// serializes, stock consumers render stock (pocketed/seamed) output without
// error.
//
// _render OWNERSHIP: fxRender's seam also installs an instance _render, and
// both patches read the prototype as their base — so the two compose by
// yielding, never by wrapping each other:
//   - fx attach OVERWRITES this lane's _render (fx does its own closing);
//     ensureDiecutRender never installs over an attached fx seam.
//   - fx detach `delete`s the instance _render — applyFxSteps re-syncs this
//     seam right after, which re-installs the lane if still needed.
function diecutWrappedRender(ctx) {
  const base = Object.getPrototypeOf(this)._render;
  try {
    // Union-contour band first (hollow/trace — the contourOutline family):
    // the two lanes share this one instance seam but are DISJOINT families
    // by construction (diecutStrokeReach disqualifies fillRouting/clearsFill
    // defs; contourOutlineWanted requires the contourOutline marker), so at
    // most one of these ever returns true for a given recipe.
    if (renderContourPlain(this, ctx, base)) return;
    if (renderDiecutPlain(this, ctx, base)) return;
  } catch (e) {
    stickerSeamLog(e);
  }
  base.call(this, ctx);
}

function ensureDiecutRender(obj) {
  const proto = Object.getPrototypeOf(obj);
  if (typeof proto._render !== 'function') {
    stickerSeamLog(new Error('fabric internals missing on prototype'));
    return;
  }
  obj.__diecutRender = true;
  if (!obj.__fxAttached) {
    obj._render = diecutWrappedRender;
    obj.dirty = true;
  }
}

function removeDiecutRender(obj) {
  if (!obj.__diecutRender) return;
  delete obj.__diecutRender;
  if (obj._render === diecutWrappedRender) delete obj._render;
  obj.dirty = true;
}

/** Attach-or-detach BOTH seam lanes to match the object's current recipe:
 * the stroke-paint seam follows strokeFillParam, and the plain _render lane
 * serves TWO disjoint stroke families — die-cut closing follows
 * diecutStrokeReach (sticker AND outline), the union-contour band follows
 * contourOutlineWanted (hollow AND trace, the contourOutline marker; their
 * raw-path stroke shows glyph construction seams — fxRender's
 * renderContourPlain strokes the true union silhouette instead). Called
 * wherever textEffects is written (apply/remove/clear/style), after fx
 * detach, and from the re-attach walk below for every path that rebuilds
 * instances. */
function syncStickerSeam(obj) {
  // Non-text kinds are ACCEPTED and need nothing here by construction
  // (universal-effects U1, per the capability matrix): their native effects
  // are plain fabric props, and their sticker plates / contour bands ride
  // data.fx — ensureFxRender (already object-generic) is their whole seam.
  // The three per-instance lanes below are all text physics: the stroke-
  // paint patch (wide painted TEXT borders), die-cut closing (glyph
  // pockets), and the union-contour band (glyph construction seams).
  if (!obj || !isTextType(obj.type)) return;
  if (stickerSeamNeeded(obj.data)) ensureStickerSeam(obj);
  else removeStickerSeam(obj);
  if (diecutStrokeReach(obj) > 0 || contourOutlineWanted(obj)) ensureDiecutRender(obj);
  else removeDiecutRender(obj);
}

/** Live-fx re-attach walk (same shape as rehydrateTextureFills): every path
 * that REBUILDS instances (loadFromJSON, undo/redo restore, clone) loses the
 * instance-level _render patch — re-attach wherever data.fx survived the
 * JSON. The sticker stroke seam rides the same walk (its recipe survives on
 * data.textEffects). Any object type, group children included.
 * Universal effects (U1): shapes/icons re-attach through the SAME data.fx
 * line below — their sticker plates and contour bands are fx steps, and
 * their native effects are plain props that need no re-attach at all (the
 * matrix's whole non-text seam story; syncStickerSeam documents why its
 * three lanes stay text-only). */
function reattachFxDeep(objects) {
  const walk = (objs) => objs.forEach((o) => {
    if (typeof o.getObjects === 'function') walk(o.getObjects());
    if (Array.isArray(o.data?.fx) && o.data.fx.length) ensureFxRender(o);
    if (isTextType(o.type)) syncStickerSeam(o);
    // 3D joystick pad: runtime-only control, lost with the instance exactly
    // like the seams above — self-deciding (fx-extrude + text3d images).
    ensureJoystick3d(o);
  });
  walk(objects || []);
}

/**
 * Re-bake-on-scale for baked recipe images (emboss-text / text3d) —
 * resolution lane 2026-07-17. When an interaction ends (object:modified)
 * with the image's displayed DEVICE pixels (scaled long edge × canvas zoom
 * × retina) exceeding its standing bakePx by >15%, re-bake IN PLACE at the
 * need, capped by BAKE_STAND_CAPS (the JSON/data-URL budget) and the
 * runtime side cap. Interaction-end only — never per-frame — and never
 * downgrades (shrinking an upgraded object keeps its sharp bake; JSON cost
 * is already paid). Failures (no WebGL, context loss) keep the standing
 * bake — the export pre-pass retries at print time regardless.
 * __bakeUpgrading is the re-entrancy flag (probes poll it); a second
 * qualifying interaction mid-bake queues exactly one follow-up.
 */
const BAKE_UPGRADE_TRIGGER = 1.15;
async function maybeUpgradeBakedImage(object, canvas, save) {
  const data = object && object.data;
  if (!data || !data.params || (data.tool !== 'emboss-text' && data.tool !== 'text3d')) return;
  if (typeof object.getScaledWidth !== 'function' || typeof object.setSrc !== 'function') return;
  const standing = Number(data.bakePx) || 0;
  if (!(standing > 0)) return;
  const zoom = (canvas.getZoom() || 1) * (canvas.getRetinaScaling ? canvas.getRetinaScaling() : 1);
  const needPx = Math.max(object.getScaledWidth(), object.getScaledHeight()) * zoom;
  const standCap = Math.min(
    data.tool === 'text3d' ? BAKE_STAND_CAPS.text3d : BAKE_STAND_CAPS.emboss,
    getRenderCaps().side,
  );
  const target = Math.round(Math.min(Math.ceil(needPx), standCap));
  if (target <= standing * BAKE_UPGRADE_TRIGGER) return;
  if (object.__bakeUpgrading) {
    object.__bakeUpgradeAgain = true;
    return;
  }
  object.__bakeUpgrading = true; // set SYNCHRONOUSLY — probes key off it
  try {
    const baker = bakerFor(data.tool);
    if (!baker) return;
    const displayedW = object.getScaledWidth();
    const displayedH = object.getScaledHeight();
    const { dataUrl } = await baker.bake(data.params, { targetPx: Math.min(target, baker.maxPx) });
    if (!object.canvas) return; // removed mid-bake — drop the result
    await object.setSrc(dataUrl);
    object.set({
      scaleX: displayedW / object.width,
      scaleY: displayedH / object.height,
      data: { ...object.data, bakePx: target },
      dirty: true,
    });
    object.setCoords();
    object.canvas.requestRenderAll();
    // The sharper standing bake is design state — persist it, but as a
    // REPLACE of the current history entry, not a new one: the upgrade is a
    // fidelity cache refresh, not a user action, so it must not cost an undo
    // press (undo after a scale must undo the SCALE). Replacing also keeps
    // save()'s dedupe truthful — a stale top entry would turn the next
    // redundant commit() into a phantom entry. Undoing to an older entry
    // restores the softer bake, which is a valid earlier state; re-scaling
    // re-triggers the upgrade. (Global-undo lane, 2026-07-21.)
    save?.('replace');
  } catch (err) {
    // Standing bake stays; the print pre-pass re-bakes at export time
    // anyway. Loud, once — a silently soft image is this lane's original sin.
    if (!object.__bakeUpgradeWarned) {
      object.__bakeUpgradeWarned = true;
      console.warn(`[bake] re-bake-on-scale failed for ${data.tool} — standing ${standing}px kept`, err);
    }
  } finally {
    object.__bakeUpgrading = false;
    if (object.__bakeUpgradeAgain) {
      object.__bakeUpgradeAgain = false;
      maybeUpgradeBakedImage(object, canvas, save);
    }
  }
}

/** Depth-first over live text objects, including group children. */
function forEachLiveText(canvas, fn) {
  const walk = (objs) => objs.forEach((o) => {
    if (typeof o.getObjects === 'function') walk(o.getObjects());
    if (isTextType(o.type)) fn(o);
  });
  walk(canvas.getObjects());
}

/** True while the object (possibly nested in groups) is still on canvas —
 * getObjects().includes() is false for group children even when live. */
function stillOnCanvas(canvas, object) {
  let root = object;
  while (root.group) root = root.group;
  return canvas.getObjects().includes(root);
}

/** Post-load half: re-resolve texture recipes into live Pattern paint (onto
 * the stroke while Hollow is active). Fire-and-forget — a missing texture
 * keeps whatever fill deserialized and must never break the load. */
function rehydrateTextureFills(canvas) {
  forEachLiveText(canvas, (object) => {
    const recipe = object.data?.textFill;
    if (recipe?.type === 'texture' && recipe.id) {
      resolveTextFillPaint(recipe)
        .then((paint) => {
          if (!(paint instanceof Pattern)) return;
          if (!stillOnCanvas(canvas, object)) return; // gone mid-flight
          object.set(
            object.data?.textEffects?.stroke?.id === 'hollow' ? 'stroke' : 'fill',
            paint,
          );
          object.dirty = true;
          if (object.group) object.group.set('dirty', true); // group caches
          canvas.requestRenderAll();
        })
        .catch(() => {});
    }
    // Border texture (sticker's borderColor) — the stroke's own paint. No race
    // with the fill above: an effect with a strokeFillParam has no fillRouting,
    // so that branch lands on 'fill' and this one on 'stroke'.
    const border = strokeFillTextureOf(object.data);
    if (border) {
      resolveTextFillPaint(border)
        .then((paint) => {
          if (!(paint instanceof Pattern)) return;
          if (!stillOnCanvas(canvas, object)) return; // gone mid-flight
          object.set('stroke', paint);
          object.dirty = true;
          if (object.group) object.group.set('dirty', true); // group caches
          canvas.requestRenderAll();
        })
        .catch(() => {}); // dead texture keeps whatever deserialized
    }
  });
}

// ---------- poster background recipe (workspace texture/image fills) ----------
// The workspace (the poster page Rect, name 'clip') accepts the same FillValue
// family objects do: solid / linear-gradient / radial-gradient / texture
// ({id} manifest — ids NEVER URLs in persisted JSON, the portability law — or
// {src} user-imported ≤1024 webp data URL). The recipe is the ONLY persisted
// truth: data.bgRecipe rides the workspace data bag (JSON_KEYS carries `data`,
// same slot family as posterLight/exportDPI), so it saves, loads and rides
// every history snapshot for free.
//
// EXPORTABILITY / STOCK DEGRADE — the flat-JSON law: unlike object texture
// fills (whose serialized Pattern is retargeted pre-parse), the workspace's
// serialized `fill` stays a PLAIN COLOR STRING at all times. A serialize seam
// on the INSTANCE (never the class, never itself serialized — the sticker-seam
// posture) swaps any live Pattern out of toObject()'s output for the flat
// degrade color (__bgFlatFill: the last solid the user saw). Stock fabric
// viewers therefore load a sensible flat page — honest degrade, and NO dead
// texture URL can ever hard-fail a whole design load through the workspace or
// through canvas.clipPath (autoZoom re-clones the workspace as the clipPath
// via clone() → instance toObject(), so the seam covers that copy too).
//
// Because the JSON fill is flat, every instance rebuild (load, undo, redo)
// must re-resolve recipe → Pattern: rehydrateWorkspaceBackground below joins
// every path that already runs rehydrateTextureFills / rehydrateCurvedText,
// PLUS undo/redo (where object texture fills re-enliven natively from their
// serialized Pattern and need no rehydrate — the workspace does, by design).
//
// UNDO ACCOUNTING: the verbs write the recipe synchronously and never save —
// the caller's single commit (the house background grammar) snapshots recipe
// + flat fill. The async pattern-land only sets the live fill and repaints;
// it must NEVER save (no second entry), and thanks to the seam the serialized
// form is IDENTICAL before and after the land — a redundant commit can't even
// phantom-dedupe-miss.

/** Bump the paint token: any in-flight texture resolve becomes stale (a
 * solid/gradient picked mid-decode must not be stomped by the late pattern). */
function cancelBgPattern(workspace) {
  workspace.__bgPaintSeq = (workspace.__bgPaintSeq || 0) + 1;
  workspace._bgPatternPromise = null;
}

/** Instance serialize seam: keep the workspace's JSON fill FLAT while a
 * texture recipe is active. Installed at apply/rehydrate time (fresh
 * instances lose it — every rehydrate path re-installs, exactly like the fx
 * re-attach walks). Failure posture: any throw falls back to stock output
 * (a serialized Pattern — retargetTexturePatterns' workspace branch then
 * covers the id-based cross-deployment case). */
function ensureBgFillSeam(workspace) {
  if (!workspace || workspace.__bgFillSeam) return;
  workspace.__bgFillSeam = true;
  const orig = workspace.toObject;
  workspace.toObject = function bgFillSeamToObject(propertiesToInclude) {
    const out = orig.call(this, propertiesToInclude);
    try {
      // Swap ANY object-form fill (landed Pattern, or a gradient still held
      // while the first pattern decodes) for the flat degrade color — the
      // serialized form is stable from the moment the recipe is written.
      if (this.data?.bgRecipe?.type === 'texture' && out && typeof out.fill === 'object' && out.fill !== null) {
        out.fill = typeof this.__bgFlatFill === 'string' ? this.__bgFlatFill : '#ffffff';
      }
    } catch (_) { /* stock output */ }
    return out;
  };
}

/** Resolve recipe → Pattern and paint the workspace when ready. No flash of
 * wrong color: the prior fill stays until the pattern lands; a dead texture
 * keeps the flat fallback (recipe stays in data — it may resolve in another
 * deployment, the rehydrateTextureFills posture). The pending promise rides
 * _bgPatternPromise so the print export can pre-await it (cold-export race).
 * NEVER saves — see the undo-accounting note above. */
function applyBgTexture(canvas, workspace, recipe) {
  const seq = (workspace.__bgPaintSeq = (workspace.__bgPaintSeq || 0) + 1);
  const pending = resolveTextFillPaint(recipe)
    .then((paint) => {
      if (!(paint instanceof Pattern)) return;
      if (workspace.__bgPaintSeq !== seq) return; // superseded by a later pick
      if (!stillOnCanvas(canvas, workspace)) return; // instance replaced (load/undo)
      workspace.set({ fill: paint });
      canvas.requestRenderAll();
    })
    .catch(() => {}); // dead texture: flat fallback stays — honest degrade
  workspace._bgPatternPromise = pending;
  return pending;
}

/** Post-load/post-restore half: re-resolve data.bgRecipe into live Pattern
 * paint. Joined everywhere instances are rebuilt (initial load, loadJson,
 * undo, redo) — the serialized fill is the flat degrade by law, so without
 * this the poster background would restore flat. Non-texture recipes need
 * nothing: their paint IS the serialized fill (stock solid/gradient). */
function rehydrateWorkspaceBackground(canvas) {
  const workspace = canvas.getObjects().find((o) => o.name === 'clip');
  const recipe = workspace?.data?.bgRecipe;
  if (recipe?.type !== 'texture' || !(recipe.id || recipe.src)) return;
  // The deserialized flat fill IS the degrade color — carry it forward so
  // the seam keeps emitting it once the pattern lands.
  workspace.__bgFlatFill = typeof workspace.fill === 'string' && workspace.fill !== 'transparent'
    ? workspace.fill
    : '#ffffff';
  ensureBgFillSeam(workspace);
  applyBgTexture(canvas, workspace, recipe);
}

/** A design saved while its curved text was inline-editing serialized
 * path:null (editing runs straight) — re-derive the curve from curveAmount +
 * curveShape, which are in JSON_KEYS and survive regardless. */
function rehydrateCurvedText(canvas) {
  let touched = false;
  forEachLiveText(canvas, (object) => {
    if (!object.curveAmount) return;
    if (!isCurveEligible(object)) return; // single-line feature — see isCurveEligible
    if (object.path) {
      // Path survived the save; only the curve's cache rule needs re-asserting
      // (objectCaching is a perf flag fabric never serializes — without this a
      // reloaded ring came back sliced). See deriveCurveProps.
      if (object.objectCaching !== false) {
        object.set({ objectCaching: false, dirty: true });
        touched = true;
      }
      return;
    }
    object.set({
      ...deriveCurveProps(object),
      dirty: true,
    });
    object.initDimensions?.();
    object.setCoords();
    if (object.group) object.group.set('dirty', true);
    touched = true;
  });
  if (touched) canvas.requestRenderAll();
}

// Duplicate the active object/selection in a single async clone. Shared by
// the toolbar Duplicate button (editor.duplicate) and the Ctrl+D hotkey.
// `batch` coalesces the N object:added events of a multi-selection duplicate
// into ONE history entry — without it, undo peels the duplicate apart one
// object at a time.
function duplicateActiveObject(canvas, batch) {
  const active = canvas.getActiveObject();
  if (!active) return;
  // clone(JSON_KEYS): bare clone() serializes with NO custom keys, so the
  // data bag (smart-object recipes, fx recipes) silently fell off every
  // duplicate — the copy of a beveled headline came back flat.
  active.clone(JSON_KEYS).then((cloned) => {
    batch?.begin();
    try {
      canvas.discardActiveObject();
      cloned.set({ left: cloned.left + 20, top: cloned.top + 20, evented: true });
      if (cloned instanceof ActiveSelection) {
        cloned.canvas = canvas;
        cloned.forEachObject((obj) => canvas.add(obj));
        cloned.setCoords();
      } else {
        canvas.add(cloned);
      }
      reattachFxDeep([cloned]); // fresh instances — re-install live fx
      canvas.setActiveObject(cloned);
      canvas.requestRenderAll();
    } finally {
      // A throw mid-add must not leave history muted (failure posture,
      // global-undo lane); end() commits whatever really landed — dedupe
      // absorbs the nothing-landed case.
      batch?.end();
    }
  }).catch(() => {}); // v6: image clones re-fetch src and can reject offline
}

/**
 * Rebuild a smart object with new params, preserving placement/scale/angle
 * (regenerateSmartObject copies those), stack position and selection. The
 * object does NOT need to be the active selection — this is the shared core
 * behind editor.regenerateSelectedSmartObject (param panel) AND live
 * on-canvas table-cell editing. batch coalesces the remove+add into ONE undo
 * entry (same pattern as changeIconColor). Generation happens BEFORE
 * batch.begin() so a throwing recipe can't leave history muted.
 * `dataPatch` (optional) merges extra SmartObjectData fields (e.g. the Smart
 * Tool modal's gradeBand memory) onto the replacement BEFORE the batch
 * commits, so they ride the same history entry as the regenerate.
 */
async function regenerateSmartObjectInPlace(canvas, batch, object, newParams = {}, dataPatch = null) {
  if (!object || !smartObjectData(object)) return null;
  const replacement = await regenerateSmartObject(object, newParams);
  if (dataPatch && typeof dataPatch === 'object') {
    replacement.data = { ...replacement.data, ...dataPatch };
  }
  // Recipes with intrinsic width/height params: the panel's numbers are
  // the DISPLAYED pixel size (resize-sync writes the bbox into them), so
  // after regenerating force the bbox to exactly params.width/height.
  // A plain scale-1 reset is not enough — groups carry fixed-px
  // adornments (chart titles/labels), so intrinsic bbox ≠ params.
  const freshData = smartObjectData(replacement);
  const recipeDef = getRecipe(freshData?.tool || '');
  const wantW = recipeDef?.schema?.some((f) => f.key === 'width') && Number(freshData?.params?.width);
  const wantH = recipeDef?.schema?.some((f) => f.key === 'height') && Number(freshData?.params?.height);
  if (wantW || wantH) {
    replacement.set({ scaleX: 1, scaleY: 1 });
    if (wantW) replacement.scaleToWidth(wantW);
    if (wantH) {
      replacement.set({ scaleY: wantH / replacement.height });
      if (!wantW) replacement.set({ scaleX: replacement.scaleY });
    }
  }
  prepareSmartObject(replacement);
  const index = canvas.getObjects().indexOf(object);
  batch.begin();
  try {
    canvas.remove(object);
    canvas.add(replacement);
    // canvas.add appends on top — restore the original z-position
    // (verified in fabric 6.9.1: Collection.moveObjectTo(object, index))
    if (index >= 0) canvas.moveObjectTo(replacement, index);
    replacement.setCoords();
    canvas.setActiveObject(replacement);
    canvas.requestRenderAll();
  } finally {
    batch.end(); // never leave saves muted (failure posture)
  }
  return replacement;
}

/**
 * Map an edited table-cell text back onto the group's recipe params.
 * Write-back contract (matches the table recipe's tableCell tags):
 *   title    → params.title
 *   header   → params.columns[col]
 *   cell     → params.rows[row][col]   (row items may be arrays OR
 *              index-keyed objects — the list editor stores {'0':..,'1':..})
 *   rowLabel → params.rowLabels[row]   ONLY if that param is an array (the
 *              v1 recipe ships it as a boolean styling flag — skip then)
 *   param    → params[key]             (micro-object foundations, M0: the
 *              tag names the ONE scalar param the text child renders —
 *              {kind:'param', key:'numerator'} writes params.numerator.
 *              Stamped by posterTools/assemble.ts for any kit TextSpec
 *              carrying `param`; every wave-1 micro recipe rides this.)
 * Returns a partial-params object (regenerateSmartObject merges it over the
 * existing params), or null when the tag can't be mapped — callers treat
 * null as "no-op, keep the canvas as it was". All shapes are verified at
 * runtime: a recipe/tag mismatch must never corrupt params or throw.
 */
function buildTableCellParams(group, cell, text) {
  const params = smartObjectData(group)?.params;
  if (!params || !cell || typeof cell !== 'object') return null;
  const { kind, row, col } = cell;
  const validRow = Number.isInteger(row) && row >= 0;
  const validCol = Number.isInteger(col) && col >= 0;
  switch (kind) {
    case 'title':
      return { title: text };
    case 'header': {
      if (!Array.isArray(params.columns) || !validCol || col >= params.columns.length) return null;
      const columns = [...params.columns];
      columns[col] = text;
      return { columns };
    }
    case 'rowLabel': {
      // Recipe contract (posterTools/recipes/table.ts header): rowLabels is a
      // BOOLEAN style toggle; the label STRING lives in the row's column 0 —
      // a rowLabel at row r writes params.rows[r][0].
      if (!Array.isArray(params.rows) || !validRow || row >= params.rows.length) return null;
      const rows = [...params.rows];
      const item = rows[row];
      if (!Array.isArray(item)) return null;
      const next = [...item];
      next[0] = text;
      rows[row] = next;
      return { rows };
    }
    case 'cell': {
      if (!Array.isArray(params.rows) || !validRow || !validCol || row >= params.rows.length) return null;
      const rows = [...params.rows];
      const item = rows[row];
      if (Array.isArray(item)) {
        const next = [...item];
        next[col] = text;
        rows[row] = next;
      } else if (item && typeof item === 'object') {
        rows[row] = { ...item, [col]: text };
      } else {
        return null;
      }
      return { rows };
    }
    case 'param': {
      // Generic micro-object write-back: the tagged text renders exactly one
      // scalar param. Guard: the key must already exist in params — generate()
      // always tags with the fully-merged param bag (generateSmartObject
      // merges defaults first), so a missing key means a stale/foreign tag
      // and the edit must no-op rather than invent params.
      const key = cell.key;
      if (typeof key !== 'string' || key.length === 0 || !(key in params)) return null;
      return { [key]: text };
    }
    default:
      return null;
  }
}

/**
 * Live on-canvas cell editing: overlay-edit one tagged text child of a smart
 * table. Creates a top-level editable Textbox positioned exactly over the
 * child (absolute transform via util.qrDecompose of the child's full
 * transform matrix — calcTransformMatrix's translation is the child's
 * CENTER, hence originX/Y 'center'), hides the child, and enters editing.
 * On exit: unchanged/Escape → restore and done; changed → write the text
 * back into the group's params and regenerate the table in place (one undo
 * entry). The overlay itself never enters history (batch.begin/abort mutes
 * the object:added/removed saves WITHOUT committing a snapshot).
 *
 * Multi-line: Textbox default keybindings are kept — Enter inserts a
 * newline (fabric maps 13 → insert '\n'); educators commit by clicking
 * away (or Escape to cancel). Do not rebind Enter to commit.
 */
function startTableCellEdit(canvas, batch, group, child) {
  const cell = child?.data?.tableCell;
  if (!cell) return;
  // Empty fill-in cells have no text object — their RECT carries the tag.
  // Overlay starts blank over the rect; the text object is born on the
  // regenerate that follows the first commit.
  const isTextChild = typeof child.text === 'string';
  const groupParams = smartObjectData(group)?.params || {};
  // One edit session at a time: a second dblclick mid-edit would exit the
  // first overlay re-entrantly and could regenerate the group out from
  // under the new session.
  if (canvas.__cceCellEditActive) return;
  // No-op cleanly when the tag can't be mapped to params (probe with the
  // current text) — don't open an editor whose result we'd have to discard.
  if (buildTableCellParams(group, cell, child.text ?? '') === null) return;
  canvas.__cceCellEditActive = true;

  const originalText = isTextChild ? (child.text ?? '') : '';
  // Absolute (scene-space) placement of the child, group transform included.
  const dec = util.qrDecompose(child.calcTransformMatrix());
  const overlay = new Textbox(originalText, {
    left: dec.translateX,
    top: dec.translateY,
    originX: 'center',
    originY: 'center',
    angle: dec.angle,
    scaleX: dec.scaleX,
    scaleY: dec.scaleY,
    skewX: dec.skewX,
    skewY: dec.skewY,
    width: Math.max(20, (isTextChild ? child.width : (child.width || 20) * 0.8) || 20),
    fontFamily: (isTextChild ? child.fontFamily : groupParams.fontFamily) || 'Arial',
    fontSize: (isTextChild ? child.fontSize : Number(groupParams.fontSize)) || 24,
    fontWeight: isTextChild ? child.fontWeight : 'normal',
    fontStyle: isTextChild ? child.fontStyle : 'normal',
    lineHeight: isTextChild ? child.lineHeight : 1.16,
    textAlign: isTextChild ? child.textAlign : 'center',
    fill: (isTextChild ? child.fill : groupParams.textColor) || '#1e293b',
    // A mid-edit autosave/export must not capture the transient overlay.
    excludeFromExport: true,
  });

  // Escape cancels. Fabric 6.9.1 maps Escape to exitEditing (keysMap 27) but
  // does NOT revert the typed text — flag the cancel in a document-level
  // CAPTURE listener, which runs before fabric's hidden-textarea handler
  // (and therefore before our editing:exited handler reads the flag).
  let cancelled = false;
  const flagEscape = (e) => { if (e.key === 'Escape') cancelled = true; };
  document.addEventListener('keydown', flagEscape, true);

  if (isTextChild) child.set('visible', false);
  group.set('dirty', true); // group caches — invalidate so the child hides
  batch.begin();
  canvas.add(overlay);
  batch.abort(); // transient: the overlay must never enter history
  canvas.setActiveObject(overlay);
  // Verified in fabric 6.9.1: enterEditing() works on a programmatically
  // added Textbox (requires .canvas set — done by canvas.add above) and
  // selectAll() pre-selects the content for immediate overtyping.
  overlay.enterEditing();
  overlay.selectAll();
  canvas.requestRenderAll();

  // 'editing:exited' fires INSIDE exitEditing, before its canvas-level
  // object:modified — removing the overlay here clears overlay.canvas, so
  // fabric skips those canvas fires entirely (no stray history entry).
  overlay.once('editing:exited', () => {
    document.removeEventListener('keydown', flagEscape, true);
    const newText = overlay.text;
    batch.begin();
    canvas.remove(overlay);
    if (isTextChild) child.set('visible', true);
    group.set('dirty', true);
    batch.abort(); // canvas is back to its pre-edit state — nothing to record
    const partial = (!cancelled && newText !== originalText)
      ? buildTableCellParams(group, cell, newText)
      : null;
    if (!partial) {
      canvas.__cceCellEditActive = false;
      canvas.setActiveObject(group);
      canvas.requestRenderAll();
      return;
    }
    regenerateSmartObjectInPlace(canvas, batch, group, partial)
      .catch((err) => {
        console.error('Table cell write-back failed:', err);
        canvas.requestRenderAll();
      })
      .finally(() => { canvas.__cceCellEditActive = false; });
  });
}

function downloadFile(file, type) {
  const anchorElement = document.createElement("a");
  anchorElement.href = file;
  anchorElement.download = `design-${Date.now()}.${type}`;
  document.body.appendChild(anchorElement);
  anchorElement.click();
  anchorElement.remove();
}

/**
 * Build the editor API object
 */
const buildEditor = ({
  save,
  batch,
  undo,
  redo,
  canRedo,
  canUndo,
  autoZoom,
  copy,
  paste,
  canvas,
  fillColor,
  fontFamily,
  setFontFamily,
  setFillColor,
  strokeColor,
  setStrokeColor,
  strokeWidth,
  setStrokeWidth,
  selectedObjects,
  strokeDashArray,
  setStrokeDashArray,
}) => {
  const getWorkspace = () => {
    return canvas.getObjects().find((object) => object.name === "clip");
  };
  // v6: object-level sendToBack() was removed — z-order lives on the canvas.
  const sendWorkspaceToBack = () => {
    const ws = getWorkspace();
    if (ws) canvas.sendObjectToBack(ws);
  };

  const center = (object) => {
    const workspace = getWorkspace();
    const centerPoint = workspace?.getCenterPoint();
    if (!centerPoint) return;
    canvas._centerObject(object, centerPoint);
  };

  const addToCanvas = (object, at) => {
    // Uniform borders on shapes: scaling must not distort stroke thickness
    if (!isTextType(object.type) && !(object instanceof FabricImage)) {
      object.set({ strokeUniform: true });
    }
    if (at && Number.isFinite(at.x) && Number.isFinite(at.y)) {
      canvas.add(object);
      object.setPositionByOrigin(new Point(at.x, at.y), 'center', 'center');
      object.setCoords();
      canvas.setActiveObject(object);
    } else {
      center(object);
      canvas.add(object);
      canvas.setActiveObject(object);
    }
  };

  const generateSaveOptions = () => {
    const workspace = getWorkspace();
    if (!workspace) return {};
    const { width, height, left, top } = workspace;
    return { name: "Image", format: "png", quality: 1, width, height, left, top };
  };

  // Shared by addImage / addImageCapped. options.at = scene-coord drop point.
  const addImageFromUrl = (value, options = {}) => {
    // v6: fromURL is promise-based and REJECTS on load failure (v5 resolved a
    // sourceless image) — failure feedback lives in .catch now.
    FabricImage.fromURL(value, { crossOrigin: "anonymous" })
      .then((image) => {
        const workspace = getWorkspace();
        image.scaleToWidth(workspace?.width * 0.5 || 300);
        addToCanvas(image, options.at);
      })
      .catch(() => {
        console.error('Failed to load image into editor');
        window.dispatchEvent(new CustomEvent('cce-image-load-failed'));
      });
  };

  // Run an export with the viewport reset to identity, then restore the
  // user's pan/zoom exactly. (The old code called autoZoom() after every
  // export — the Save modal's thumbnail render re-fit the canvas the moment
  // it opened, stomping any manual navigation.)
  const withIdentityViewport = (fn) => {
    const prev = canvas.viewportTransform ? [...canvas.viewportTransform] : null;
    canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
    try {
      return fn();
    } finally {
      if (prev) canvas.setViewportTransform(prev);
      canvas.requestRenderAll();
    }
  };

  return {
    // Export methods
    savePng: () => {
      const options = generateSaveOptions();
      const dataUrl = withIdentityViewport(() => canvas.toDataURL(options));
      downloadFile(dataUrl, "png");
    },
    saveJpg: () => {
      const options = generateSaveOptions();
      const dataUrl = withIdentityViewport(() => canvas.toDataURL(options));
      downloadFile(dataUrl, "jpg");
    },
    saveJson: async () => {
      const dataUrl = canvas.toObject(JSON_KEYS);
      const fileString = `data:text/json;charset=utf-8,${encodeURIComponent(
        JSON.stringify(dataUrl, null, "\t"),
      )}`;
      downloadFile(fileString, "json");
    },
    loadJson: (json) => {
      const data = typeof json === 'string' ? JSON.parse(json) : json;

      // Get current canvas dimensions from workspace
      const currentWorkspace = getWorkspace();
      const currentWidth = currentWorkspace?.width || DEFAULT_WIDTH;
      const currentHeight = currentWorkspace?.height || DEFAULT_HEIGHT;

      // Find the template's clip object to get its dimensions and position
      // If no clip exists, look for a "background" object or use canvas dimensions from template
      let templateWidth = currentWidth;
      let templateHeight = currentHeight;
      let templateClipLeft = 0;
      let templateClipTop = 0;
      let hasClip = false;
      if (data.objects && Array.isArray(data.objects)) {
        const templateClip = data.objects.find(obj => obj.name === 'clip');
        if (templateClip) {
          hasClip = true;
          templateWidth = templateClip.width || currentWidth;
          templateHeight = templateClip.height || currentHeight;
          templateClipLeft = templateClip.left || 0;
          templateClipTop = templateClip.top || 0;
        } else {
          // No clip - try to get dimensions from background or first rect
          const background = data.objects.find(obj => obj.name === 'background');
          if (background) {
            templateWidth = background.width || currentWidth;
            templateHeight = background.height || currentHeight;
          }
        }
      }

      // Calculate scale factor to fit template into current canvas
      const scaleX = currentWidth / templateWidth;
      const scaleY = currentHeight / templateHeight;
      const scale = Math.min(scaleX, scaleY); // Uniform scale to maintain aspect ratio

      // Calculate offset to center the scaled content if aspect ratios differ
      const scaledWidth = templateWidth * scale;
      const scaledHeight = templateHeight * scale;
      const offsetX = (currentWidth - scaledWidth) / 2;
      const offsetY = (currentHeight - scaledHeight) / 2;

      // Scale and sanitize all objects
      if (data.objects && Array.isArray(data.objects)) {
        // If template has no clip, we need to add one
        if (!hasClip) {
          // Create a clip object matching the current canvas
          const clipObject = {
            type: 'rect',
            name: 'clip',
            left: 0,
            top: 0,
            width: currentWidth,
            height: currentHeight,
            fill: '#ffffff',
            selectable: false,
            hasControls: false,
            evented: false,
            // Same document-level defaults the boot workspace carries.
            data: { posterLight: { ...POSTER_LIGHT_DEFAULTS } },
          };
          // Insert clip at beginning (must be first object)
          data.objects.unshift(clipObject);
        }

        data.objects = data.objects.map(obj => {
          // Update clip object to match current canvas size
          if (obj.name === 'clip') {
            obj.width = currentWidth;
            obj.height = currentHeight;
            obj.left = 0;
            obj.top = 0;
            obj.selectable = false;
            obj.hasControls = false;
            obj.evented = false;
            return obj;
          }

          // Offset position relative to original clip, then scale, then add centering offset
          if (obj.left !== undefined) {
            obj.left = ((obj.left - templateClipLeft) * scale) + offsetX;
          }
          if (obj.top !== undefined) {
            obj.top = ((obj.top - templateClipTop) * scale) + offsetY;
          }

          // Scale sizes (raw-JSON type: case-insensitive — v6 writes 'Circle').
          // IMAGES scale via scaleX/scaleY: their width/height is the CROP
          // WINDOW in source pixels — multiplying it past the bitmap's natural
          // size makes fabric draw only the pixels that exist (catalog
          // templates rendered at ~70% and looked "broken"). Crop stays in
          // source pixels; only the transform scales.
          const rawType = String(obj.type || '').toLowerCase();
          if (rawType === 'image') {
            obj.scaleX = (obj.scaleX ?? 1) * scale;
            obj.scaleY = (obj.scaleY ?? 1) * scale;
          } else {
          if (obj.width !== undefined && rawType !== 'circle') obj.width = obj.width * scale;
          if (obj.height !== undefined) obj.height = obj.height * scale;
          }
          if (obj.radius !== undefined) obj.radius = obj.radius * scale;
          if (obj.rx !== undefined) obj.rx = obj.rx * scale;
          if (obj.ry !== undefined) obj.ry = obj.ry * scale;
          if (obj.fontSize !== undefined) obj.fontSize = obj.fontSize * scale;
          if (obj.strokeWidth !== undefined) obj.strokeWidth = obj.strokeWidth * scale;

          // Scale path points for polygons/polylines
          if (obj.points && Array.isArray(obj.points)) {
            obj.points = obj.points.map(p => ({
              x: p.x * scale,
              y: p.y * scale
            }));
          }

          // Sanitize textbox styles to prevent Fabric.js errors. Raw-JSON type
          // strings vary by writer: legacy v5 saves are lowercase
          // ('textbox'/'i-text'), v6 writes capitalized ('Textbox'/'IText') —
          // compare case-insensitively. Styles come in two shapes: the v5
          // nested object ({line:{char:{...}}}, sanitized below) and the v6
          // range array ([{start,end,style}]) which v6 parses natively via
          // stylesFromArray — leave arrays untouched.
          if (rawType === 'textbox' || rawType === 'i-text' || rawType === 'itext' || rawType === 'text') {
            if (!obj.styles || typeof obj.styles !== 'object') {
              obj.styles = {};
            }
            if (!Array.isArray(obj.styles)) {
              Object.keys(obj.styles).forEach(lineKey => {
                if (!obj.styles[lineKey] || typeof obj.styles[lineKey] !== 'object') {
                  delete obj.styles[lineKey];
                }
              });
            }
            // Per-character emphasis carries its own absolute fontSize/deltaY.
            // The base fontSize is scaled above; these must scale WITH it or a
            // template loaded at anything but 1:1 comes back with emphasized
            // words at the authored size against re-scaled body text.
            if (scale !== 1) {
              const scaleCharStyle = (style) => {
                if (!style || typeof style !== 'object') return;
                if (typeof style.fontSize === 'number') style.fontSize *= scale;
                if (typeof style.deltaY === 'number') style.deltaY *= scale;
              };
              if (Array.isArray(obj.styles)) {
                // v6 range array: [{start, end, style}]
                obj.styles.forEach(range => scaleCharStyle(range?.style));
              } else {
                // v5 nested map: {line: {char: style}}
                Object.values(obj.styles).forEach(line => {
                  if (line && typeof line === 'object') Object.values(line).forEach(scaleCharStyle);
                });
              }
            }
          }

          return obj;
        });
      }

      // Texture fills saved elsewhere must not 404-reject this load
      retargetTexturePatterns(data);

      // Load every referenced font BEFORE deserializing so text measures and
      // wraps with the real faces (templates reference Google Fonts that
      // silently fell back before the font system existed). Soft-fails to
      // fallback fonts offline.
      ensureFontsForDesign(data).finally(() => {
        // Coalesce the load into ONE history entry. loadFromJSON internally
        // clear()s (firing object:removed per existing object) then add()s
        // each incoming object (firing object:added per object) — unguarded,
        // a 40-object template pushed ~80 full-canvas snapshots (the O(N²)
        // "sluggish template load") and undo then peeled the template apart
        // object by object.
        batch.begin();
        // v6: loadFromJSON returns a Promise; the old completion-callback slot
        // is now the per-object REVIVER — completion logic must live in .then()
        // or it runs once per object.
        canvas.loadFromJSON(data).then(async () => {
          migrateLegacyLocks(canvas);
          canvas.getObjects().forEach(prepareSmartObject);
          // Re-measure text now the real faces are registered
          canvas.getObjects().forEach((o) => {
            if (isTextType(o.type)) {
              o.initDimensions?.();
              o.setCoords();
            }
          });
          // After loading, ensure the clip is set up properly
          const loadedClip = canvas.getObjects().find(obj => obj.name === 'clip');
          if (loadedClip) {
            // Ensure clip is at the back
            canvas.sendObjectToBack(loadedClip);
            // Set canvas clipPath. NOTE: no absolutePositioned — autoZoom()
            // immediately re-clones the workspace as the canvas clipPath in
            // the viewport plane (v5 snapshot parity: the first post-load
            // history entry must serialize the same flavor autoZoom leaves).
            const cloned = await loadedClip.clone();
            canvas.clipPath = cloned;
          }
          // Non-blocking: texture recipes re-resolve for THIS deployment;
          // curves saved mid-edit (path:null) re-derive from curveAmount
          rehydrateTextureFills(canvas);
          rehydrateWorkspaceBackground(canvas); // poster bg: flat JSON → live Pattern
          rehydrateCurvedText(canvas);
          reattachFxDeep(canvas.getObjects()); // live fx back on fresh instances
          autoZoom();
          batch.end();
        }).catch((err) => {
          // v6 loadFromJSON REJECTS if any object fails to enliven (e.g. one
          // dead remote image URL) where v5 loaded a sourceless placeholder.
          // Without this catch: skipSave stays stuck true (history + autosave
          // silently dead) and renderOnAddRemove stays false. Deliberately NOT
          // batch.end() — that would snapshot the partially-cleared canvas.
          console.error('Design load failed:', err);
          batch.abort();
          canvas.renderOnAddRemove = true;
          window.dispatchEvent(new CustomEvent('cce-image-load-failed'));
        });
      });
    },
    toJSON: () => canvas.toObject(JSON_KEYS),
    toDataURL: (options = {}) => {
      const workspace = getWorkspace();
      if (!workspace) return null;
      const { width, height, left, top } = workspace;
      return withIdentityViewport(() => canvas.toDataURL({
        format: options.format || 'png',
        quality: options.quality || 1,
        multiplier: options.pixelRatio || 1,
        width, height, left, top,
      }));
    },

    // History
    canUndo,
    canRedo,
    onUndo: () => undo(),
    onRedo: () => redo(),
    // Explicit history commit. Property setters (color, font, opacity,
    // filters, ...) deliberately do NOT push history themselves — the UI
    // funnels every mutation through commit() so a slider drag lands as ONE
    // entry instead of one per tick. save() dedupes, so redundant commits
    // after event-driven saves are free.
    commit: () => save(),

    // History coalescer, surfaced for the left INSERT panels that add an
    // object AND style/effect it in one gesture (Text drawer's Decorative /
    // 3D tiles = addText + applyTextStyle | patchTextFx as ONE undo entry).
    // begin() mutes the event-driven save so the intermediate add doesn't
    // push its own entry; end() unmutes and commits the composed state. The
    // end() save() DEDUPES, so wrapping a SELF-batching verb like
    // applyTextStyle is safe: the inner commit lands the single entry and the
    // outer end() re-saves the identical state as a no-op. Same {begin, end,
    // abort} object the engine batches loads/duplicates/regenerates with.
    batch,

    // Clipboard
    onCopy: () => copy(),
    onPaste: () => paste(),

    // Duplicate the active object/selection in a single async clone.
    // (The old toolbar called onCopy() then onPaste() back-to-back, but
    // copy()'s clone is async — paste ran before the clipboard was set and
    // duplicated the PREVIOUS clipboard contents, or nothing at all.)
    duplicate: () => duplicateActiveObject(canvas, batch),

    // Group / ungroup a multi-selection. v6 removed
    // ActiveSelection.toGroup() / Group.toActiveSelection() — the Group
    // rewrite expects explicit construction/dissolution.
    group: () => {
      const active = canvas.getActiveObject();
      if (!active || !(active instanceof ActiveSelection)) return;
      batch.begin();
      try {
        const objects = active.getObjects();
        canvas.discardActiveObject();
        objects.forEach((o) => canvas.remove(o));
        const grouped = new Group(objects);
        canvas.add(grouped);
        canvas.setActiveObject(grouped);
        canvas.requestRenderAll();
        canvas.fire("object:modified", { target: grouped });
      } finally {
        batch.end(); // never leave saves muted (failure posture)
      }
    },
    ungroup: () => {
      const active = canvas.getActiveObject();
      if (!(active instanceof Group) || active instanceof ActiveSelection) return;
      batch.begin();
      try {
        // removeAll() restores the children's canvas-space transforms
        const children = active.removeAll();
        canvas.remove(active);
        canvas.add(...children);
        const selection = new ActiveSelection(children, { canvas });
        canvas.setActiveObject(selection);
        canvas.requestRenderAll();
        canvas.fire("object:modified", { target: canvas.getActiveObject() });
      } finally {
        batch.end(); // never leave saves muted (failure posture)
      }
    },

    // Flip
    flipHorizontal: () => {
      canvas.getActiveObjects().forEach((o) => o.set("flipX", !o.flipX));
      canvas.renderAll();
      canvas.fire("object:modified", { target: canvas.getActiveObject() });
    },
    flipVertical: () => {
      canvas.getActiveObjects().forEach((o) => o.set("flipY", !o.flipY));
      canvas.renderAll();
      canvas.fire("object:modified", { target: canvas.getActiveObject() });
    },

    // Zoom
    autoZoom,
    getWorkspace,
    zoomIn: () => {
      let zoomRatio = canvas.getZoom();
      zoomRatio += 0.05;
      const center = canvas.getCenter();
      canvas.zoomToPoint(new Point(center.left, center.top), Math.min(zoomRatio, 3));
    },
    zoomOut: () => {
      let zoomRatio = canvas.getZoom();
      zoomRatio -= 0.05;
      const center = canvas.getCenter();
      canvas.zoomToPoint(new Point(center.left, center.top), Math.max(zoomRatio, 0.1));
    },

    // Canvas size
    changeSize: (value) => {
      const workspace = getWorkspace();
      workspace?.set(value);
      // WYSIWYG fix (2026-07-18, caught by probe-wysiwyg): set() does NOT
      // refresh aCoords, and isOnScreen() reads the CACHED coords — after a
      // doc grows (24×36 → 42×63), skipOffscreen culling still used the old
      // rect, so the white page rect was CULLED from any single-surface
      // region export below the old bounds (transparent print background)
      // and from the live view when zoomed wholly below them. One call
      // makes the workspace's geometry truth immediate.
      workspace?.setCoords();
      autoZoom();
      save();
    },
    getWidth: () => getWorkspace()?.width || DEFAULT_WIDTH,
    getHeight: () => getWorkspace()?.height || DEFAULT_HEIGHT,

    // Background. No internal save(): the color wheel calls this per tick
    // while dragging — callers commit once (debounced / on blur / on click).
    // All three verbs keep data.bgRecipe (the persisted FillValue truth —
    // see the poster-background block above rehydrateWorkspaceBackground)
    // in sync, so a later texture can never survive a solid pick stale, and
    // cancel any in-flight texture decode (last pick wins).
    changeBackground: (value) => {
      const workspace = getWorkspace();
      if (!workspace) return;
      cancelBgPattern(workspace);
      if (typeof value === 'string') {
        workspace.data = { ...workspace.data, bgRecipe: { type: 'solid', color: value } };
      }
      workspace.set({ fill: value });
      canvas.renderAll();
    },
    changeBackgroundGradient: (color1, color2, direction = 'vertical') => {
      const workspace = getWorkspace();
      if (!workspace) return;

      const coords = direction === 'horizontal'
        ? { x1: 0, y1: 0, x2: workspace.width, y2: 0 }
        : direction === 'vertical'
          ? { x1: 0, y1: 0, x2: 0, y2: workspace.height }
          : { x1: 0, y1: 0, x2: workspace.width, y2: workspace.height }; // diagonal

      const gradient = new Gradient({
        type: 'linear',
        coords: coords,
        colorStops: [
          { offset: 0, color: color1 },
          { offset: 1, color: color2 },
        ],
      });
      cancelBgPattern(workspace);
      workspace.data = {
        ...workspace.data,
        bgRecipe: {
          type: 'linear-gradient',
          angle: direction === 'horizontal' ? 0 : direction === 'vertical' ? 90 : 45,
          stops: [
            { offset: 0, color: color1 },
            { offset: 1, color: color2 },
          ],
        },
      };
      workspace.set({ fill: gradient });
      canvas.renderAll();
    },
    // THE poster-background recipe verb (page.setBackgroundRecipe contract):
    // one FillValue in, the workspace painted like objects paint —
    //   string | {type:'solid',color}            → flat color (sync)
    //   {type:'linear-gradient',stops,angle}     → full-stop Gradient at the
    //                                              TRUE angle (sync;
    //                                              percentage units — scales
    //                                              with the page)
    //   {type:'radial-gradient',stops}           → center-out Gradient (sync;
    //                                              same resolveFillSync path
    //                                              objects use — parity free)
    //   {type:'texture', id|src, scale?}         → repeating Pattern via the
    //                                              OBJECT texture machinery
    //                                              (resolveTextFillPaint: the
    //                                              id→URL law + the 72/300
    //                                              print-sharpness tile base)
    // Sync forms paint before the caller's commit; the texture form writes
    // the recipe synchronously (it rides the commit) and paints when the
    // decode lands — prior fill stays until then (no flash), and the land
    // never saves. Unknown FillValues leave the document untouched.
    setBackgroundRecipe: (recipe) => {
      const workspace = getWorkspace();
      if (!workspace || recipe == null) return;
      const value = typeof recipe === 'string' ? { type: 'solid', color: recipe } : recipe;
      if (value.type === 'texture' && (value.id || value.src)) {
        // Flat degrade color: the last plain color the user saw — the prior
        // fill when it was a solid, else the prior recipe's first stop, else
        // the previous texture's own degrade, else white. Computed BEFORE
        // the recipe write while the prior truth is still readable.
        const prior = workspace.fill;
        workspace.__bgFlatFill = typeof prior === 'string' && prior !== 'transparent'
          ? prior
          : (solidFromFillValue(workspace.data?.bgRecipe)
            ?? (typeof workspace.__bgFlatFill === 'string' ? workspace.__bgFlatFill : '#ffffff'));
        workspace.data = { ...workspace.data, bgRecipe: value };
        ensureBgFillSeam(workspace);
        applyBgTexture(canvas, workspace, value);
        return;
      }
      const paint = resolveFillSync(value);
      if (paint === undefined) return; // unknown form — no state change at all
      cancelBgPattern(workspace);
      workspace.data = { ...workspace.data, bgRecipe: value };
      workspace.set({ fill: paint });
      canvas.renderAll();
    },

    // Drawing mode
    enableDrawingMode: () => {
      canvas.discardActiveObject();
      canvas.renderAll();
      // v6 no longer creates a default brush — dereferencing
      // freeDrawingBrush without this throws on first entry.
      if (!canvas.freeDrawingBrush) canvas.freeDrawingBrush = new PencilBrush(canvas);
      canvas.isDrawingMode = true;
      canvas.freeDrawingBrush.width = strokeWidth;
      canvas.freeDrawingBrush.color = strokeColor;
    },
    disableDrawingMode: () => {
      canvas.isDrawingMode = false;
    },

    // Drawing brush settings
    setBrushType: (type) => {
      let brush;
      switch (type) {
        case 'spray':
          brush = new SprayBrush(canvas);
          brush.density = 20;
          break;
        case 'circle':
          brush = new CircleBrush(canvas);
          break;
        case 'pattern':
          brush = new PatternBrush(canvas);
          break;
        case 'pencil':
        default:
          brush = new PencilBrush(canvas);
          break;
      }
      // Preserve current settings
      const currentWidth = canvas.freeDrawingBrush?.width || strokeWidth;
      const currentColor = canvas.freeDrawingBrush?.color || strokeColor;
      canvas.freeDrawingBrush = brush;
      canvas.freeDrawingBrush.width = currentWidth;
      canvas.freeDrawingBrush.color = currentColor;
    },
    setDrawingBrushWidth: (width) => {
      if (canvas.freeDrawingBrush) {
        canvas.freeDrawingBrush.width = width;
      }
    },
    setDrawingBrushColor: (color) => {
      if (canvas.freeDrawingBrush) {
        canvas.freeDrawingBrush.color = color;
      }
    },
    getDrawingBrushWidth: () => canvas.freeDrawingBrush?.width || strokeWidth,
    getDrawingBrushColor: () => canvas.freeDrawingBrush?.color || strokeColor,

    // Delete selected. Batched: a multi-selection delete fires one
    // object:removed per member — unbatched that was one history entry per
    // object, and undo resurrected the selection one piece at a time
    // (global-undo audit, 2026-07-21). try/finally: a throwing remove must
    // never leave saves muted.
    delete: () => {
      batch.begin();
      try {
        canvas.getActiveObjects().forEach((object) => canvas.remove(object));
        canvas.discardActiveObject();
        canvas.renderAll();
      } finally {
        batch.end(); // ONE entry (dedupe no-ops an empty selection)
      }
    },

    // Add shapes
    addCircle: () => {
      const object = new Circle({
        ...CIRCLE_OPTIONS,
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth: strokeWidth,
        strokeDashArray: strokeDashArray,
      });
      addToCanvas(object);
    },
    addRectangle: () => {
      const object = new Rect({
        ...RECTANGLE_OPTIONS,
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth: strokeWidth,
        strokeDashArray: strokeDashArray,
      });
      addToCanvas(object);
    },
    addSoftRectangle: () => {
      const object = new Rect({
        ...RECTANGLE_OPTIONS,
        rx: 30,
        ry: 30,
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth: strokeWidth,
        strokeDashArray: strokeDashArray,
      });
      addToCanvas(object);
    },
    addTriangle: () => {
      const object = new Triangle({
        ...TRIANGLE_OPTIONS,
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth: strokeWidth,
        strokeDashArray: strokeDashArray,
      });
      addToCanvas(object);
    },
    addInverseTriangle: () => {
      const { height: HEIGHT, width: WIDTH } = TRIANGLE_OPTIONS;
      const object = new Polygon(
        [{ x: 0, y: 0 }, { x: WIDTH, y: 0 }, { x: WIDTH / 2, y: HEIGHT }],
        {
          ...TRIANGLE_OPTIONS,
          fill: fillColor,
          stroke: strokeColor,
          strokeWidth: strokeWidth,
          strokeDashArray: strokeDashArray,
        }
      );
      addToCanvas(object);
    },
    addDiamond: () => {
      const { height: HEIGHT, width: WIDTH } = DIAMOND_OPTIONS;
      const object = new Polygon(
        [
          { x: WIDTH / 2, y: 0 },
          { x: WIDTH, y: HEIGHT / 2 },
          { x: WIDTH / 2, y: HEIGHT },
          { x: 0, y: HEIGHT / 2 },
        ],
        {
          ...DIAMOND_OPTIONS,
          fill: fillColor,
          stroke: strokeColor,
          strokeWidth: strokeWidth,
          strokeDashArray: strokeDashArray,
        }
      );
      addToCanvas(object);
    },

    // Extended shapes
    addLine: () => {
      const object = new Line([0, 0, 300, 0], {
        left: 100,
        top: 100,
        stroke: strokeColor,
        strokeWidth: strokeWidth,
        strokeDashArray: strokeDashArray,
      });
      addToCanvas(object);
    },

    addArrow: () => {
      // Arrow using a path
      const arrowPath = 'M 0 50 L 250 50 L 250 25 L 300 50 L 250 75 L 250 50';
      const object = new Path(arrowPath, {
        left: 100,
        top: 100,
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth: strokeWidth,
        strokeDashArray: strokeDashArray,
      });
      addToCanvas(object);
    },

    addDoubleArrow: () => {
      // Double arrow using a path
      const arrowPath = 'M 0 50 L 50 25 L 50 40 L 250 40 L 250 25 L 300 50 L 250 75 L 250 60 L 50 60 L 50 75 Z';
      const object = new Path(arrowPath, {
        left: 100,
        top: 100,
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth: strokeWidth,
        strokeDashArray: strokeDashArray,
      });
      addToCanvas(object);
    },

    addStar: () => {
      // 5-point star
      const points = [];
      const outerRadius = 150;
      const innerRadius = 60;
      for (let i = 0; i < 10; i++) {
        const radius = i % 2 === 0 ? outerRadius : innerRadius;
        const angle = (Math.PI / 5) * i - Math.PI / 2;
        points.push({
          x: outerRadius + radius * Math.cos(angle),
          y: outerRadius + radius * Math.sin(angle),
        });
      }
      const object = new Polygon(points, {
        left: 100,
        top: 100,
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth: strokeWidth,
        strokeDashArray: strokeDashArray,
      });
      addToCanvas(object);
    },

    addStar6: () => {
      // 6-point star (Star of David / Hexagram)
      const points = [];
      const outerRadius = 150;
      const innerRadius = 75;
      for (let i = 0; i < 12; i++) {
        const radius = i % 2 === 0 ? outerRadius : innerRadius;
        const angle = (Math.PI / 6) * i - Math.PI / 2;
        points.push({
          x: outerRadius + radius * Math.cos(angle),
          y: outerRadius + radius * Math.sin(angle),
        });
      }
      const object = new Polygon(points, {
        left: 100,
        top: 100,
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth: strokeWidth,
        strokeDashArray: strokeDashArray,
      });
      addToCanvas(object);
    },

    addPentagon: () => {
      const points = [];
      const radius = 150;
      for (let i = 0; i < 5; i++) {
        const angle = (2 * Math.PI / 5) * i - Math.PI / 2;
        points.push({
          x: radius + radius * Math.cos(angle),
          y: radius + radius * Math.sin(angle),
        });
      }
      const object = new Polygon(points, {
        left: 100,
        top: 100,
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth: strokeWidth,
        strokeDashArray: strokeDashArray,
      });
      addToCanvas(object);
    },

    addHexagon: () => {
      const points = [];
      const radius = 150;
      for (let i = 0; i < 6; i++) {
        const angle = (2 * Math.PI / 6) * i - Math.PI / 2;
        points.push({
          x: radius + radius * Math.cos(angle),
          y: radius + radius * Math.sin(angle),
        });
      }
      const object = new Polygon(points, {
        left: 100,
        top: 100,
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth: strokeWidth,
        strokeDashArray: strokeDashArray,
      });
      addToCanvas(object);
    },

    addOctagon: () => {
      const points = [];
      const radius = 150;
      for (let i = 0; i < 8; i++) {
        const angle = (2 * Math.PI / 8) * i - Math.PI / 2;
        points.push({
          x: radius + radius * Math.cos(angle),
          y: radius + radius * Math.sin(angle),
        });
      }
      const object = new Polygon(points, {
        left: 100,
        top: 100,
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth: strokeWidth,
        strokeDashArray: strokeDashArray,
      });
      addToCanvas(object);
    },

    addHeart: () => {
      // Heart shape using SVG path
      const heartPath = 'M 150 30 C 100 0, 0 50, 0 120 C 0 200, 150 280, 150 280 C 150 280, 300 200, 300 120 C 300 50, 200 0, 150 30 Z';
      const object = new Path(heartPath, {
        left: 100,
        top: 100,
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth: strokeWidth,
        strokeDashArray: strokeDashArray,
        scaleX: 0.8,
        scaleY: 0.8,
      });
      addToCanvas(object);
    },

    addCross: () => {
      // Plus/Cross shape
      const crossPath = 'M 100 0 L 200 0 L 200 100 L 300 100 L 300 200 L 200 200 L 200 300 L 100 300 L 100 200 L 0 200 L 0 100 L 100 100 Z';
      const object = new Path(crossPath, {
        left: 100,
        top: 100,
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth: strokeWidth,
        strokeDashArray: strokeDashArray,
        scaleX: 0.8,
        scaleY: 0.8,
      });
      addToCanvas(object);
    },

    addSpeechBubble: () => {
      // Speech bubble using SVG path
      const bubblePath = 'M 50 0 L 250 0 Q 300 0, 300 50 L 300 150 Q 300 200, 250 200 L 100 200 L 50 250 L 75 200 L 50 200 Q 0 200, 0 150 L 0 50 Q 0 0, 50 0 Z';
      const object = new Path(bubblePath, {
        left: 100,
        top: 100,
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth: strokeWidth,
        strokeDashArray: strokeDashArray,
      });
      addToCanvas(object);
    },

    addCallout: () => {
      // Rectangle callout with pointer
      const calloutPath = 'M 0 0 L 300 0 L 300 150 L 180 150 L 150 200 L 120 150 L 0 150 Z';
      const object = new Path(calloutPath, {
        left: 100,
        top: 100,
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth: strokeWidth,
        strokeDashArray: strokeDashArray,
      });
      addToCanvas(object);
    },

    // Additional shapes
    addEllipse: () => {
      const object = new Ellipse({
        left: 100,
        top: 100,
        rx: 100,
        ry: 60,
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth: strokeWidth,
        strokeDashArray: strokeDashArray,
      });
      addToCanvas(object);
    },

    addParallelogram: () => {
      const path = 'M 50 0 L 250 0 L 200 100 L 0 100 Z';
      const object = new Path(path, {
        left: 100,
        top: 100,
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth: strokeWidth,
        strokeDashArray: strokeDashArray,
      });
      addToCanvas(object);
    },

    addTrapezoid: () => {
      const path = 'M 50 0 L 200 0 L 250 100 L 0 100 Z';
      const object = new Path(path, {
        left: 100,
        top: 100,
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth: strokeWidth,
        strokeDashArray: strokeDashArray,
      });
      addToCanvas(object);
    },

    addStar4: () => {
      // 4-point star
      const path = 'M 100 0 L 120 80 L 200 100 L 120 120 L 100 200 L 80 120 L 0 100 L 80 80 Z';
      const object = new Path(path, {
        left: 100,
        top: 100,
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth: strokeWidth,
        strokeDashArray: strokeDashArray,
      });
      addToCanvas(object);
    },

    addStar8: () => {
      // 8-point star
      const centerX = 100, centerY = 100, outerR = 100, innerR = 50;
      const points = [];
      for (let i = 0; i < 16; i++) {
        const r = i % 2 === 0 ? outerR : innerR;
        const angle = (Math.PI / 8) * i - Math.PI / 2;
        points.push({ x: centerX + r * Math.cos(angle), y: centerY + r * Math.sin(angle) });
      }
      const object = new Polygon(points, {
        left: 100,
        top: 100,
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth: strokeWidth,
        strokeDashArray: strokeDashArray,
      });
      addToCanvas(object);
    },

    addCloud: () => {
      // Cloud shape
      const path = 'M 60 120 C 20 120 0 90 20 60 C 0 30 30 0 70 10 C 90 -10 130 0 140 30 C 170 10 210 30 200 60 C 230 70 230 110 200 120 Z';
      const object = new Path(path, {
        left: 100,
        top: 100,
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth: strokeWidth,
        strokeDashArray: strokeDashArray,
        scaleX: 1.2,
        scaleY: 1.2,
      });
      addToCanvas(object);
    },

    addLightning: () => {
      // Lightning bolt
      const path = 'M 100 0 L 60 80 L 90 80 L 50 160 L 120 70 L 90 70 L 130 0 Z';
      const object = new Path(path, {
        left: 100,
        top: 100,
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth: strokeWidth,
        strokeDashArray: strokeDashArray,
      });
      addToCanvas(object);
    },

    addCrescent: () => {
      // Crescent moon
      const path = 'M 100 0 C 155 0 200 45 200 100 C 200 155 155 200 100 200 C 120 180 130 145 130 100 C 130 55 120 20 100 0 Z';
      const object = new Path(path, {
        left: 100,
        top: 100,
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth: strokeWidth,
        strokeDashArray: strokeDashArray,
      });
      addToCanvas(object);
    },

    addCheckmark: () => {
      // Checkmark
      const path = 'M 20 100 L 70 150 L 180 30 L 160 10 L 70 110 L 40 80 Z';
      const object = new Path(path, {
        left: 100,
        top: 100,
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth: strokeWidth,
        strokeDashArray: strokeDashArray,
      });
      addToCanvas(object);
    },

    addXMark: () => {
      // X mark
      const path = 'M 40 0 L 100 60 L 160 0 L 200 40 L 140 100 L 200 160 L 160 200 L 100 140 L 40 200 L 0 160 L 60 100 L 0 40 Z';
      const object = new Path(path, {
        left: 100,
        top: 100,
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth: strokeWidth,
        strokeDashArray: strokeDashArray,
      });
      addToCanvas(object);
    },

    addRibbon: () => {
      // Ribbon/Banner shape
      const path = 'M 0 30 L 30 30 L 30 0 L 270 0 L 270 30 L 300 30 L 270 50 L 270 80 L 30 80 L 30 50 L 0 50 Z';
      const object = new Path(path, {
        left: 100,
        top: 100,
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth: strokeWidth,
        strokeDashArray: strokeDashArray,
      });
      addToCanvas(object);
    },

    // Add text
    addText: (value, options = {}) => {
      // Default size/width scale with the artboard: a fixed 32px default is
      // unreadably small on a 1728px+ poster canvas.
      const ws = getWorkspace();
      const wsWidth = ws?.width || DEFAULT_WIDTH;
      if (options.fontSize === undefined) {
        options = { ...options, fontSize: Math.round(wsWidth * 0.05) };
      }
      const hugWidth = options.width === undefined;
      if (hugWidth) {
        // Measured after construction — seed with a wrap-proof width so the
        // measurement sees the intended single-line layout.
        options = { ...options, width: Math.round(wsWidth * 2) };
      }
      const object = new Textbox(value, {
        ...TEXT_OPTIONS,
        fill: fillColor,
        ...options,
      });
      if (hugWidth) {
        // HUG LAW (owner 2026-07-23: "the box is always too wide. It needs
        // to load at the correct size hugging the text"): width = the
        // longest rendered line + a hair of breathing room for the cursor.
        // Explicit options.width (presets, callers with layouts) is honored
        // untouched above.
        const ink = object.calcTextWidth ? object.calcTextWidth() : 0;
        if (ink > 0) object.set({ width: Math.ceil(ink + 4) });
      }
      addToCanvas(object);
    },

    // Add image (options.at = scene-coord placement, e.g. drag-drop point)
    addImage: (value, options) => addImageFromUrl(value, options),

    // Convert a DOM client-coordinate point into scene coordinates
    // (drag-drop onto the canvas, context-menu placement, ...)
    clientToScene: (clientX, clientY) => {
      const el = canvas.upperCanvasEl;
      if (!el) return null;
      const rect = el.getBoundingClientRect();
      const point = new Point(clientX - rect.left, clientY - rect.top);
      const vpt = canvas.viewportTransform;
      if (!vpt) return point;
      return util.transformPoint(point, util.invertTransform(vpt));
    },

    // Add a remote photo at print-grade resolution, capped: sources larger
    // than ~3× the workspace width (≈216 DPI at full-bleed) are downscaled
    // through an offscreen canvas first — a 20 MP Pexels original would bloat
    // the design JSON and every history snapshot for no visible print gain.
    addImageCapped: (url, maxPx) => {
      const workspace = getWorkspace();
      const cap = maxPx || Math.round((workspace?.width || DEFAULT_WIDTH) * 3);
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const longest = Math.max(img.naturalWidth, img.naturalHeight);
        if (longest <= cap) {
          addImageFromUrl(url);
          return;
        }
        try {
          const scale = cap / longest;
          const c = document.createElement('canvas');
          c.width = Math.round(img.naturalWidth * scale);
          c.height = Math.round(img.naturalHeight * scale);
          c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
          addImageFromUrl(c.toDataURL('image/jpeg', 0.9));
        } catch (err) {
          // CORS taint or canvas failure — fall back to the raw URL
          console.error('Downscale failed, inserting original:', err);
          addImageFromUrl(url);
        }
      };
      img.onerror = () => {
        console.error('Failed to load image for capped insert');
        window.dispatchEvent(new CustomEvent('cce-image-load-failed'));
      };
      img.src = url;
    },

    // Add an SVG string as a native vector group — stays crisp at any print
    // size (used by the QR panel; remote-PNG QR codes pixelated on posters).
    addSvgString: (svgString, options = {}) => {
      // v6: promise-based; `objects` may contain nulls (unsupported nodes) —
      // filter before grouping.
      loadSVGFromString(svgString)
        .then(({ objects, options: opts }) => {
          const parsed = (objects || []).filter(Boolean);
          if (parsed.length === 0) {
            throw new Error('empty SVG parse');
          }
          const group = util.groupSVGElements(parsed, opts);
          const workspace = getWorkspace();
          const targetWidth = options.targetWidth
            || Math.round((workspace?.width || DEFAULT_WIDTH) * 0.25);
          group.scaleToWidth(targetWidth);
          if (options.set) group.set(options.set);
          addToCanvas(group);
        })
        .catch(() => {
          console.error('Failed to parse SVG string');
          window.dispatchEvent(new CustomEvent('cce-image-load-failed'));
        });
    },

    // Print quality of a placed image: source pixels per printed inch
    // (the canvas is 72 px = 1 in, so effective DPI collapses to 72/scale).
    // Recipe-carrying bakes (text3d / emboss-text) are re-baked at print DPI
    // by the export pre-pass, so their STANDING bake is never the print — the
    // honest number for them is the DELIVERABLE print DPI: the 300 target,
    // reduced only when the object's physical size pushes the re-bake past
    // the runtime export caps (the owner saw a false 'Will print blurry'
    // badge on crisp-printing 3D text, 2026-07-19).
    getEffectiveDPI: (obj) => {
      const target = obj || selectedObjects[0];
      if (!(target instanceof FabricImage)) return null;
      const tool = target.data?.tool;
      if (tool === 'text3d' || tool === 'emboss-text') {
        const wIn = target.getScaledWidth() / 72;
        const hIn = target.getScaledHeight() / 72;
        if (!Number.isFinite(wIn) || !Number.isFinite(hIn) || wIn <= 0 || hIn <= 0) return null;
        const caps = getRenderCaps();
        const bySide = caps.exportSide / Math.max(wIn, hIn);
        const byArea = Math.sqrt(caps.exportArea / (wIn * hIn));
        return Math.round(Math.min(300, bySide, byArea));
      }
      const s = Math.max(target.scaleX || 1, target.scaleY || 1);
      if (!s || !Number.isFinite(s)) return null;
      return Math.round(72 / s);
    },

    // Pre-print check: every visible image below the DPI threshold.
    // Baked recipe text (text3d, emboss-text) re-bakes at print DPI in the
    // export pre-pass, so it is judged by its DELIVERABLE dpi (same math as
    // getEffectiveDPI) — which only dips below threshold for physically
    // enormous single objects (≈109in+ at the 16384 export side cap), the
    // exact case where warning IS the truth.
    getLowResImages: (threshold = 150) => {
      const caps = getRenderCaps();
      return canvas.getObjects()
        .filter((o) => o instanceof FabricImage && o.visible !== false)
        .map((o) => {
          const tool = o.data?.tool;
          if (tool === 'text3d' || tool === 'emboss-text') {
            const wIn = o.getScaledWidth() / 72;
            const hIn = o.getScaledHeight() / 72;
            if (!(wIn > 0) || !(hIn > 0)) return { object: o, dpi: 300 };
            return {
              object: o,
              dpi: Math.round(Math.min(300, caps.exportSide / Math.max(wIn, hIn),
                Math.sqrt(caps.exportArea / (wIn * hIn)))),
            };
          }
          return {
            object: o,
            dpi: Math.round(72 / Math.max(o.scaleX || 1, o.scaleY || 1)),
          };
        })
        .filter((r) => r.dpi < threshold);
    },

    // Add icon with metadata for color changing
    addIcon: (iconName, color = '#000000') => {
      const [prefix, name] = iconName.includes(':') ? iconName.split(':') : ['mdi', iconName];
      const iconUrl = `${API_BASE}/design/icons/svg/${prefix}/${name}.svg?color=${encodeURIComponent(color)}`;

      FabricImage.fromURL(iconUrl, { crossOrigin: "anonymous" })
        .then((image) => {
          const workspace = getWorkspace();
          image.scaleToWidth(Math.min(workspace?.width * 0.3 || 150, 200));
          // Store icon metadata for color changing
          image.set({
            iconName: iconName,
            iconColor: color,
            isIcon: true,
          });
          addToCanvas(image);
        })
        .catch(() => console.error('Failed to load icon:', iconName));
    },

    // Change icon color by re-fetching with new color
    changeIconColor: (newColor) => {
      const object = selectedObjects[0];
      if (!object || !object.isIcon || !object.iconName) return;

      const iconName = object.iconName;
      const [prefix, name] = iconName.includes(':') ? iconName.split(':') : ['mdi', iconName];
      const iconUrl = `${API_BASE}/design/icons/svg/${prefix}/${name}.svg?color=${encodeURIComponent(newColor)}`;

      // Store position/scale before replacing. changeIconColor REPLACES the
      // instance, so anything NOT copied here is lost on every recolor —
      // universal-effects U2 rider: carry the shadow slot (native shadow/lift/
      // glow/echo/sticker-drop) and the whole data bag (data.effects recipe +
      // data.fx live steps: bevel dome, contour bands, sticker plate). Without
      // this a tinted icon loses all its effects.
      const props = {
        left: object.left,
        top: object.top,
        scaleX: object.scaleX,
        scaleY: object.scaleY,
        angle: object.angle,
        flipX: object.flipX,
        flipY: object.flipY,
        opacity: object.opacity,
        shadow: object.shadow || null,
      };
      // Shallow copy — the old instance is discarded, so sharing the nested
      // effects bag / fx array is safe (the established data-carry posture).
      const carriedData = object.data ? { ...object.data } : undefined;

      FabricImage.fromURL(iconUrl, { crossOrigin: "anonymous" })
        .then((newImage) => {
          // Apply stored properties
          newImage.set({
            ...props,
            iconName: iconName,
            iconColor: newColor,
            isIcon: true,
          });
          if (carriedData) newImage.data = carriedData;

          // Remove old icon, add new one — coalesced so a recolor is ONE
          // undo step, not a remove entry followed by an add entry.
          batch.begin();
          try {
            canvas.remove(object);
            canvas.add(newImage);
            // Re-install the live fx seam on the fresh instance (the recolored
            // alpha re-derives the bevel dome / contour band from its own
            // silhouette) — same walker the load/undo path runs.
            reattachFxDeep([newImage]);
            canvas.setActiveObject(newImage);
            canvas.renderAll();
          } finally {
            batch.end(); // never leave saves muted (failure posture)
          }
        })
        .catch(() => {});
    },

    // Swap the selected shape's SILHOUETTE while keeping every style
    // customization (owner directive §3). Build the NEW plain fabric primitive
    // (buildSwapShape), FIT it to the current object's displayed box, carry
    // fill/stroke/dash/uniform/shadow + the WHOLE data bag (data.effects recipe
    // + data.fx live steps), then remove(old)/add(new) coalesced into ONE undo
    // entry (the changeIconColor posture, incl. the finally-end). Effects
    // re-derive from the new outline via reattachFxDeep — nothing pre-computed.
    swapShapeType: (newType) => {
      const object = selectedObjects[0];
      if (!object) return;
      const bare = buildSwapShape(newType);
      if (!bare) return;

      // Fit the new silhouette into the CURRENT box (fill the same
      // left/top/width×height; angle/flip/opacity kept). Non-uniform on
      // purpose — the box is the contract: swap the outline, keep the frame.
      const targetW = (object.width || 1) * (object.scaleX || 1);
      const targetH = (object.height || 1) * (object.scaleY || 1);
      const nw = bare.width || 1;
      const nh = bare.height || 1;
      bare.set({
        left: object.left,
        top: object.top,
        originX: object.originX,
        originY: object.originY,
        angle: object.angle || 0,
        flipX: !!object.flipX,
        flipY: !!object.flipY,
        opacity: object.opacity ?? 1,
        scaleX: targetW / nw,
        scaleY: targetH / nh,
        stroke: object.stroke ?? null,
        strokeWidth: object.strokeWidth || 0,
        strokeDashArray: object.strokeDashArray || null,
        strokeUniform: object.strokeUniform ?? false,
        shadow: object.shadow || null,
      });
      // Fill carries only when the source had one (a Line has none — keep the
      // fresh primitive's own default rather than blanking it).
      if (object.fill != null) bare.set({ fill: object.fill });
      // Corner radius transfers only Rect→Rect (the only target with corners);
      // every other target has no rx/ry, so the fresh instance starts clean —
      // shape-specific props that don't transfer are dropped by never carrying.
      if (bare instanceof Rect && (object.rx || object.ry)) {
        bare.set({ rx: object.rx || 0, ry: object.ry || 0 });
      }
      // The WHOLE data bag rides as-is (shallow copy — the old instance is
      // discarded, so sharing the nested effects recipe / fx step array is
      // safe, the established data-carry posture): data.effects + data.fx
      // re-bake from the NEW silhouette below.
      if (object.data) bare.data = { ...object.data };

      batch.begin();
      try {
        canvas.remove(object);
        canvas.add(bare);
        // Fresh instance → re-install the live fx seam; the bevel dome /
        // sticker plate / 3D bake re-derive from THIS silhouette's own alpha
        // (the load/undo walker — never pre-computed).
        reattachFxDeep([bare]);
        canvas.setActiveObject(bare);
        canvas.renderAll();
      } finally {
        batch.end(); // ONE undo entry (never leave saves muted)
      }
    },

    // ── Depth-Everywhere wave N (lane N2): two vector-editor powers, both
    // BAKING to plain fabric.Path (exportability law — no custom class ever
    // rides into a design). Both mirror swapShapeType's carry posture (box +
    // paint + shadow + the whole data bag + reattachFxDeep, ONE batch entry)
    // and lazy-load lib/geo (WASM ~529KB; never eager at boot — the geo lazy
    // law: dynamic import at verb time, exactly like the text3d graph loads
    // three). ───────────────────────────────────────────────────────────────

    // Round the corners of ANY non-Rect fill shape (triangle/polygon/path) by
    // baking a rounded plain fabric.Path — the Corners slider un-gated past
    // Rect. Rect keeps its LIVE rx/ry (changeCornerRadius, non-destructive);
    // circle/ellipse have no corners and line no area, so neither reaches here.
    //
    // Frame: LOCAL space. The silhouette rings come out in the object's own
    // center-origin units (shapeRings — the 2D twin of silhouette3d's exact
    // extraction), get filleted, and rebuild as a Path fitted into the CURRENT
    // displayed box (swapShapeType's exact carry) — the object keeps its live
    // scale/angle/flip, r reads in local units (the Rect rx/ry convention).
    //
    // THE 2r FEATURE LAW (F1, hard): sub-2r features ERASE under roundCorners'
    // opening. Realized ENGINE-EXACT here, not from a stroke-width guess: bake
    // at the requested r, and if it drops net area below 90% of the input
    // (a whole limb vanished — corner filleting alone loses only a sliver),
    // binary-search the largest r in [0, requested] whose output keeps ≥90%
    // area (≤6 offset composions — monotone-enough; the search settles just
    // under the erasure knee). Documented pick of the two the plan offered
    // (area-preservation vs makeRingsDistanceSampler medial width); this one is
    // engine-exact and ties to the OBSERVABLE outcome (area kept), not a proxy.
    roundShapeCorners: async (radiusUnits) => {
      const object = selectedObjects[0];
      if (!object) return;
      const r = Number(radiusUnits) || 0;
      if (!(r > 0)) return;
      const type = String(object.type || '').toLowerCase();
      // Rect = live rx/ry (elsewhere); circle/ellipse have no corners; line no
      // fill. Only the cornered fill shapes bake.
      if (!['triangle', 'polygon', 'polyline', 'path'].includes(type)) return;

      const [{ ringsOfObjectLocal }, { roundCorners }, { toFabricPathData, areaOfRings }] = await Promise.all([
        import('../lib/geo/shapeRings.js'),
        import('../lib/geo/exactOffset.js'),
        import('../lib/geo/booleans2d.js'),
      ]);
      const rings = ringsOfObjectLocal(object);
      if (!rings.length) return;

      // 2r FEATURE CLAMP — area-band LINEAR SCAN (engine-exact). The exactOffset
      // header's suggested "area ≥ 0.90·A0" guards only SHRINKAGE (openClose
      // erases sub-2r strokes); our mandated closeOpen fails the OTHER way — it
      // BRIDGES sub-2r gaps, so area first DIPS (tips shave), then RISES past A0
      // (gaps fill to a blob — measured ~4.5× on a thin star), then CRASHES to 0
      // (annihilation). That non-monotonic curve breaks a two-sided binary search
      // (bridging brings area back through the band on the far side), so scan r
      // UPWARD from 0 and take the last CONTIGUOUS in-band step — the first
      // feature-preserving interval, before shave/bridge/annihilate leaves ±15%.
      // A shape whose corners are obtuse (rounded rect, hexagon) never leaves the
      // band → the requested r applies untouched; only spiky features clamp.
      const A0 = await areaOfRings(rings);
      const BAND = 0.15;
      const inBand = (a) => A0 > 0 && Math.abs(a - A0) <= BAND * A0;
      let out = await roundCorners(rings, r, { order: 'closeOpen' });
      if (A0 > 0 && !inBand(await areaOfRings(out))) {
        // Fine ABSOLUTE steps (shape-scaled, not rReq/N — a huge rReq must not
        // overshoot the small safe ceiling), walking up from 0 until the band
        // exits. Cheap: a spiky shape leaves the band within a few steps, and
        // the break ends the scan there (the cap only bounds a pathological run).
        const step = Math.max(1, Math.sqrt(A0) / 30);
        let best = rings; // r=0 (untouched) always in band
        for (let i = 1, rr = step; i <= 60 && rr <= r + 1e-6; i += 1, rr += step) {
          const cand = await roundCorners(rings, Math.min(rr, r), { order: 'closeOpen' }); // eslint-disable-line no-await-in-loop
          if (inBand(await areaOfRings(cand))) best = cand; else break; // eslint-disable-line no-await-in-loop
        }
        out = best;
      }
      const pathData = toFabricPathData(out);
      if (!pathData.length) return;

      // Rounding must NOT resize the shape (a box-fit would scale a corner-shaved
      // outline back up, growing it). Keep the object's own scale/angle/flip and
      // re-center on its center — corners soften in place, size held.
      const bare = new Path(pathData, {});
      const center = typeof object.getCenterPoint === 'function'
        ? object.getCenterPoint() : { x: object.left, y: object.top };
      bare.set({
        originX: 'center',
        originY: 'center',
        left: center.x,
        top: center.y,
        angle: object.angle || 0,
        flipX: !!object.flipX,
        flipY: !!object.flipY,
        opacity: object.opacity ?? 1,
        scaleX: object.scaleX || 1,
        scaleY: object.scaleY || 1,
        stroke: object.stroke ?? null,
        strokeWidth: object.strokeWidth || 0,
        strokeDashArray: object.strokeDashArray || null,
        strokeUniform: object.strokeUniform ?? false,
        shadow: object.shadow || null,
      });
      if (object.fill != null) bare.set({ fill: object.fill });
      if (object.data) bare.data = { ...object.data };

      batch.begin();
      try {
        canvas.remove(object);
        canvas.add(bare);
        reattachFxDeep([bare]); // effects re-bake from THIS silhouette's alpha
        canvas.setActiveObject(bare);
        canvas.renderAll();
      } finally {
        batch.end(); // ONE undo entry (returns the original shape whole)
      }
    },

    // Combine 2+ selected shapes (any fill kind, incl. baked Paths) into ONE
    // plain fabric.Path via exact 2D booleans. Modes speak educator words:
    //   'merge'   → union (all outlines fused)
    //   'cut'     → subtract: the BOTTOM shape minus the ones on TOP (z-order —
    //               the rule the flyout item hint states)
    //   'overlap' → intersect (only where they all overlap)
    //
    // Frame: WORLD space. Each object's rings are transformed by its OWN
    // calcTransformMatrix (position/scale/rotation baked into the vertices),
    // the boolean runs in canvas coordinates, and the result bakes as a Path
    // whose GEOMETRY is the answer — identity transform, so no rotation trick
    // survives into the export (the cleanest exportable form; documented choice
    // over a bottom-local frame, which would mix one live transform with
    // vertex-baked others). Carries the BOTTOM object's paint/shadow/opacity +
    // data bag (data.fx re-attaches via reattachFxDeep); ONE batch entry.
    combineShapes: async (mode) => {
      const COMBINABLE = new Set(['rect', 'circle', 'ellipse', 'triangle', 'polygon', 'polyline', 'path']);
      const active = (typeof canvas.getActiveObjects === 'function' ? canvas.getActiveObjects() : selectedObjects) || [];
      const shapes = active.filter((o) => o && COMBINABLE.has(String(o.type || '').toLowerCase()));
      if (shapes.length < 2) return;

      // Bottom-most in z-order is the style + subtract anchor.
      const all = canvas.getObjects();
      const ordered = shapes.slice().sort((a, b) => all.indexOf(a) - all.indexOf(b));
      const bottom = ordered[0];
      const others = ordered.slice(1);

      const [{ ringsOfObjectLocal }, { unionAllRings, subtractRings, intersectRings, toFabricPathData }] = await Promise.all([
        import('../lib/geo/shapeRings.js'),
        import('../lib/geo/booleans2d.js'),
      ]);
      // Object rings → WORLD (canvas) coordinates via the object's matrix.
      const worldRingsOf = (o) => {
        const m = o.calcTransformMatrix();
        return ringsOfObjectLocal(o).map((ring) => ring.map(([x, y]) => {
          const p = util.transformPoint(new Point(x, y), m);
          return [p.x, p.y];
        }));
      };
      const bottomRings = worldRingsOf(bottom);
      const otherSets = others.map(worldRingsOf).filter((s) => s.length);
      if (!bottomRings.length) return;

      let result;
      if (mode === 'merge') {
        result = await unionAllRings([bottomRings, ...otherSets]);
      } else if (mode === 'overlap') {
        result = bottomRings;
        for (const s of otherSets) result = await intersectRings(result, s); // eslint-disable-line no-await-in-loop
      } else if (mode === 'cut') {
        const othersUnion = otherSets.length ? await unionAllRings(otherSets) : [];
        result = othersUnion.length ? await subtractRings(bottomRings, othersUnion) : bottomRings;
      } else {
        return;
      }
      const pathData = toFabricPathData(result);
      if (!pathData.length) return; // empty (e.g. no overlap) — no destructive change

      // New Path in world coords (fabric derives left/top from the bbox → it
      // lands where the geometry is). Carry the BOTTOM style; geometry is
      // scale-1/angle-0, so a non-uniform bottom stroke folds into the width.
      const bare = new Path(pathData, {});
      const avgScale = (Math.abs(bottom.scaleX || 1) + Math.abs(bottom.scaleY || 1)) / 2;
      bare.set({
        fill: bottom.fill ?? null,
        stroke: bottom.stroke ?? null,
        strokeWidth: (bottom.strokeWidth || 0) * (bottom.strokeUniform ? 1 : avgScale),
        strokeDashArray: bottom.strokeDashArray || null,
        strokeUniform: bottom.strokeUniform ?? false,
        opacity: bottom.opacity ?? 1,
        shadow: bottom.shadow || null,
      });
      if (bottom.data) bare.data = { ...bottom.data };

      batch.begin();
      try {
        canvas.discardActiveObject();
        shapes.forEach((o) => canvas.remove(o));
        canvas.add(bare);
        reattachFxDeep([bare]); // effects re-derive from the merged silhouette
        canvas.setActiveObject(bare);
        canvas.renderAll();
      } finally {
        batch.end(); // ONE undo entry (restores every original)
      }
    },

    // Swap the selected icon for a different one, KEEPING its current tint +
    // every effect (owner directive §3). changeIconColor's exact flow, but with
    // the NEW iconName and the CURRENT iconColor: re-fetch the tinted SVG, carry
    // transform + shadow + the whole data bag, remove(old)/add(new) coalesced
    // into ONE undo entry; effects re-derive via reattachFxDeep.
    swapIcon: (newIconName) => {
      const object = selectedObjects[0];
      if (!object || !object.isIcon || !newIconName) return;

      const iconColor = object.iconColor || '#000000';
      const [prefix, name] = newIconName.includes(':') ? newIconName.split(':') : ['mdi', newIconName];
      const iconUrl = `${API_BASE}/design/icons/svg/${prefix}/${name}.svg?color=${encodeURIComponent(iconColor)}`;

      // Carry exactly what changeIconColor carries (anything not copied is lost
      // when the instance is replaced): transform, shadow, and the whole data
      // bag (data.effects recipe + data.fx live steps).
      const props = {
        left: object.left,
        top: object.top,
        scaleX: object.scaleX,
        scaleY: object.scaleY,
        angle: object.angle,
        flipX: object.flipX,
        flipY: object.flipY,
        opacity: object.opacity,
        shadow: object.shadow || null,
      };
      const carriedData = object.data ? { ...object.data } : undefined;

      FabricImage.fromURL(iconUrl, { crossOrigin: 'anonymous' })
        .then((newImage) => {
          newImage.set({
            ...props,
            iconName: newIconName,
            iconColor,
            isIcon: true,
          });
          if (carriedData) newImage.data = carriedData;

          batch.begin();
          try {
            canvas.remove(object);
            canvas.add(newImage);
            reattachFxDeep([newImage]);
            canvas.setActiveObject(newImage);
            canvas.renderAll();
          } finally {
            batch.end(); // never leave saves muted (failure posture)
          }
        })
        .catch(() => {});
    },

    // Get current icon color
    getIconColor: () => {
      const object = selectedObjects[0];
      if (!object || !object.isIcon) return null;
      return object.iconColor || '#000000';
    },

    // Check if selected object is an icon
    isIcon: () => {
      const object = selectedObjects[0];
      return object?.isIcon === true;
    },

    // Image filters
    applyImageFilter: (filterType, value) => {
      canvas.getActiveObjects().forEach((object) => {
        if (!(object instanceof FabricImage)) return;

        // Initialize filters array if needed
        if (!object.filters) object.filters = [];

        // Find existing filter of this type
        const filterIndex = object.filters.findIndex(f => f && f.type === filterType);

        // v6: filter classes moved off Image (fabric.Image.filters) to the
        // top-level `filters` export — FabricImage.filters is undefined.
        let filter;
        switch (filterType) {
          case 'Brightness':
            filter = new filters.Brightness({ brightness: value });
            break;
          case 'Contrast':
            filter = new filters.Contrast({ contrast: value });
            break;
          case 'Saturation':
            filter = new filters.Saturation({ saturation: value });
            break;
          case 'Blur':
            filter = new filters.Blur({ blur: value });
            break;
          case 'Grayscale':
            filter = value ? new filters.Grayscale() : null;
            break;
          case 'Sepia':
            filter = value ? new filters.Sepia() : null;
            break;
          case 'Invert':
            filter = value ? new filters.Invert() : null;
            break;
          case 'Pixelate':
            filter = new filters.Pixelate({ blocksize: value });
            break;
          case 'Noise':
            filter = new filters.Noise({ noise: value });
            break;
          default:
            return;
        }

        if (filterIndex >= 0) {
          if (filter) {
            object.filters[filterIndex] = filter;
          } else {
            object.filters.splice(filterIndex, 1);
          }
        } else if (filter) {
          object.filters.push(filter);
        }

        object.applyFilters();
      });
      canvas.renderAll();
    },

    removeAllImageFilters: () => {
      canvas.getActiveObjects().forEach((object) => {
        if (!(object instanceof FabricImage)) return;
        object.filters = [];
        object.applyFilters();
      });
      canvas.renderAll();
    },

    getImageFilterValue: (filterType) => {
      const object = selectedObjects[0];
      if (!(object instanceof FabricImage) || !object.filters) return null;

      const filter = object.filters.find(f => f && f.type === filterType);
      if (!filter) return null;

      switch (filterType) {
        case 'Brightness': return filter.brightness || 0;
        case 'Contrast': return filter.contrast || 0;
        case 'Saturation': return filter.saturation || 0;
        case 'Blur': return filter.blur || 0;
        case 'Pixelate': return filter.blocksize || 4;
        case 'Noise': return filter.noise || 0;
        case 'Grayscale':
        case 'Sepia':
        case 'Invert':
          return true;
        default: return null;
      }
    },

    hasImageFilter: (filterType) => {
      const object = selectedObjects[0];
      if (!(object instanceof FabricImage) || !object.filters) return false;
      return object.filters.some(f => f && f.type === filterType);
    },

    // Text properties — every setter below routes through the ONE selection
    // rule first (routeToLiveSelection): highlighted characters get a
    // per-char style while the teacher is editing, everything else falls
    // through to the whole-object body exactly as before (ELA-tools lane).
    changeFontSize: (value) => {
      if (routeToLiveSelection({ fontSize: value })) return;
      canvas.getActiveObjects().forEach((object) => {
        if (isTextType(object.type)) object.set({ fontSize: value });
      });
      canvas.renderAll();
    },
    getActiveFontSize: () => selectedObjects[0]?.get("fontSize") || FONT_SIZE,

    // Async: the face loads (network/disk-cached for Google families) before
    // it's applied, then commits its own history entry — the toolbar's
    // immediate commit is a dedupe no-op since nothing changed yet.
    changeFontFamily: (value) => {
      setFontFamily(value);
      ensureFont(value).then((res) => {
        if (res && res.ok === false) {
          window.dispatchEvent(new CustomEvent('cce-font-load-failed', { detail: { family: value } }));
          return;
        }
        // Per-selection when characters are highlighted (the face is loaded
        // either way first — a per-char font must not render as fallback).
        if (!routeToLiveSelection({ fontFamily: value })) {
          canvas.getActiveObjects().forEach((object) => {
            if (isTextType(object.type)) {
              object.set({ fontFamily: value });
              object.initDimensions?.();
              object.setCoords();
            }
          });
          canvas.requestRenderAll();
        }
        save();
      });
    },
    getActiveFontFamily: () => selectedObjects[0]?.get("fontFamily") || fontFamily,

    changeFontWeight: (value) => {
      const family = selectedObjects[0]?.get("fontFamily") || fontFamily;
      ensureFont(family, [value]).then(() => {
        if (!routeToLiveSelection({ fontWeight: value })) {
          canvas.getActiveObjects().forEach((object) => {
            if (isTextType(object.type)) object.set({ fontWeight: value });
          });
          canvas.requestRenderAll();
        }
        save();
      });
    },
    getActiveFontWeight: () => selectedObjects[0]?.get("fontWeight") || FONT_WEIGHT,

    changeFontStyle: (value) => {
      if (routeToLiveSelection({ fontStyle: value })) return;
      canvas.getActiveObjects().forEach((object) => {
        if (isTextType(object.type)) object.set({ fontStyle: value });
      });
      canvas.renderAll();
    },
    getActiveFontStyle: () => selectedObjects[0]?.get("fontStyle") || "normal",

    changeTextAlign: (value) => {
      canvas.getActiveObjects().forEach((object) => {
        if (isTextType(object.type)) object.set({ textAlign: value });
      });
      canvas.renderAll();
    },
    getActiveTextAlign: () => selectedObjects[0]?.get("textAlign") || "left",

    changeFontUnderline: (value) => {
      if (routeToLiveSelection({ underline: value })) return;
      canvas.getActiveObjects().forEach((object) => {
        if (isTextType(object.type)) object.set({ underline: value });
      });
      canvas.renderAll();
    },
    getActiveFontUnderline: () => selectedObjects[0]?.get("underline") || false,

    changeFontLinethrough: (value) => {
      if (routeToLiveSelection({ linethrough: value })) return;
      canvas.getActiveObjects().forEach((object) => {
        if (isTextType(object.type)) object.set({ linethrough: value });
      });
      canvas.renderAll();
    },
    getActiveFontLinethrough: () => selectedObjects[0]?.get("linethrough") || false,

    // Letter spacing (charSpacing in Fabric.js)
    changeCharSpacing: (value) => {
      canvas.getActiveObjects().forEach((object) => {
        if (isTextType(object.type)) object.set({ charSpacing: value });
      });
      canvas.renderAll();
    },
    getActiveCharSpacing: () => selectedObjects[0]?.get("charSpacing") || 0,

    // Line height
    changeLineHeight: (value) => {
      canvas.getActiveObjects().forEach((object) => {
        if (isTextType(object.type)) object.set({ lineHeight: value });
      });
      canvas.renderAll();
    },
    getActiveLineHeight: () => selectedObjects[0]?.get("lineHeight") || 1.16,

    // Shadow for ANY object type (shapes, images, icons, text). The old
    // changeTextShadow gated on text — kept below as the text-only alias.
    changeShadow: (shadowOptions) => {
      canvas.getActiveObjects().forEach((object) => {
        if (object.name === 'clip') return;
        if (shadowOptions === null) {
          object.set({ shadow: null });
        } else {
          object.set({
            shadow: new Shadow({
              color: shadowOptions.color || 'rgba(0,0,0,0.5)',
              blur: shadowOptions.blur ?? 5,
              offsetX: shadowOptions.offsetX ?? 2,
              offsetY: shadowOptions.offsetY ?? 2,
            }),
          });
        }
      });
      canvas.renderAll();
    },

    // Text shadow. DEPRECATED for the Style tab — route through
    // applyTextEffect('shadow'|'glow'|...) instead; kept for legacy callers.
    // ?? not ||: zero offsets are meaningful (glow) and must not coerce.
    changeTextShadow: (shadowOptions) => {
      canvas.getActiveObjects().forEach((object) => {
        if (isTextType(object.type)) {
          if (shadowOptions === null) {
            object.set({ shadow: null });
          } else {
            const shadow = new Shadow({
              color: shadowOptions.color || 'rgba(0,0,0,0.5)',
              blur: shadowOptions.blur ?? 5,
              offsetX: shadowOptions.offsetX ?? 2,
              offsetY: shadowOptions.offsetY ?? 2,
            });
            object.set({ shadow });
          }
        }
      });
      canvas.renderAll();
    },
    // DEPRECATED with changeTextShadow (see above); ?? so zero-offset glows
    // round-trip instead of snapping back to the 2px legacy defaults.
    getActiveTextShadow: () => {
      const shadow = selectedObjects[0]?.get("shadow");
      if (!shadow) return null;
      return {
        color: shadow.color || 'rgba(0,0,0,0.5)',
        blur: shadow.blur ?? 5,
        offsetX: shadow.offsetX ?? 2,
        offsetY: shadow.offsetY ?? 2,
      };
    },

    // Text outline (stroke on text). DEPRECATED for the Style tab — route
    // through applyTextEffect('outline'); kept for legacy callers.
    // paintFirst:'stroke' keeps the stroke from eating into the glyphs;
    // strokeUniform survives scaling; round joins avoid miter spikes.
    changeTextOutline: (color, width) => {
      canvas.getActiveObjects().forEach((object) => {
        if (isTextType(object.type)) {
          object.set({
            stroke: color,
            strokeWidth: width,
            paintFirst: 'stroke',
            strokeUniform: true,
            strokeLineJoin: 'round',
          });
        }
      });
      canvas.renderAll();
    },
    getActiveTextOutline: () => ({
      color: selectedObjects[0]?.get("stroke") || null,
      width: selectedObjects[0]?.get("strokeWidth") || 0,
    }),

    // Text highlight — fabric's built-in per-line background (serializes
    // natively). Canva's "background block" equivalent, v1.
    changeTextBackground: (value) => {
      canvas.getActiveObjects().forEach((object) => {
        if (isTextType(object.type)) object.set({ textBackgroundColor: value || '' });
      });
      canvas.renderAll();
    },
    getActiveTextBackground: () => selectedObjects[0]?.get("textBackgroundColor") || null,

    // Curved text via fabric's native text-on-path. shape = 'arc' (default —
    // a quadratic arch whose control-point height maps from the slider) |
    // 'circle' (a ring: |amount| = how far around it the words wrap, sign =
    // outward/top vs inward/bottom) | 'wave' (two sine periods, amount =
    // amplitude). The path serializes with the object; curveAmount + curveShape
    // re-hydrate the slider and the tiles.
    changeTextCurve: (amount, shape) => {
      canvas.getActiveObjects().forEach((object) => {
        if (!isTextType(object.type)) return;
        const amt = clampCurveAmount(amount);
        if (!amt) {
          // curveShape is kept: it is the shape MEMORY, not the on/off switch.
          object.set({ path: null, curveAmount: 0, objectCaching: true });
        } else {
          // Multi-line text is refused outright rather than merged — see
          // isCurveEligible; the panel's hint tells the teacher what to do.
          if (!isCurveEligible(object)) return;
          // Curve and live extrude are mutually exclusive both ways (the 3D
          // bake lays glyphs out itself — see patchTextFx's mirror rule):
          // curving drops the extrude step in the SAME write/history entry.
          if (Array.isArray(object.data?.fx)
            && object.data.fx.some((s) => s?.id === 'extrude')) {
            applyFxSteps(object, object.data.fx.filter((s) => s?.id !== 'extrude'));
          }
          const next = CURVE_SHAPES.includes(shape) ? shape : (object.curveShape || 'arc');
          object.set({
            ...deriveCurveProps(object, amt, next),
            curveAmount: amt,
            curveShape: next,
          });
        }
        object.set({ dirty: true });
        object.initDimensions?.();
        object.setCoords();
      });
      canvas.requestRenderAll();
    },
    getActiveTextCurve: () => selectedObjects[0]?.get("curveAmount") || 0,
    getActiveTextCurveShape: () => selectedObjects[0]?.get("curveShape") || 'arc',

    // ---------- Rich text: per-character emphasis ----------
    // Overrides live on obj.styles and serialize NATIVELY: Text.toObject()
    // emits them as a {start,end,style} range array, and loadJson's styles
    // sanitizer deliberately leaves arrays untouched (it only prunes the v5
    // nested-object shape) — so emphasis round-trips with no JSON_KEYS entry.
    // Range rule (textSelectionRange): the live in-canvas selection while the
    // teacher has characters highlighted, otherwise the WHOLE string —
    // teachers reach for these controls with the OBJECT selected far more
    // often than with individual characters selected.
    setSelectionTextStyle: (styleObj) => {
      activeTextObjects().forEach((object) => {
        const { start, end } = textSelectionRange(object);
        if (end <= start) return;
        object.setSelectionStyles(styleObj, start, end);
        restyleText(object);
      });
      canvas.renderAll();
    },
    getSelectionTextStyle,

    // THE selection-routing rule for UI call sites that own their own
    // whole-object fallback (FillSection's solid picks → setTextFill).
    // Per-char write when characters are highlighted (gates included —
    // see routeToLiveSelection), else runs the fallback and returns its
    // result so promise-aware apply() keeps working for async fills.
    forSelectionOrObject: (styleProps, applyWholeObject) =>
      (routeToLiveSelection(styleProps) ? undefined : applyWholeObject?.()),

    clearSelectionTextStyle: () => {
      const reset = {};
      RICH_TEXT_STYLE_KEYS.forEach((key) => { reset[key] = undefined; });
      activeTextObjects().forEach((object) => {
        const { start, end } = textSelectionRange(object);
        if (end <= start) return;
        // fabric's _extendStyles pickBy-drops undefined values, so this
        // DELETES the overrides rather than writing undefined ones;
        // stylesToArray then omits the emptied characters entirely.
        object.setSelectionStyles(reset, start, end);
        restyleText(object);
      });
      canvas.renderAll();
    },

    // ---------- Phonics: one-click colour-coding ----------
    // mode: 'none' | 'vowels' | 'syllables' | 'blends'. The RECIPE rides
    // data.phonics = {mode, colors} (data is in JSON_KEYS) and the per-char
    // fills ride obj.styles, which Text.toObject() emits natively — so both
    // halves round-trip with no extra JSON_KEYS entry, exactly like emphasis.
    //
    // Range space: phonicsPlan is handed object._text (fabric's GRAPHEME
    // array), so the flat indices it returns are precisely what
    // setSelectionStyles(style, start, end) consumes — see lib/phonics.js for
    // the fabric-source evidence that setSelectionStyles is flat-indexed and
    // maps to (line, char) internally.
    //
    // Unlike the emphasis controls this NEVER reads textSelectionRange: a
    // phonics chart is a property of the WHOLE string, so it re-derives from
    // index 0 every time (which is also what makes retyping re-colour).
    applyPhonics: (mode, colors) => {
      activeTextObjects().forEach((object) => {
        const wanted = PHONICS_MODES.includes(mode) ? mode : 'none';
        const data = { ...object.data };
        if (wanted === 'none') {
          delete data.phonics;
        } else {
          data.phonics = { mode: wanted, colors: colors || null };
        }
        object.data = data;
        repaintPhonics(object);
      });
      canvas.renderAll();
    },
    getPhonics,

    // 'super' | 'sub' | 'normal' — fabric's native setSuperscript/setSubscript.
    setTextScript: (kind) => {
      activeTextObjects().forEach((object) => {
        const { start, end } = textSelectionRange(object);
        if (end <= start) return;
        // fabric's _setScript multiplies the CURRENT fontSize by 0.6, so a
        // second press would compound (0.36) and super→sub would stack two
        // baseline shifts. Lift any existing script first: the buttons are
        // then idempotent and swap cleanly.
        object.setSelectionStyles({ fontSize: undefined, deltaY: undefined }, start, end);
        if (kind === 'super') object.setSuperscript(start, end);
        else if (kind === 'sub') object.setSubscript(start, end);
        restyleText(object);
      });
      canvas.renderAll();
    },

    // Size the text to its box: the largest fontSize whose text still fits the
    // box's WIDTH without forcing the box wider. 'grow' takes it there;
    // 'shrink' only ever comes DOWN to it (never enlarges).
    //
    // Width is the only real constraint a Textbox has. Its HEIGHT is derived —
    // initDimensions() ends with `height = calcTextHeight()` — so a height test
    // measures the text against itself and always passes, which silently froze
    // the search at the current size. Its width is likewise clamped UP to
    // dynamicMinWidth (the longest unbreakable word) on every re-wrap, so the
    // binding question is exactly "does the longest word fit the box".
    autoFitText: (mode = 'shrink') => {
      activeTextObjects().forEach((object) => {
        // Snapshot the target box BEFORE probing — the probes mutate it.
        const boxW = object.width;
        const startSize = object.fontSize || FONT_SIZE;
        const fitsAt = (size) => {
          object.set({ fontSize: size });
          // Force the target width back on by direct assignment: the `width`
          // SETTER clamps up to dynamicMinWidth at the current size, and
          // initDimensions() only ever grows the box — so a probe that had to
          // widen would poison every later probe with the widened width.
          object.width = boxW;
          object.initDimensions();
          // Having to widen past the target IS the definition of not fitting.
          return object.width <= boxW + 0.5;
        };
        // fitsAt() is monotonic in fontSize, so binary-search the largest
        // fitting size — a 1px linear walk would re-wrap the box ~400 times.
        let lo = AUTOFIT_MIN;
        let hi = AUTOFIT_MAX;
        let best = null;
        while (lo <= hi) {
          const mid = Math.floor((lo + hi) / 2);
          if (fitsAt(mid)) { best = mid; lo = mid + 1; } else { hi = mid - 1; }
        }
        const fitted = best ?? AUTOFIT_MIN;
        object.set({ fontSize: mode === 'shrink' ? Math.min(fitted, startSize) : fitted });
        object.width = boxW;
        restyleText(object);
      });
      canvas.renderAll();
    },

    // ---------- Text-inspector engine (Style tab) ----------
    // Slot-model effects + rich fills; recipes ride the data bag. No
    // self-save (the panel commits via apply()) — applyTextStyle is the one
    // exception (its batch.end() is the single history entry). Implemented
    // as hoisted internals below so applyTextStyle can compose them.
    // The ASYNC mutators are serialized through chainedTextFx: a texture/
    // gradient resolve in flight must finish before the next user action
    // mutates, or its post-await write-backs (data.textEffects, stroke
    // paint) clobber the newer state — and rapid fill clicks must land
    // last-click-wins. applyTextStyle composes the RAW internals, so
    // chaining it does not deadlock.
    setTextFill: (fillValue) => chainedTextFx(setTextFill, [fillValue]),
    getTextFill,
    applyTextEffect: (effectId, params) => chainedTextFx(applyTextEffect, [effectId, params]),
    removeTextEffect: (effectId) => chainedTextFx(removeTextEffect, [effectId]),
    clearTextEffects: () => chainedTextFx(clearTextEffects, []),

    // ---------- Universal effects (U1): object-generic verbs ----------
    // Kind-routed through the capability matrix (lib/fx/capabilityMatrix —
    // the ONE gate table); text delegates to the applyTextEffect family
    // above unchanged. Same async chain: a texture resolve in flight lands
    // before the next mutation. applyEffect resolves to {applied, denied}
    // — matrix denials are honest no-ops the UI can voice.
    applyEffect: (effectId, params) => chainedTextFx(applyEffect, [effectId, params]),
    removeEffect: (effectId) => chainedTextFx(removeEffect, [effectId]),
    clearEffects: () => chainedTextFx(clearEffects, []),
    getEffects,
    getObjectKind: () => objectKindOf(selectedObjects[0]),
    setTextHighlight,
    setTextList,
    getTextList,
    setLetterCase,
    getLetterCase,
    changeBlendMode,
    getActiveBlendMode,
    applyTextStyle: (preset) => chainedTextFx(applyTextStyle, [preset]),

    // ---------- Live effects (fx) ----------
    // The live-effects seam (lib/fx/fxRender): pixels come from a per-instance
    // _render interceptor, the RECIPE rides data.fx. These setters only write
    // the data bag (new object identity — history diffs by JSON), re-attach or
    // unwrap, and mark dirty (data is NOT in cacheProperties, so fabric will
    // not auto-dirty). Works on any selected object type, not just text —
    // 'Text' in the names matches the applyTextEffect family the UI knows.
    // No self-save: the UI drives commits via apply().
    setTextFx,
    patchTextFx,
    getTextFx,

    // ---------- Poster light (document-level shared sun) ----------
    // ONE light for every effect whose params opt in (lightMode 'poster' on
    // fx bevel/extrude — owner 2026-07-19: "a global lightsource that will
    // enforce a standard light direction for any object on the poster that
    // makes use of it"). STORAGE: workspace.data.posterLight (`data` is in
    // JSON_KEYS → serializes, loads, and rides every history snapshot — one
    // history entry per commit is simply the workspace-data write landing in
    // the normal save()). No self-save, same contract as setTextFx: the UI
    // commits via apply() (dial drag = defer ticks + commitNow on release).
    // Re-bakes ride the SEAM HASH (fxRender appends the light values for
    // opted-in stacks): this setter only dirties the opted-in objects so
    // fabric re-runs their _render — the hash miss does the actual work, and
    // custom-mode objects (unchanged hash) redraw from their stash for free.
    getPosterLight: () => sanitizePosterLight(getWorkspace()?.data?.posterLight),
    setPosterLight: (patch) => {
      const workspace = getWorkspace();
      if (!workspace) return;
      const next = sanitizePosterLight({
        ...sanitizePosterLight(workspace.data?.posterLight),
        ...(patch || {}),
      });
      workspace.data = { ...workspace.data, posterLight: next };
      const walk = (objs) => objs.forEach((o) => {
        if (typeof o.getObjects === 'function') walk(o.getObjects());
        const fx = Array.isArray(o.data?.fx) ? o.data.fx : null;
        if (fx && fx.some((s) => s?.params?.lightMode === 'poster')) {
          o.dirty = true;
          let g = o.group;
          while (g) { g.set('dirty', true); g = g.group; }
        }
      });
      walk(canvas.getObjects());
      canvas.requestRenderAll();
    },

    // One-click style preset (StylesTab): batch-apply fill/gradient, border,
    // shadow, text highlight, image filter recipes — ONE history entry.
    applyStyle: (preset) => {
      if (!preset?.props) return;
      const objs = canvas.getActiveObjects().filter((o) => o.name !== 'clip');
      if (!objs.length) return;
      objs.forEach((object) => {
        const t = object.type;
        const kind = isTextType(t)
          ? 'text'
          : object instanceof FabricImage ? (object.isIcon ? 'icon' : 'image') : 'shape';
        if (preset.appliesTo !== kind && preset.appliesTo !== 'any') return;
        const p = preset.props;

        if (p.gradient) {
          const coords = p.gradient.direction === 'vertical'
            ? { x1: 0, y1: 0, x2: 0, y2: object.height }
            : p.gradient.direction === 'diagonal'
              ? { x1: 0, y1: 0, x2: object.width, y2: object.height }
              : { x1: 0, y1: 0, x2: object.width, y2: 0 };
          object.set({
            fill: new Gradient({
              type: 'linear',
              coords,
              colorStops: [
                { offset: 0, color: p.gradient.from },
                { offset: 1, color: p.gradient.to },
              ],
            }),
          });
        } else if (p.fill !== undefined) {
          object.set({ fill: p.fill });
        }
        if (p.stroke !== undefined) {
          object.set({
            stroke: p.stroke,
            strokeWidth: p.stroke ? (p.strokeWidth ?? 2) : 0,
            strokeUniform: true,
          });
          if (isTextType(t) && p.stroke) object.set({ paintFirst: 'stroke' });
        }
        if (p.strokeDashArray !== undefined) object.set({ strokeDashArray: p.strokeDashArray });
        if (p.shadow !== undefined) {
          object.set({ shadow: p.shadow ? new Shadow(p.shadow) : null });
        }
        if (p.opacity !== undefined) object.set({ opacity: p.opacity });
        if (p.textBackgroundColor !== undefined && isTextType(t)) {
          object.set({ textBackgroundColor: p.textBackgroundColor });
        }
        if (p.filters && object instanceof FabricImage) {
          object.filters = [];
          const F = filters; // v6: top-level export, no longer on FabricImage
          if (p.filters.brightness) object.filters.push(new F.Brightness({ brightness: p.filters.brightness }));
          if (p.filters.contrast) object.filters.push(new F.Contrast({ contrast: p.filters.contrast }));
          if (p.filters.saturation) object.filters.push(new F.Saturation({ saturation: p.filters.saturation }));
          if (p.filters.sepia) object.filters.push(new F.Sepia());
          if (p.filters.grayscale) object.filters.push(new F.Grayscale());
          object.applyFilters();
        }
        object.setCoords();
      });
      canvas.renderAll();
      save();
    },

    // Text transform (uppercase/lowercase)
    transformTextCase: (transform) => {
      canvas.getActiveObjects().forEach((object) => {
        if (isTextType(object.type)) {
          const currentText = object.get("text");
          let newText = currentText;
          switch (transform) {
            case 'uppercase':
              newText = currentText.toUpperCase();
              break;
            case 'lowercase':
              newText = currentText.toLowerCase();
              break;
            case 'capitalize':
              newText = currentText.replace(/\b\w/g, (char) => char.toUpperCase());
              break;
            default:
              break;
          }
          object.set({ text: newText });
        }
      });
      canvas.renderAll();
    },

    // Fill & stroke
    changeFillColor: (value) => {
      setFillColor(value);
      // Highlighted characters get a per-char fill (the routing helper
      // refuses when Hollow/Trace own the fill, so the outline-recolor
      // branch below still wins there).
      if (routeToLiveSelection({ fill: value })) return;
      canvas.getActiveObjects().forEach((object) => {
        if (isTextType(object.type)) {
          // Keep the Style-tab recipe in sync (data.textFill is the panel's
          // source of truth once set) and route paint to the outline while
          // Hollow is active — a toolbar recolor must not resurrect the
          // transparent fill. Auto-colored effects (echo/outline) re-derive.
          const hollow = object.data?.textEffects?.stroke?.id === 'hollow';
          if (object.data?.textFill != null || hollow) {
            object.data = { ...object.data, textFill: { type: 'solid', color: value } };
          }
          object.set(hollow ? { stroke: value } : { fill: value });
          refreshTextGeometryEffects(object);
          return;
        }
        object.set({ fill: value });
      });
      canvas.renderAll();
    },
    getActiveFillColor: () => selectedObjects[0]?.get("fill") || fillColor,

    changeStrokeColor: (value) => {
      setStrokeColor(value);
      canvas.getActiveObjects().forEach((object) => {
        if (isTextType(object.type)) {
          object.set({ fill: value });
        } else {
          object.set({ stroke: value });
        }
      });
      if (canvas.freeDrawingBrush) canvas.freeDrawingBrush.color = value;
      canvas.renderAll();
    },
    getActiveStrokeColor: () => selectedObjects[0]?.get("stroke") || strokeColor,

    changeStrokeWidth: (value) => {
      setStrokeWidth(value);
      canvas.getActiveObjects().forEach((object) => object.set({ strokeWidth: value }));
      if (canvas.freeDrawingBrush) canvas.freeDrawingBrush.width = value;
      canvas.renderAll();
    },
    getActiveStrokeWidth: () => selectedObjects[0]?.get("strokeWidth") ?? strokeWidth,

    changeStrokeDashArray: (value) => {
      setStrokeDashArray(value);
      canvas.getActiveObjects().forEach((object) => object.set({ strokeDashArray: value }));
      canvas.renderAll();
    },
    getActiveStrokeDashArray: () => selectedObjects[0]?.get("strokeDashArray") || strokeDashArray,

    // Gradient fill (works on text too — v5 gradients fill glyphs fine)
    applyGradient: (color1, color2, direction = 'horizontal') => {
      canvas.getActiveObjects().forEach((object) => {
        if (object.name === 'clip') return;

        const coords = direction === 'horizontal'
          ? { x1: 0, y1: 0, x2: object.width, y2: 0 }
          : direction === 'vertical'
            ? { x1: 0, y1: 0, x2: 0, y2: object.height }
            : { x1: 0, y1: 0, x2: object.width, y2: object.height }; // diagonal

        const gradient = new Gradient({
          type: 'linear',
          coords: coords,
          colorStops: [
            { offset: 0, color: color1 },
            { offset: 1, color: color2 },
          ],
        });
        object.set({ fill: gradient });
      });
      canvas.renderAll();
    },
    removeGradient: () => {
      canvas.getActiveObjects().forEach((object) => {
        if (object.name === 'clip') return;
        object.set({ fill: fillColor });
      });
      canvas.renderAll();
    },
    hasGradient: () => {
      const obj = selectedObjects[0];
      return obj?.fill && typeof obj.fill === 'object' && obj.fill.type === 'linear';
    },

    // Opacity
    changeOpacity: (value) => {
      canvas.getActiveObjects().forEach((object) => object.set({ opacity: value }));
      canvas.renderAll();
    },
    // ?? not ||: an object at opacity 0 must report 0, not snap the UI to 1
    getActiveOpacity: () => selectedObjects[0]?.get("opacity") ?? 1,

    // Layer order (stacking changes don't fire any fabric event — commit
    // explicitly or they're invisible to undo)
    bringForward: () => {
      canvas.getActiveObjects().forEach((object) => canvas.bringObjectForward(object));
      canvas.renderAll();
      sendWorkspaceToBack();
      save();
    },
    sendBackwards: () => {
      canvas.getActiveObjects().forEach((object) => canvas.sendObjectBackwards(object));
      canvas.renderAll();
      sendWorkspaceToBack();
      save();
    },

    // Layer management
    getLayers: () => {
      // Get all objects except workspace, in reverse order (top to bottom)
      const objects = canvas.getObjects().filter((obj) => obj.name !== "clip");
      return objects.reverse().map((obj, index) => ({
        id: obj.id || `layer-${index}`,
        object: obj,
        type: obj.type,
        name: obj.name || `${obj.type || i18n.t('editor:layers.objectFallback')} ${index + 1}`,
        visible: obj.visible !== false,
        locked: obj.locked === true,
      }));
    },

    selectLayer: (object) => {
      canvas.discardActiveObject();
      canvas.setActiveObject(object);
      canvas.renderAll();
    },

    toggleLayerVisibility: (object) => {
      const newVisible = object.visible === false ? true : false;
      object.set({ visible: !newVisible });
      canvas.renderAll();
      save();
    },

    toggleLayerLock: (object) => {
      setObjectLocked(object, !(object.locked === true));
      canvas.renderAll();
      save();
    },

    moveLayerUp: (object) => {
      canvas.bringObjectForward(object);
      canvas.renderAll();
      sendWorkspaceToBack();
      save();
    },

    moveLayerDown: (object) => {
      canvas.sendObjectBackwards(object);
      canvas.renderAll();
      sendWorkspaceToBack();
      save();
    },

    moveLayerToTop: (object) => {
      canvas.bringObjectToFront(object);
      canvas.renderAll();
      sendWorkspaceToBack();
      save();
    },

    moveLayerToBottom: (object) => {
      canvas.sendObjectToBack(object);
      sendWorkspaceToBack();
      canvas.renderAll();
      save();
    },

    deleteLayer: (object) => {
      canvas.remove(object);
      canvas.discardActiveObject();
      canvas.renderAll();
      save();
    },

    duplicateLayer: (object) => {
      // clone(JSON_KEYS) + fx re-attach — same contract as duplicate/paste.
      object.clone(JSON_KEYS).then((cloned) => {
        cloned.set({
          left: (object.left || 0) + 20,
          top: (object.top || 0) + 20,
          evented: true,
        });
        canvas.add(cloned);
        reattachFxDeep([cloned]);
        canvas.setActiveObject(cloned);
        canvas.requestRenderAll();
        save();
      }).catch(() => {});
    },

    // No save() here: the Layers-panel slider calls this per tick — the
    // slider commits once on pointer-up via editor.commit().
    changeLayerOpacity: (object, opacity) => {
      object.set({ opacity: opacity });
      canvas.renderAll();
    },

    getLayerOpacity: (object) => {
      return object?.opacity || 1;
    },

    // ---------- Alignment & distribution ----------
    // Fabric footgun: objects inside an activeSelection hold selection-
    // relative coords, but getBoundingRect(true) is absolute — the old align
    // methods mixed the two and mis-placed everything on multi-selections.
    // withReselect discards the selection (coords become absolute), mutates,
    // then re-creates the selection. One history entry.
    alignLeft: () => alignToPage('left'),
    alignCenterH: () => alignToPage('centerH'),
    alignRight: () => alignToPage('right'),
    alignTop: () => alignToPage('top'),
    alignMiddleV: () => alignToPage('middleV'),
    alignBottom: () => alignToPage('bottom'),

    // Align the members of a multi-selection against each other
    alignSelection: (edge) => {
      const objs = canvas.getActiveObjects().filter((o) => o.name !== 'clip');
      if (objs.length < 2) return;
      // Union bounds must be computed while still selected? No — compute
      // AFTER discard (absolute coords), inside the reselect wrapper.
      withReselect((objects) => {
        const rects = objects.map((o) => o.getBoundingRect(true));
        const bounds = {
          left: Math.min(...rects.map((r) => r.left)),
          top: Math.min(...rects.map((r) => r.top)),
        };
        bounds.width = Math.max(...rects.map((r) => r.left + r.width)) - bounds.left;
        bounds.height = Math.max(...rects.map((r) => r.top + r.height)) - bounds.top;
        objects.forEach((object) => alignObjectToBounds(object, edge, bounds));
      });
    },

    // Space three-or-more objects evenly (first/last stay fixed)
    distribute: (axis) => {
      withReselect((objects) => {
        if (objects.length < 3) return;
        const rects = objects.map((o) => ({ o, br: o.getBoundingRect(true) }));
        rects.sort((a, b) => (axis === 'h' ? a.br.left - b.br.left : a.br.top - b.br.top));
        if (axis === 'h') {
          const first = rects[0];
          const last = rects[rects.length - 1];
          const span = (last.br.left + last.br.width) - first.br.left;
          const totalW = rects.reduce((s, r) => s + r.br.width, 0);
          const gap = (span - totalW) / (rects.length - 1);
          let x = first.br.left;
          for (const r of rects) {
            r.o.set({ left: r.o.left + (x - r.br.left) });
            x += r.br.width + gap;
          }
        } else {
          const first = rects[0];
          const last = rects[rects.length - 1];
          const span = (last.br.top + last.br.height) - first.br.top;
          const totalH = rects.reduce((s, r) => s + r.br.height, 0);
          const gap = (span - totalH) / (rects.length - 1);
          let y = first.br.top;
          for (const r of rects) {
            r.o.set({ top: r.o.top + (y - r.br.top) });
            y += r.br.height + gap;
          }
        }
      });
    },

    // ---------- Lock ----------
    toggleLock: () => {
      const objs = canvas.getActiveObjects().filter((o) => o.name !== 'clip');
      if (objs.length === 0) return;
      const nextLocked = !objs.every((o) => o.locked === true);
      objs.forEach((o) => setObjectLocked(o, nextLocked));
      canvas.requestRenderAll();
      save();
    },
    isLocked: () => {
      const objs = canvas.getActiveObjects().filter((o) => o.name !== 'clip');
      return objs.length > 0 && objs.every((o) => o.locked === true);
    },

    // Select everything selectable (also used by the context menu)
    selectAll: () => {
      const objects = canvas.getObjects().filter((o) => o.name !== 'clip' && o.selectable !== false);
      if (!objects.length) return;
      canvas.discardActiveObject();
      const selection = new ActiveSelection(objects, { canvas });
      canvas.setActiveObject(selection);
      canvas.requestRenderAll();
    },

    // Frame masks: clip the photo into a shape (relative clipPath in the
    // image's local coords, centered, sized to the visible crop window —
    // serializes natively and composes with the native crop: crop first,
    // then the frame masks the result).
    applyFrameShape: (shapeId) => {
      const obj = canvas.getActiveObject();
      if (!(obj instanceof FabricImage)) return;
      if (!shapeId) {
        obj.set({ clipPath: null, frameShape: null });
      } else {
        const clip = makeFrameClip(obj.width, obj.height, shapeId);
        if (!clip) return;
        obj.set({ clipPath: clip, frameShape: shapeId });
      }
      obj.set({ dirty: true });
      obj.setCoords();
      canvas.requestRenderAll();
      save();
    },

    // Replace the selected photo's source in place: keeps position, on-screen
    // footprint and frame (re-fit to the new source); crop window resets.
    replaceActiveImage: (value) => {
      const obj = canvas.getActiveObject();
      if (!(obj instanceof FabricImage)) return;
      const displayW = obj.getScaledWidth();
      // v6: setSrc is promise-based and rejects on load failure.
      obj.setSrc(value, { crossOrigin: 'anonymous' })
        .then(() => {
          const el = obj._originalElement || obj._element;
          obj.set({
            cropX: 0,
            cropY: 0,
            width: el.naturalWidth || el.width,
            height: el.naturalHeight || el.height,
          });
          obj.scaleToWidth(displayW);
          if (obj.frameShape) {
            const clip = makeFrameClip(obj.width, obj.height, obj.frameShape);
            if (clip) obj.set({ clipPath: clip });
          }
          obj.set({ dirty: true });
          obj.setCoords();
          canvas.requestRenderAll();
          canvas.fire('object:modified', { target: obj }); // one undo entry
        })
        .catch(() => {
          window.dispatchEvent(new CustomEvent('cce-image-load-failed'));
        });
    },

    // Fill the page with the selected photo: cover-scale to the workspace,
    // center, send to back, and lock it so foreground content stays clickable
    // (unlock via the toolbar/context menu to move it again).
    setAsBackground: () => {
      const obj = canvas.getActiveObject();
      const workspace = getWorkspace();
      if (!(obj instanceof FabricImage) || !workspace) return;
      const scale = Math.max(workspace.width / obj.width, workspace.height / obj.height);
      obj.set({ scaleX: scale, scaleY: scale, angle: 0 });
      obj.setPositionByOrigin(workspace.getCenterPoint(), 'center', 'center');
      canvas.sendObjectToBack(obj);
      canvas.sendObjectToBack(workspace);
      setObjectLocked(obj, true);
      canvas.discardActiveObject();
      obj.setCoords();
      canvas.requestRenderAll();
      save();
    },
    getActiveFrameShape: () => {
      const obj = selectedObjects[0];
      return obj instanceof FabricImage ? (obj.frameShape || null) : null;
    },

    // Numeric position/size in inches, relative to the workspace origin
    // (72 canvas px = 1"). Read side feeds the Position tab.
    getPositionInfo: () => {
      const obj = selectedObjects[0];
      const workspace = getWorkspace();
      if (!obj || !workspace || obj.name === 'clip') return null;
      const br = obj.getBoundingRect(true);
      return {
        x: (br.left - workspace.left) / 72,
        y: (br.top - workspace.top) / 72,
        w: br.width / 72,
        h: br.height / 72,
        angle: Math.round(obj.angle || 0),
      };
    },
    setPositionInches: (patch) => {
      const obj = canvas.getActiveObject();
      const workspace = getWorkspace();
      if (!obj || !workspace || obj instanceof ActiveSelection || obj.name === 'clip') return;
      const br = obj.getBoundingRect(true);
      if (patch.x !== undefined) {
        obj.set({ left: obj.left + (workspace.left + patch.x * 72 - br.left) });
      }
      if (patch.y !== undefined) {
        obj.set({ top: obj.top + (workspace.top + patch.y * 72 - br.top) });
      }
      if (patch.w !== undefined && br.width > 0 && patch.w > 0) {
        const factor = (patch.w * 72) / br.width;
        obj.set({ scaleX: (obj.scaleX || 1) * factor });
        if (patch.lockAspect !== false) obj.set({ scaleY: (obj.scaleY || 1) * factor });
      }
      if (patch.h !== undefined && br.height > 0 && patch.h > 0) {
        const factor = (patch.h * 72) / br.height;
        obj.set({ scaleY: (obj.scaleY || 1) * factor });
        if (patch.lockAspect !== false && patch.w === undefined) {
          obj.set({ scaleX: (obj.scaleX || 1) * factor });
        }
      }
      if (patch.angle !== undefined) obj.rotate(patch.angle);
      obj.setCoords();
      canvas.requestRenderAll();
    },

    // Corner radius for rectangles
    changeCornerRadius: (value) => {
      canvas.getActiveObjects().forEach((object) => {
        if (object instanceof Rect) object.set({ rx: value, ry: value });
      });
      canvas.renderAll();
    },

    // ---------- Smart objects (poster-project parametric recipes) ----------
    // Generate a recipe (params merged over its defaults), scale it to ~35%
    // of the workspace width, center it, and select it. The single
    // canvas.add fires one object:added → one history entry (mirrors
    // addImageFromUrl). Rejects propagate — the panel toasts them.
    // EXCEPTION (R1 rider, flagged by M1-B): recipes declaring
    // insertAtTrueScale make a PHYSICAL promise (params in inches × the
    // 72-units law — answer-box widthIn 3 ⇒ exactly 216 units); the preview
    // scale-to-35% would void it, so they insert at scale 1.
    // opts.gradeBand (Smart Tool modal): stamps SmartObjectData.gradeBand on
    // the object BEFORE the add, so the band memory rides the same (single)
    // history entry as the insert.
    addSmartObject: async (id, params = {}, opts = {}) => {
      const object = await generateSmartObject(id, params);
      if (opts.gradeBand) {
        object.data = { ...object.data, gradeBand: opts.gradeBand };
      }
      if (!getRecipe(id)?.insertAtTrueScale) {
        const workspace = getWorkspace();
        object.scaleToWidth(Math.round((workspace?.width || DEFAULT_WIDTH) * 0.35));
      }
      prepareSmartObject(object);
      addToCanvas(object);
      canvas.requestRenderAll();
      return object;
    },

    // Aspect lock: smart objects scale proportionally by default — only the
    // corner handles show. Explicit unlock (data.aspectLocked = false)
    // restores the side-stretch handles. Persisted inside `data` (rides
    // JSON_KEYS through every save).
    setSmartAspectLock: (locked) => {
      const object = canvas.getActiveObject();
      const data = object && smartObjectData(object);
      if (!data) return;
      object.data = { ...data, aspectLocked: !!locked };
      applySmartAspectLock(object);
      canvas.requestRenderAll();
      canvas.fire('object:modified', { target: object }); // one undo entry
    },

    // Rebuild the selected smart object with new params — facade over the
    // shared regenerateSmartObjectInPlace core (also used by live on-canvas
    // table-cell editing, which regenerates a group that is NOT the active
    // selection). opts (Smart Tool modal):
    //   target    — pin the object to regenerate (the modal has no scrim, so
    //               the active selection can drift mid-session);
    //   gradeBand — persist the band chip as SmartObjectData.gradeBand inside
    //               the same history entry.
    regenerateSelectedSmartObject: (newParams = {}, opts = {}) =>
      regenerateSmartObjectInPlace(
        canvas,
        batch,
        opts.target || canvas.getActiveObject(),
        newParams,
        opts.gradeBand ? { gradeBand: opts.gradeBand } : null,
      ),

    // {recipe, data} for the selected smart object, or null. recipe is null
    // (data still returned) when the design references a tool this build
    // doesn't register.
    getSelectedSmartObject: () => {
      // Single selection only: in a multi-select, [0] is just one member and
      // regenerate would no-op against the ActiveSelection wrapper anyway.
      if (selectedObjects.length !== 1) return null;
      const object = selectedObjects[0];
      if (!object) return null;
      const data = smartObjectData(object);
      if (!data) return null;
      return { recipe: getRecipe(data.tool) || null, data };
    },

    // ---------- 3D text (baked recipe images — lib/text3d) ----------
    // Delegates to text3dEditor via DYNAMIC import: three.js must never load
    // at editor boot; the chunk loads on first insert/regenerate (the 3D
    // panel usually pulled it already). Both land exactly ONE history entry.
    insertText3d: async (params, opts) => {
      const { insertText3d } = await import('../lib/text3d/text3dEditor.js');
      return insertText3d({ canvas, batch, save }, params, opts);
    },
    regenerateText3d: async (object, params) => {
      const { regenerateText3dInPlace } = await import('../lib/text3d/text3dEditor.js');
      return regenerateText3dInPlace(
        { canvas, batch, save },
        object || canvas.getActiveObject(),
        params,
      );
    },
    // Recipe params of a text3d object (a safe copy), or null. Sync and
    // import-free on purpose — selection UIs may call it every render and it
    // must not pull the three chunk.
    getText3dParams: (object) => {
      const target = object || selectedObjects[0];
      const data = target?.data;
      if (!data || data.tool !== 'text3d' || !data.params) return null;
      return JSON.parse(JSON.stringify(data.params));
    },

    // ---------- emboss text (baked 2D Bevel & Emboss — lib/emboss) ----------
    // Same delegation shape as text3d: a DYNAMIC import keeps the bake modules
    // (fabric StaticCanvas + the recipe fill resolver) off the editor boot
    // chunk; the chunk loads on first insert/regenerate. Both land exactly ONE
    // history entry.
    insertEmboss: async (params, opts) => {
      const { insertEmboss } = await import('../lib/emboss/embossTextEditor.js');
      return insertEmboss({ canvas, batch, save }, params, opts);
    },
    regenerateEmboss: async (object, params) => {
      const { regenerateEmbossInPlace } = await import('../lib/emboss/embossTextEditor.js');
      return regenerateEmbossInPlace(
        { canvas, batch, save },
        object || canvas.getActiveObject(),
        params,
      );
    },
    // Recipe params of an emboss object (a safe copy), or null. Sync and
    // import-free on purpose — selection UIs may call it every render and it
    // must not pull the bake chunk.
    getEmbossParams: (object) => {
      const target = object || selectedObjects[0];
      const data = target?.data;
      if (!data || data.tool !== 'emboss-text' || !data.params) return null;
      return JSON.parse(JSON.stringify(data.params));
    },

    canvas,
    selectedObjects,
  };

  // ---------- text-inspector engine internals (closure helpers) ----------
  // Hoisted function declarations: referenced by the API object above and by
  // each other (applyTextStyle composes the rest).

  /** Active text objects only — every engine setter iterates these. */
  function activeTextObjects() {
    return canvas.getActiveObjects()
      .filter((o) => o.name !== 'clip' && isTextType(o.type));
  }

  /** The character range the emphasis controls act on: the live in-canvas
   * selection while the teacher has characters highlighted, else the WHOLE
   * string. Indices are grapheme offsets (_text), which is the space
   * setSelectionStyles/get2DCursorLocation work in — text.length would drift
   * on emoji and combining marks. */
  function textSelectionRange(object) {
    const len = object._text?.length ?? (object.text || '').length;
    if (object.isEditing && object.selectionStart !== object.selectionEnd) {
      return {
        start: Math.max(0, Math.min(object.selectionStart, object.selectionEnd)),
        end: Math.min(len, Math.max(object.selectionStart, object.selectionEnd)),
        whole: false,
      };
    }
    return { start: 0, end: len, whole: true };
  }

  /** Re-measure after a per-char style write: styles change glyph metrics, so
   * a Textbox must re-wrap before its controls are re-placed. */
  function restyleText(object) {
    object.dirty = true;
    object.initDimensions?.();
    object.setCoords();
  }

  /** THE selection-routing rule (ELA-tools lane, 2026-07-19): the standard
   * controls (toolbar font/size/B/I/U, Color-section solid picks) style the
   * HIGHLIGHTED characters while the teacher is editing, the whole object
   * otherwise. Returns true when the per-char write happened — the caller
   * skips its whole-object body; false = fall through to today's behavior
   * EXACTLY (not editing, no range, non-text, or one of the gates below).
   *
   * Selection survives the DOM click by construction: fabric 6 only exits
   * editing on canvas mouse-downs/Escape, never on outside-DOM focus loss,
   * and selectionStart/End are read HERE at call time (textSelectionRange)
   * — the same mechanism the old Emphasize section relied on.
   *
   * Gates (both fall back to whole-object rather than doing nothing):
   *  • live 3D — only SELECTION_3D_SAFE_KEYS route per-char (per-glyph
   *    materials); metric keys re-bake whole-object as they do today.
   *  • fill while Hollow/Trace own the fill (fillRouting:'stroke' /
   *    clearsFill) — a per-char fill would resurrect the see-through body;
   *    whole-object keeps the outline-recolor behavior. */
  function routeToLiveSelection(styleProps) {
    const obj = canvas.getActiveObject();
    if (!obj || obj.name === 'clip' || !isTextType(obj.type)) return false;
    if (!obj.isEditing || obj.selectionStart === obj.selectionEnd) return false;
    const keys = Object.keys(styleProps || {});
    if (!keys.length) return false;
    const has3d = Array.isArray(obj.data?.fx) && obj.data.fx.some((s) => s?.id === 'extrude');
    if (has3d && !keys.every((k) => SELECTION_3D_SAFE_KEYS.has(k))) return false;
    if (keys.includes('fill')) {
      const strokeDef = EFFECT_DEFS[obj.data?.textEffects?.stroke?.id];
      if (strokeDef?.fillRouting === 'stroke' || strokeDef?.clearsFill) return false;
    }
    const { start, end } = textSelectionRange(obj);
    if (end <= start) return false;
    obj.setSelectionStyles(styleProps, start, end);
    restyleText(obj);
    canvas.renderAll();
    return true;
  }

  /** The per-char overrides shared by the whole emphasis range, or null.
   * SPARSE by design — a key appears only when EVERY character in the range
   * carries the same override, so the panel can tell "all bold" from "mixed"
   * and from "no override at all" (in which case it falls back to the
   * object's own props). */
  function getSelectionTextStyle() {
    const obj = canvas.getActiveObject() || selectedObjects[0];
    if (!obj || !isTextType(obj.type)) return null;
    const { start, end } = textSelectionRange(obj);
    if (end <= start) return null;
    const styles = obj.getSelectionStyles(start, end, false);
    if (!styles.length) return null;
    const common = {};
    RICH_TEXT_STYLE_KEYS.forEach((key) => {
      const first = styles[0]?.[key];
      if (first === undefined) return;
      if (styles.every((s) => s?.[key] === first)) common[key] = first;
    });
    return common;
  }

  /** The live recipe for the panel. Shape mirrors getTextList/getLetterCase. */
  function getPhonics() {
    const p = selectedObjects[0]?.data?.phonics;
    return { mode: p?.mode ?? 'none', colors: p?.colors ?? null };
  }

  /** Serialize the async text mutators: run strictly one-at-a-time in call
   * order. A pending texture/fill resolve otherwise interleaves with the
   * next click — its post-await write-backs (data.textEffects snapshot,
   * stroke paint) would clobber the newer state, and a slow texture could
   * land over a later, faster solid. The chain lives on the CANVAS (not this
   * closure): buildEditor re-runs per render and a rebuild mid-flight must
   * not fork the queue. Callers get the real call's promise (rejections
   * propagate to them; the chain itself never rejects). */
  function chainedTextFx(fn, args) {
    const run = () => fn(...args);
    const prev = canvas.__textFxChain || Promise.resolve();
    const p = prev.then(run, run);
    canvas.__textFxChain = p.then(() => undefined, () => undefined);
    return p;
  }

  /** Rich text fill (solid / gradient / texture). Resolves the FillValue,
   * routes the paint to the STROKE while Hollow is active, persists the
   * recipe on data.textFill and remembers the last solid color beside it
   * (data.textFillLastSolid) so the Color pill can restore it. */
  async function setTextFill(fillValue) {
    let paint;
    try {
      paint = await resolveTextFillPaint(fillValue);
    } catch {
      // Dead texture: keep the current fill, tell the panel (mirror of
      // cce-font-load-failed).
      window.dispatchEvent(new CustomEvent('cce-texture-load-failed'));
      return;
    }
    if (paint === undefined) return;
    const becomingSolid = typeof fillValue === 'string' || fillValue?.type === 'solid';
    activeTextObjects().forEach((object) => {
      const prev = object.data?.textFill;
      // lastSolid memory only captures true solids, only when leaving them
      const prevSolid = typeof prev === 'string' ? prev
        : prev?.type === 'solid' ? prev.color
        : prev == null && typeof object.fill === 'string' && object.fill !== 'transparent' ? object.fill
        : null;
      const data = { ...object.data, textFill: fillValue };
      if (prevSolid && !becomingSolid) data.textFillLastSolid = prevSolid;
      object.data = data;
      // Where the new paint lands is the DEF's call, not the id's:
      //   fillRouting:'stroke' (hollow) — paint the stroke, the glyphs stay open;
      //   clearsFill (trace)           — paint NOTHING. The letters must stay
      //     transparent to trace inside, and the guide stroke is the def's own
      //     color. data.textFill above still records the pick, so removing
      //     trace restores the color the teacher chose WHILE tracing;
      //   otherwise                    — paint the fill.
      const strokeDef = EFFECT_DEFS[data.textEffects?.stroke?.id];
      if (strokeDef?.fillRouting === 'stroke') object.set('stroke', paint);
      else if (!strokeDef?.clearsFill) object.set('fill', paint);
      object.dirty = true;
      // Auto-colored effects (echo inherits, outline contrasts) track the fill
      refreshTextGeometryEffects(object);
    });
    canvas.renderAll();
  }

  /** Recipe getter for the panel — live-fill derivation covers objects that
   * predate the inspector. */
  function getTextFill() {
    const obj = selectedObjects[0];
    if (!obj || !isTextType(obj.type)) return null;
    return obj.data?.textFill ?? fillValueFromLive(obj.fill);
  }

  // ---------- Live effects (fx) internals ----------
  /** Drop malformed/no-op steps, clamp params through each effect's own
   * sanitize. Steps with ids we don't know yet are kept VERBATIM — a design
   * saved by a future version keeps its recipe (the seam renders base-only
   * for them), it just can't be edited here. */
  function sanitizeFxSteps(steps) {
    if (!Array.isArray(steps)) return [];
    return steps
      .filter((s) => s && typeof s.id === 'string' && s.id)
      .map((s) => {
        const def = getFx(s.id);
        if (!def) return { id: s.id, params: { ...(s.params || {}) } };
        const params = def.sanitize(s.params || {});
        return def.isEmpty(params) ? null : { id: s.id, params };
      })
      .filter(Boolean);
  }

  /** Write one object's fx list: empty → unwrap completely (delete data.fx +
   * remove the _render patch — the object is stock fabric again); non-empty →
   * new data identity + ensure the interceptor. Ancestor group caches must be
   * dirtied by hand (child dirty does not bubble in v6). */
  function applyFxSteps(object, steps) {
    if (!steps.length) {
      removeFxRender(object);
      if (object.data?.fx) {
        const data = { ...object.data };
        delete data.fx;
        object.data = data;
      }
      // fx detach deletes the instance _render slot — put the die-cut
      // closing lane back if the object's border effect still wants it.
      syncStickerSeam(object);
    } else {
      object.data = { ...object.data, fx: steps };
      ensureFxRender(object);
    }
    // 3D pad follows the recipe in the SAME write: extrude slider to 0 drops
    // it, the first extrude tick grows it — runtime-only, no history impact.
    ensureJoystick3d(object);
    object.dirty = true;
    let g = object.group;
    while (g) { g.set('dirty', true); g = g.group; }
  }

  /** Engine mirror of a def's `shapeFxStep` marker (sticker shape modes +
   * 'Beveled sticker', 2026-07-20). The RECIPE truth stays on
   * data.textEffects exactly as before; what cannot be a native prop — a
   * circle/rect/square/rounded badge, or the band/badge dome — rides
   * data.fx as one filter step, kept in lockstep here on every apply/remove/
   * displace. `params` = the effect's full merged params, or null to drop
   * the step. Idle recipes (shape 'outline', beveled false) mirror to NO
   * step, so every pre-lane sticker stays byte-stable in JSON and never
   * grows an fx wrapper. Keyed off the def MARKER, never an effect id (the
   * effectDefs law). */
  function syncShapeFxStep(object, def, params) {
    const stepId = def?.shapeFxStep;
    if (!stepId) return;
    const steps = Array.isArray(object.data?.fx) ? object.data.fx : [];
    let want = null;
    if (params) {
      const fxDef = getFx(stepId);
      const raw = {
        shape: params.shape ?? 'outline',
        border: params.border,
        cornerRadius: params.cornerRadius,
        // Plate-model params (owner 2026-07-20): Wider/Taller per-axis margin,
        // the dome Finish, and the optional plate-edge contour all ride the
        // same fx step. Undefined values sanitize to the step's defaults.
        padWidth: params.padWidth,
        padHeight: params.padHeight,
        beveled: !!params.beveled,
        finish: params.finish,
        // The step carries the border's FillValue under the neutral key
        // `fill` — textures stay {type:'texture', id, scale} (IDs only: the
        // portability law; the filter re-derives the URL at run time).
        fill: params[def.strokeFillParam] ?? '#ffffff',
        edge: params.edge ?? null,
        // v3 keys (Depth-Everywhere N1): the mirror must carry them or any
        // re-sync (e.g. a Border drag) rewrites the step WITHOUT them and a
        // live puffy/scalloped sticker silently flattens.
        edgeStyle: params.edgeStyle,
        scallopSize: params.scallopSize,
        body: params.body,
        puffDepth: params.puffDepth,
        bodyMaterial: params.bodyMaterial,
      };
      const clean = fxDef ? fxDef.sanitize(raw) : raw;
      if (!fxDef || !fxDef.isEmpty(clean)) want = { id: stepId, params: clean };
    }
    const kept = steps.filter((s) => s?.id !== stepId);
    if (!want && kept.length === steps.length) return; // idle → idle: untouched
    if (want) {
      // Identity guard: an unchanged step must not rewrite data.fx (fresh
      // identity would churn the seam hash/stash on every idempotent
      // re-apply mid-drag).
      const cur = steps.find((s) => s?.id === stepId);
      if (cur) {
        try {
          if (JSON.stringify(cur.params) === JSON.stringify(want.params)) return;
        } catch (_) { /* rewrite */ }
      }
    }
    applyFxSteps(object, want ? [...kept, want] : kept);
  }

  function fxTargets() {
    return canvas.getActiveObjects().filter((o) => o.name !== 'clip');
  }

  /** Replace the whole fx stack on the selection. steps: [{id, params}] or
   * empty/null to strip live effects entirely. */
  function setTextFx(steps) {
    const clean = sanitizeFxSteps(steps);
    fxTargets().forEach((object) => applyFxSteps(object, clean));
    canvas.requestRenderAll();
  }

  /** Merge a params patch into ONE step (adding it if absent). An effect
   * whose merged params are a no-op (bevel/extrude: depth <= 0) drops out —
   * dragging the slider to 0 leaves a clean, unwrapped object (bevel +
   * extrude compose since 2026-07-16 — see FX_EXCLUSIVE, now empty).
   * Activating an effect still displaces any FX_EXCLUSIVE rivals, and
   * extrude straightens curved text (inside this one write → one history
   * entry; undo restores the rival/curve). */
  function patchTextFx(effectId, paramsPatch = {}) {
    const def = getFx(effectId);
    fxTargets().forEach((object) => {
      const steps = Array.isArray(object.data?.fx) ? object.data.fx : [];
      const idx = steps.findIndex((s) => s?.id === effectId);
      const merged = { ...(idx >= 0 ? steps[idx].params : null), ...paramsPatch };
      const params = def ? def.sanitize(merged) : merged;
      let next;
      if (def?.isEmpty(params)) {
        next = steps.filter((s) => s?.id !== effectId);
      } else {
        const rivals = FX_EXCLUSIVE[effectId] || [];
        const kept = steps.filter((s) => !rivals.includes(s?.id));
        const keptIdx = kept.findIndex((s) => s?.id === effectId);
        if (keptIdx >= 0) {
          next = kept.slice();
          next[keptIdx] = { id: effectId, params };
        } else {
          next = [...kept, { id: effectId, params }];
        }
        if (effectId === 'extrude' && object.curveAmount) {
          // The 3D bake lays glyphs out itself — fabric's text-on-path can't
          // ride along. Clear the curve exactly like changeTextCurve(0)
          // (curveShape stays as shape memory; objectCaching back on so the
          // seam leaves stash-only mode).
          object.set({ path: null, curveAmount: 0, objectCaching: true });
          object.initDimensions?.();
          object.setCoords();
        }
      }
      applyFxSteps(object, next);
    });
    canvas.requestRenderAll();
  }

  function getTextFx() {
    const obj = selectedObjects[0];
    return Array.isArray(obj?.data?.fx) ? obj.data.fx : null;
  }

  /** Apply/replace an effect from EFFECT_DEFS. Idempotent full-apply: slider
   * drags call it repeatedly with the full merged params. An occupant of a
   * claimed slot is displaced with its OWN slot resets first (tile-swap
   * semantics; sticker spans shadow+stroke atomically, so displacing it
   * clears both). Async only for the hollow fill/stroke resolution. */
  async function applyTextEffect(effectId, params = {}) {
    const def = EFFECT_DEFS[effectId];
    if (!def) return;
    const merged = { ...effectDefaults(def), ...params };
    for (const object of activeTextObjects()) {
      const effects = { ...(object.data?.textEffects || {}) };
      for (const slot of def.slots) {
        const occupant = effects[slot];
        if (!occupant?.id || occupant.id === effectId) continue;
        const occDef = EFFECT_DEFS[occupant.id];
        (occDef?.slots || [slot]).forEach((s) => {
          if (SLOT_RESET[s]) object.set(SLOT_RESET[s]);
          effects[s] = null;
        });
        if (holdsFillHostage(occDef)) {
          await restoreTextFill(object); // hollow/trace held the fill hostage
        }
        // A displaced sticker takes its badge/dome step with it (tile-swap
        // semantics cover the WHOLE effect, fx layer included).
        syncShapeFxStep(object, occDef, null);
      }
      // Hollow routes the fill recipe to the stroke, trace blanks it — either
      // way capture the live fill as the recipe FIRST (before derive runs), or
      // removing the effect has nothing to restore.
      if (holdsFillHostage(def) && object.data?.textFill == null) {
        object.data = { ...object.data, textFill: fillValueFromLive(object.fill) };
      }
      object.set(def.derive(merged, object.fontSize, textEffectCtx(object)));
      if (def.fillRouting === 'stroke') {
        let paint;
        try {
          paint = await resolveTextFillPaint(object.data?.textFill);
        } catch {
          paint = undefined; // dead texture → solid fallback below
        }
        object.set('stroke', paint ?? solidFromFillValue(object.data?.textFill) ?? '#000000');
      } else if (def.strokeFillParam) {
        // The effect's OWN stroke paint (sticker's border), independent of the
        // text fill. Solids and gradients already came back from derive above;
        // only a texture is missing, because derive OMITS the stroke key rather
        // than await an image decode (see strokePaintFromFillValue). Resolve it
        // here — the same id→URL + print-base scaling as any text fill, so a
        // border texture obeys the identical portability law.
        // Shape modes skip the resolve: there is no halo stroke to paint
        // (derive zeroed it) — the BADGE owns this paint, tiled by the fx
        // step itself at the same print-base density.
        const shapeMode = !!def.shapeFxStep && (merged.shape ?? 'outline') !== 'outline';
        const value = merged[def.strokeFillParam];
        if (!shapeMode && value?.type === 'texture' && value.id) {
          let paint;
          try {
            paint = await resolveTextFillPaint(value);
          } catch {
            paint = undefined; // dead texture → solid fallback below
          }
          // Never leave the border unpainted: a texture recipe carries no
          // color to fall back to, hence the explicit white default.
          object.set('stroke', paint ?? solidFromFillValue(value) ?? '#ffffff');
        }
      }
      const entry = { id: def.id, params: merged };
      def.slots.forEach((s) => { effects[s] = entry; });
      object.data = { ...object.data, textEffects: effects };
      // The live fx layer follows the recipe in the same apply (sticker
      // shape modes / Beveled sticker — no-op for every other def and for
      // idle sticker params).
      syncShapeFxStep(object, def, merged);
      // Seam BEFORE setCoords: the dims half must be live when coords bake,
      // or the first selection box after apply is one render stale.
      syncStickerSeam(object);
      object.dirty = true;
      object.setCoords();
    }
    canvas.renderAll();
  }

  /** Remove one effect (lit-tile tap): its slot resets, plus fill restore
   * when hollow was routing it to the stroke. */
  async function removeTextEffect(effectId) {
    const def = EFFECT_DEFS[effectId];
    if (!def) return;
    for (const object of activeTextObjects()) {
      const effects = { ...(object.data?.textEffects || {}) };
      let removed = false;
      def.slots.forEach((s) => {
        if (effects[s]?.id !== effectId) return;
        if (SLOT_RESET[s]) object.set(SLOT_RESET[s]);
        effects[s] = null;
        removed = true;
      });
      if (!removed) continue;
      if (holdsFillHostage(def)) await restoreTextFill(object);
      object.data = { ...object.data, textEffects: effects };
      syncShapeFxStep(object, def, null); // badge/dome step leaves with it
      syncStickerSeam(object); // sticker gone → stock instance again
      object.dirty = true;
      object.setCoords();
    }
    canvas.renderAll();
  }

  /** The None tile: reset both slots, restore the fill if Hollow held it. */
  async function clearTextEffects() {
    for (const object of activeTextObjects()) {
      const strokeDef = EFFECT_DEFS[object.data?.textEffects?.stroke?.id];
      const heldFill = holdsFillHostage(strokeDef);
      object.set(SLOT_RESET.shadow);
      object.set(SLOT_RESET.stroke);
      if (heldFill) await restoreTextFill(object);
      object.data = { ...object.data, textEffects: null };
      syncShapeFxStep(object, strokeDef, null); // sticker's fx layer too
      syncStickerSeam(object); // slots emptied → stock instance again
      object.dirty = true;
      object.setCoords();
    }
    canvas.renderAll();
  }

  // ---------- Universal effects (U1): object-generic verbs ----------
  // (owner 2026-07-21: "apply all the same effects to shapes and icons".)
  // Routing law: TEXT DELEGATES to the applyTextEffect family above, byte-
  // unchanged — zero text regressions is the hard law. Shapes/icons run the
  // generalized slot machinery below, gated per object by the capability
  // matrix (lib/fx/capabilityMatrix — the ONE table; denials are honest
  // no-ops returned in `denied`, never throws). Non-text recipes ride
  // data.effects (text keeps data.textEffects; every read is dual via
  // effectsBagOf, so old designs load forever).

  /** Recipe size basis for non-text derives — the seam's unitFor convention
   * EXACTLY (fxRender: u = fontSize/100 for text, min(w,h)/100 otherwise,
   * w/h in UNSCALED object units): derive(params, basis) yields u = basis/100.
   * Corner-drag resizes change scale, not w/h — native shadows scale with
   * the object (fabric applies object scaling to shadow offsets/blur) and
   * fx spill is drawn in object units, so proportions survive by
   * construction, matching the text fontSize-relative law. */
  function sizeBasisOf(object) {
    if (object.fontSize) return object.fontSize;
    const w = object.width || 0;
    const h = object.height || 0;
    return Math.max(1, Math.min(w, h));
  }

  /** derive() ctx for non-text kinds — the solid approximation of the
   * object's own paint (echo inherits it, outline auto-contrasts, hollow's
   * band carries it). Icons: the tint IS the paint. */
  function effectCtxFor(object) {
    if (objectKindOf(object) === 'icon') {
      return {
        fillColor: typeof object.iconColor === 'string' && object.iconColor
          ? object.iconColor : '#000000',
      };
    }
    const live = object.fill;
    const liveSolid = (typeof live === 'string' && live !== 'transparent' && live !== '') || live?.colorStops?.length
      ? solidFromFabricFill(live)
      : null;
    return { fillColor: liveSolid ?? solidFromFillValue(object.data?.hollowParked?.fill) ?? '#000000' };
  }

  /** Hollow-on-shape hostage bag (the fill-hostage law generalized): the
   * pre-hollow fill RECIPE and the Border tool's stroke state, parked once
   * on first apply, restored on removal/displacement. JSON-able recipes
   * only — data rides JSON_KEYS, so the parked state survives save/load. */
  function parkHollowState(object) {
    if (object.data?.hollowParked) return;
    object.data = {
      ...object.data,
      hollowParked: {
        fill: fillValueFromLive(object.fill),
        stroke: typeof object.stroke === 'string' ? object.stroke
          : object.stroke ? fillValueFromLive(object.stroke) : null,
        strokeWidth: Number(object.strokeWidth) || 0,
        strokeDashArray: Array.isArray(object.strokeDashArray)
          ? [...object.strokeDashArray] : null,
        paintFirst: object.paintFirst || 'fill',
        strokeUniform: object.strokeUniform !== false,
      },
    };
  }

  async function restoreHollowState(object) {
    const parked = object.data?.hollowParked;
    if (!parked) {
      // Nothing parked (hand-edited JSON): never leave an invisible shape.
      object.set({ fill: '#000000' });
      object.set(SLOT_RESET.stroke);
      return;
    }
    let paint;
    try {
      paint = await resolveTextFillPaint(parked.fill);
    } catch {
      paint = undefined;
    }
    object.set('fill', paint ?? solidFromFillValue(parked.fill) ?? '#000000');
    let strokePaint = null;
    if (typeof parked.stroke === 'string') {
      strokePaint = parked.stroke;
    } else if (parked.stroke) {
      try {
        strokePaint = (await resolveTextFillPaint(parked.stroke)) ?? solidFromFillValue(parked.stroke);
      } catch {
        strokePaint = solidFromFillValue(parked.stroke);
      }
    }
    object.set({
      stroke: strokePaint,
      strokeWidth: parked.strokeWidth || 0,
      strokeDashArray: Array.isArray(parked.strokeDashArray) ? [...parked.strokeDashArray] : null,
      paintFirst: parked.paintFirst || 'fill',
      strokeUniform: parked.strokeUniform !== false,
    });
    const data = { ...object.data };
    delete data.hollowParked;
    object.data = data;
  }

  /** Undo the NATIVE half of a non-text effect (removal/displacement):
   * shadow-slot resets, hollow-on-shape restores the parked fill/stroke.
   * The stroke slot's native props are otherwise NEVER reset on non-text
   * kinds — they belong to the Border tool, not to effects. */
  async function undoObjectEffectNative(object, kind, def) {
    if (!def) return;
    if ((def.slots || []).includes('shadow')) object.set(SLOT_RESET.shadow);
    if (kind === 'shape' && holdsFillHostage(def)
      && effectCapability(kind, def.id).mode === 'native') {
      await restoreHollowState(object);
    }
  }

  /** Engine mirror of the matrix's fx-contour cells (icon Outline/Hollow/
   * Trace, shape Trace): ONE contourBand step per object, kept in lockstep
   * with the slot recipe — the sibling of syncShapeFxStep. Keyed off the
   * matrix cell (`via` = contourBand), never off effect ids at call sites.
   * `merged` = the effect's full merged params, or null to drop the step. */
  function syncContourFxStep(object, kind, def, merged) {
    if (!def) return;
    if (effectCapability(kind, def.id).via !== CONTOUR_FX_STEP_ID) return;
    const steps = Array.isArray(object.data?.fx) ? object.data.fx : [];
    let want = null;
    if (merged) {
      const fxDef = getFx(CONTOUR_FX_STEP_ID);
      const ctx = effectCtxFor(object);
      const mode = def.id; // outline | hollow | trace — ids ARE the modes
      // Paint law per mode: hollow carries the OBJECT's own paint (the
      // routed-fill law's solid approximation — icon tint / shape solid);
      // outline defaults to auto-contrast against it (the text def's law);
      // trace owns its guide gray outright.
      const color = mode === 'hollow'
        ? ctx.fillColor
        : merged.color ?? (mode === 'outline'
          ? (luminance(ctx.fillColor) > 0.5 ? '#000000' : '#ffffff')
          : '#94a3b8');
      const raw = {
        mode,
        thickness: merged.thickness,
        color,
        dash: merged.dash,
        gap: merged.gap,
        // Multi-ring outlines (Depth Everywhere N3): the ring trio mirrors
        // through to the step; sanitize normalizes (rings ≤ 1 → keys
        // OMITTED, so every pre-lane step stays byte-stable).
        rings: merged.rings,
        ringGap: merged.ringGap,
        ringColor2: merged.ringColor2,
      };
      const clean = fxDef ? fxDef.sanitize(raw) : raw;
      if (!fxDef || !fxDef.isEmpty(clean)) want = { id: CONTOUR_FX_STEP_ID, params: clean };
    }
    const kept = steps.filter((s) => s?.id !== CONTOUR_FX_STEP_ID);
    if (!want && kept.length === steps.length) return; // idle → idle
    if (want) {
      const cur = steps.find((s) => s?.id === CONTOUR_FX_STEP_ID);
      if (cur) {
        try {
          if (JSON.stringify(cur.params) === JSON.stringify(want.params)) return;
        } catch (_) { /* rewrite */ }
      }
    }
    applyFxSteps(object, want ? [...kept, want] : kept);
  }

  /** patchTextFx's per-object body, scoped to ONE object (the generic verbs
   * gate per object through the matrix, so the selection-wide fxTargets walk
   * cannot be reused). Same merge/sanitize/rival/extrude-curve laws. */
  function applyFxStepToObject(object, effectId, paramsPatch = {}) {
    const def = getFx(effectId);
    const steps = Array.isArray(object.data?.fx) ? object.data.fx : [];
    const idx = steps.findIndex((s) => s?.id === effectId);
    const merged = { ...(idx >= 0 ? steps[idx].params : null), ...paramsPatch };
    const params = def ? def.sanitize(merged) : merged;
    let next;
    if (def?.isEmpty(params)) {
      next = steps.filter((s) => s?.id !== effectId);
    } else {
      const rivals = FX_EXCLUSIVE[effectId] || [];
      const kept = steps.filter((s) => !rivals.includes(s?.id));
      const keptIdx = kept.findIndex((s) => s?.id === effectId);
      if (keptIdx >= 0) {
        next = kept.slice();
        next[keptIdx] = { id: effectId, params };
      } else {
        next = [...kept, { id: effectId, params }];
      }
      if (effectId === 'extrude' && object.curveAmount) {
        // Same curve-clearing law as patchTextFx (text objects only reach
        // here through the generic depth route).
        object.set({ path: null, curveAmount: 0, objectCaching: true });
        object.initDimensions?.();
        object.setCoords();
      }
    }
    applyFxSteps(object, next);
  }

  /** Kind split of the active selection for the generic verbs. */
  function effectTargetsByKind() {
    const targets = canvas.getActiveObjects().filter((o) => o.name !== 'clip');
    const text = [];
    const rest = [];
    for (const o of targets) {
      if (objectKindOf(o) === 'text') text.push(o);
      else rest.push(o);
    }
    return { text, rest };
  }

  /** Apply one non-text effect to one object (cap.mode already vetted). */
  async function applyObjectEffect(object, kind, def, merged, cap) {
    const bag = { ...(effectsBagOf(object) || {}) };
    // Tile-swap displacement — the occupant leaves with its OWN resets
    // (native half + fx steps), exactly the text law.
    for (const slot of def.slots) {
      const occupant = bag[slot];
      if (!occupant?.id || occupant.id === def.id) continue;
      const occDef = EFFECT_DEFS[occupant.id];
      (occDef?.slots || [slot]).forEach((s) => { bag[s] = null; });
      await undoObjectEffectNative(object, kind, occDef);
      syncShapeFxStep(object, occDef, null);
      syncContourFxStep(object, kind, occDef, null);
    }
    if (cap.mode === 'native') {
      if (holdsFillHostage(def)) {
        // Hollow-on-shape: park fill + Border-tool stroke once, then paint
        // the native stroke from the parked fill recipe (the generalized
        // fill-hostage law — your shape's color becomes its outline).
        parkHollowState(object);
        const derived = def.derive(merged, sizeBasisOf(object), effectCtxFor(object));
        // Text-parity resize law: fabric TEXT ignores strokeUniform at paint
        // time, so a scaled text hollow band scales with the glyphs. Shapes
        // DO honor strokeUniform — pinning it would freeze the band in
        // screen px while the shape grows. Un-pin (parked value restores
        // the Border tool's choice on removal).
        derived.strokeUniform = false;
        object.set(derived);
        let paint;
        try {
          paint = await resolveTextFillPaint(object.data.hollowParked.fill);
        } catch {
          paint = undefined;
        }
        object.set('stroke', paint ?? solidFromFillValue(object.data.hollowParked.fill) ?? '#000000');
      } else {
        object.set(def.derive(merged, sizeBasisOf(object), effectCtxFor(object)));
      }
    } else {
      // fx cell: the native half is at most the drop shadow the derive
      // carries (sticker) — stroke-family keys never land on non-text kinds
      // (the Border tool owns them; see the matrix's degrade notes).
      const derived = def.derive(merged, sizeBasisOf(object), effectCtxFor(object));
      if (derived.shadow !== undefined && (def.slots || []).includes('shadow')) {
        object.set({ shadow: derived.shadow });
      }
      syncShapeFxStep(object, def, merged);
      syncContourFxStep(object, kind, def, merged);
    }
    const entry = { id: def.id, params: merged };
    def.slots.forEach((s) => { bag[s] = entry; });
    object.data = writeEffectsBag(object.data, bag);
    object.dirty = true;
    object.setCoords();
  }

  /** Non-text bag write: the general key wins; a stale textEffects alias
   * (an alias-keyed design that just got edited) is retired in the same
   * write so the JSON carries ONE truth. */
  function writeEffectsBag(data, bag) {
    const next = { ...data, effects: bag };
    if ('textEffects' in next) delete next.textEffects;
    return next;
  }

  /**
   * Object-generic applyEffect — kind-routed through the capability matrix.
   * Slot ids (EFFECT_DEFS) run the slot machinery; depth ids (bevel/extrude
   * — registered fx steps with no slot entry) run the fx-step body per
   * allowed object. Returns { applied, denied: [{id, kind, reason}] }.
   */
  async function applyEffect(effectId, params = {}) {
    const { text, rest } = effectTargetsByKind();
    const denied = [];
    let applied = 0;
    const def = EFFECT_DEFS[effectId];

    if (!def && getFx(effectId)) {
      for (const object of [...text, ...rest]) {
        const kind = objectKindOf(object);
        const cap = effectCapability(kind, effectId);
        if (cap.mode === 'unavailable') {
          denied.push({ id: effectId, kind, reason: cap.reason });
          continue;
        }
        applyFxStepToObject(object, effectId, params);
        applied += 1;
      }
      canvas.requestRenderAll();
      return { applied, denied };
    }
    if (!def) {
      return { applied: 0, denied: [{ id: effectId, kind: null, reason: 'Unknown effect' }] };
    }

    if (text.length) {
      // The WHOLE text half delegates to the untouched text engine.
      await applyTextEffect(effectId, params);
      applied += text.length;
    }
    const merged = { ...effectDefaults(def), ...params };
    for (const object of rest) {
      const kind = objectKindOf(object);
      const cap = effectCapability(kind, effectId);
      if (cap.mode === 'unavailable') {
        denied.push({ id: effectId, kind, reason: cap.reason });
        continue;
      }
      await applyObjectEffect(object, kind, def, merged, cap);
      applied += 1;
    }
    if (rest.length) canvas.renderAll();
    return { applied, denied };
  }

  /** Object-generic removeEffect — the lit-tile tap, any kind. */
  async function removeEffect(effectId) {
    const { text, rest } = effectTargetsByKind();
    if (text.length && EFFECT_DEFS[effectId]) await removeTextEffect(effectId);
    const def = EFFECT_DEFS[effectId];
    let touched = false;
    if (!def && getFx(effectId)) {
      for (const object of [...text, ...rest]) {
        const steps = Array.isArray(object.data?.fx) ? object.data.fx : [];
        if (!steps.some((s) => s?.id === effectId)) continue;
        applyFxSteps(object, steps.filter((s) => s?.id !== effectId));
        touched = true;
      }
      if (touched) canvas.requestRenderAll();
      return;
    }
    if (!def) return;
    for (const object of rest) {
      const kind = objectKindOf(object);
      if (!kind) continue;
      const bag = { ...(effectsBagOf(object) || {}) };
      let removed = false;
      def.slots.forEach((s) => {
        if (bag[s]?.id !== effectId) return;
        bag[s] = null;
        removed = true;
      });
      if (!removed) continue;
      await undoObjectEffectNative(object, kind, def);
      object.data = writeEffectsBag(object.data, bag);
      syncShapeFxStep(object, def, null);
      syncContourFxStep(object, kind, def, null);
      object.dirty = true;
      object.setCoords();
      touched = true;
    }
    if (touched) canvas.renderAll();
  }

  /** Object-generic None tile: clear both slots on every selected object. */
  async function clearEffects() {
    const { text, rest } = effectTargetsByKind();
    if (text.length) await clearTextEffects();
    let touched = false;
    for (const object of rest) {
      const kind = objectKindOf(object);
      if (!kind) continue;
      const bag = effectsBagOf(object);
      if (!bag || (!bag.shadow?.id && !bag.stroke?.id)) continue;
      const done = new Set();
      for (const slot of ['shadow', 'stroke']) {
        const id = bag[slot]?.id;
        if (!id || done.has(id)) continue; // sticker spans both slots
        done.add(id);
        const slotDef = EFFECT_DEFS[id];
        await undoObjectEffectNative(object, kind, slotDef);
        syncShapeFxStep(object, slotDef, null);
        syncContourFxStep(object, kind, slotDef, null);
      }
      object.data = writeEffectsBag(object.data, null);
      object.dirty = true;
      object.setCoords();
      touched = true;
    }
    if (touched) canvas.renderAll();
  }

  /** The live slot bag for the panel — dual-read, any kind. */
  function getEffects() {
    return effectsBagOf(selectedObjects[0]) ?? null;
  }

  /** Highlight: value = { color: hex, fade: 0..100 } | null. The rgba is
   * composed HERE so the panel's <input type=color> only ever sees hex
   * (params store color+fade separately). Legacy changeTextBackground stays
   * untouched for old callers. */
  function setTextHighlight(value) {
    activeTextObjects().forEach((object) => {
      if (value?.color) {
        object.set({ textBackgroundColor: rgba(value.color, 1 - (value.fade ?? 0) / 100) });
        object.data = { ...object.data, highlight: { color: value.color, fade: value.fade ?? 0 } };
      } else {
        object.set({ textBackgroundColor: '' });
        object.data = { ...object.data, highlight: null };
      }
    });
    canvas.renderAll();
  }

  /** Reversible letter case ('none'|'upper'|'lower'|'title'): the as-typed
   * string rides data.originalText while a case is active; 'none' restores
   * it and drops both keys. Inline edits are re-cased on editing:exited
   * (typed edits stay cased; "As typed" then returns the post-edit text). */
  /** Lists and letter case both own obj.text, so whichever rewrites it
   * re-anchors the case layer on the newly rendered string: prefixes render
   * first, the case then transforms what is DISPLAYED. Keeps "As typed"
   * reversible in either order. */
  function recaseAfterTextRewrite(object) {
    const mode = object.data?.letterCase;
    if (!mode || mode === 'none') return;
    object.data = { ...object.data, originalText: object.text };
    object.set({ text: transformCase(object.text, mode) });
  }

  /** Lists ('none'|'bullet'|'number'|'checkbox'|'letter'), the reversible
   * letter-case contract applied to line prefixes: the authored lines ride
   * data.listSource, obj.text renders them prefixed, data.list = {mode} is the
   * recipe, and 'none' restores the source and drops both keys. Numbers and
   * letters are re-derived from the CURRENT lines on every apply, so adding or
   * removing a line renumbers/re-letters (see the editing:exited hook —
   * mid-keystroke re-prefixing would fight the caret). Checkbox (routines) and
   * A. B. C. (choices) are the teaching flavors, concept §3. */
  function setTextList(mode) {
    const wanted = LIST_MODES.includes(mode) ? mode : 'none';
    activeTextObjects().forEach((object) => {
      const data = { ...object.data };
      if (wanted === 'none') {
        if (typeof data.listSource === 'string') object.set({ text: data.listSource });
        delete data.list;
        delete data.listSource;
      } else {
        // First apply (or a mode switch): the displayed text becomes the
        // source, minus any prefix the teacher typed by hand (else Bullets
        // would double it). Strip in the TARGET mode: hand-typed "a) " must
        // fold into the letter list the teacher just asked for.
        const source = typeof data.listSource === 'string'
          ? data.listSource
          : listSourceFrom(object.text, wanted);
        data.listSource = source;
        data.list = { mode: wanted };
        object.set({ text: renderList(source, wanted) });
      }
      object.data = data;
      recaseAfterTextRewrite(object);
      object.initDimensions?.();
      object.setCoords();
      refreshTextGeometryEffects(object); // text metrics changed → re-derive the curve
    });
    canvas.renderAll();
  }

  function getTextList() {
    return selectedObjects[0]?.data?.list?.mode ?? 'none';
  }

  function setLetterCase(mode) {
    activeTextObjects().forEach((object) => {
      const data = { ...object.data };
      if (!mode || mode === 'none') {
        if (typeof data.originalText === 'string') object.set({ text: data.originalText });
        delete data.letterCase;
        delete data.originalText;
      } else {
        if (typeof data.originalText !== 'string') data.originalText = object.text;
        data.letterCase = mode;
        object.set({ text: transformCase(data.originalText, mode) });
      }
      object.data = data;
      object.initDimensions?.();
      object.setCoords();
      refreshTextGeometryEffects(object); // text metrics changed → re-arc
    });
    canvas.renderAll();
  }

  function getLetterCase() {
    return selectedObjects[0]?.data?.letterCase ?? 'none';
  }

  /** "Blend with background" — native composite op, serialized by default
   * toObject. Any object type. */
  function changeBlendMode(op) {
    canvas.getActiveObjects().forEach((object) => {
      if (object.name === 'clip') return;
      object.set({ globalCompositeOperation: op || 'source-over' });
    });
    canvas.renderAll();
  }

  function getActiveBlendMode() {
    return selectedObjects[0]?.globalCompositeOperation ?? 'source-over';
  }

  /** Quick Style preset: clear slots → fill → effects → highlight, batched
   * into exactly ONE history entry. The only self-saving engine method
   * (batch.end() commits) — recipe entries are {id, params} where id is the
   * EFFECT id, not the slot name.
   * The derive/resolve half lives in composeRecipePatch (shared with the
   * style-preview renderer — thumbs and canvas cannot drift); this function
   * keeps the engine's object-mutation semantics: per-object data-bag writes
   * (textFill/textFillLastSolid, textEffects per slot, highlight),
   * refreshTextGeometryEffects after the fill, dirty/setCoords, renderAll. */
  async function applyTextStyle(preset) {
    const recipe = preset?.recipe;
    if (!recipe) return;
    batch.begin();
    try {
      await clearTextEffects();
      let textureFailed = false;
      for (const object of activeTextObjects()) {
        const patch = await composeRecipePatch(recipe, {
          fontSize: object.fontSize,
          ctx: textEffectCtx(object),
          currentFill: object.data?.textFill ?? fillValueFromLive(object.fill),
          lastSolid: object.data?.textFillLastSolid ?? null,
        });

        // Fill half — mirrors setTextFill's per-object body (slots were just
        // cleared, so the paint always lands on `fill`, never hollow's stroke).
        if (patch.fill.failed) {
          // Dead texture: keep the current fill, tell the panel once.
          if (!textureFailed) {
            textureFailed = true;
            window.dispatchEvent(new CustomEvent('cce-texture-load-failed'));
          }
        } else if (patch.fill.applied) {
          const prev = object.data?.textFill;
          // lastSolid memory only captures true solids, only when leaving them
          const prevSolid = typeof prev === 'string' ? prev
            : prev?.type === 'solid' ? prev.color
            : prev == null && typeof object.fill === 'string' && object.fill !== 'transparent' ? object.fill
            : null;
          const data = { ...object.data, textFill: recipe.fill };
          if (prevSolid && !patch.fill.becomingSolid) data.textFillLastSolid = prevSolid;
          object.data = data;
          object.set('fill', patch.fill.paint);
          object.dirty = true;
          // Auto-colored effects (echo inherits, outline contrasts) track the fill
          refreshTextGeometryEffects(object);
        }

        // Effects half — mirrors the applyTextEffect sequence (patch.props
        // accumulates slot resets + derives + hollow's fill/stroke routing).
        if (patch.strokeFillRouted && object.data?.textFill == null) {
          // Hollow routes the fill recipe to the stroke — capture the live
          // fill as the recipe so removing hollow can restore it.
          object.data = { ...object.data, textFill: fillValueFromLive(object.fill) };
        }
        if (patch.effectsTouched) {
          object.set(patch.props);
          object.data = { ...object.data, textEffects: patch.effects };
          // A styled sticker mirrors its fx layer exactly like a hand-applied
          // one. Every shipped style is shape 'outline' + unbeveled → no-op
          // today; a future manifest entry with shape params works unedited.
          const strokeEntry = patch.effects?.stroke;
          const strokeDef = EFFECT_DEFS[strokeEntry?.id];
          if (strokeDef?.shapeFxStep) {
            syncShapeFxStep(object, strokeDef,
              { ...effectDefaults(strokeDef), ...(strokeEntry.params || {}) });
          }
          // Styles can carry sticker (the 273-style manifest does) — same
          // seam contract as applyTextEffect. clearTextEffects above already
          // detached, so this is attach-or-noop.
          syncStickerSeam(object);
          object.dirty = true;
          object.setCoords();
        }

        // Highlight — mirrors setTextHighlight's per-object body.
        object.set({ textBackgroundColor: patch.textBackgroundColor });
        object.data = { ...object.data, highlight: patch.highlight };
      }
      canvas.renderAll();
    } finally {
      batch.end();
    }
  }

  // ---------- shared alignment internals (closure helpers) ----------
  function withReselect(fn) {
    const objs = canvas.getActiveObjects().filter((o) => o.name !== 'clip');
    if (objs.length === 0) return;
    canvas.discardActiveObject();
    fn(objs);
    objs.forEach((o) => o.setCoords());
    if (objs.length === 1) {
      canvas.setActiveObject(objs[0]);
    } else {
      canvas.setActiveObject(new ActiveSelection(objs, { canvas }));
    }
    canvas.requestRenderAll();
    save();
  }

  function alignObjectToBounds(object, edge, bounds) {
    const br = object.getBoundingRect(true);
    switch (edge) {
      case 'left':
        object.set({ left: object.left + (bounds.left - br.left) });
        break;
      case 'centerH':
        object.set({ left: object.left + (bounds.left + bounds.width / 2 - (br.left + br.width / 2)) });
        break;
      case 'right':
        object.set({ left: object.left + (bounds.left + bounds.width - (br.left + br.width)) });
        break;
      case 'top':
        object.set({ top: object.top + (bounds.top - br.top) });
        break;
      case 'middleV':
        object.set({ top: object.top + (bounds.top + bounds.height / 2 - (br.top + br.height / 2)) });
        break;
      case 'bottom':
        object.set({ top: object.top + (bounds.top + bounds.height - (br.top + br.height)) });
        break;
      default:
        break;
    }
  }

  function alignToPage(edge) {
    const workspace = getWorkspace();
    if (!workspace) return;
    const bounds = {
      left: workspace.left,
      top: workspace.top,
      width: workspace.width,
      height: workspace.height,
    };
    withReselect((objects) => {
      objects.forEach((object) => alignObjectToBounds(object, edge, bounds));
    });
  }
};

/**
 * Main hook for the canvas editor
 */
export const useCanvasEditor = ({
  defaultState,
  defaultHeight = DEFAULT_HEIGHT,
  defaultWidth = DEFAULT_WIDTH,
  clearSelectionCallback,
  saveCallback,
} = {}) => {
  const initialState = useRef(defaultState);
  const initialWidth = useRef(defaultWidth);
  const initialHeight = useRef(defaultHeight);

  const [canvas, setCanvas] = useState(null);
  const [container, setContainer] = useState(null);
  const [selectedObjects, setSelectedObjects] = useState([]);

  const [fontFamily, setFontFamily] = useState(FONT_FAMILY);
  const [fillColor, setFillColor] = useState(FILL_COLOR);
  const [strokeColor, setStrokeColor] = useState(STROKE_COLOR);
  const [strokeWidth, setStrokeWidth] = useState(STROKE_WIDTH);
  const [strokeDashArray, setStrokeDashArray] = useState(STROKE_DASH_ARRAY);

  // Navigation state: once the user pans/zooms manually, auto-refits
  // (window resize) must stop stomping their viewport. Fit/load resets it.
  const manualViewportRef = useRef(false);
  const spaceDownRef = useRef(false);
  const panStateRef = useRef(null);
  const guidesRef = useRef(null);
  const lastPasteEventRef = useRef(0);

  // History
  const canvasHistory = useRef([]);
  const [historyIndex, setHistoryIndex] = useState(0);
  // Mirror of historyIndex for use inside save() without making its identity
  // churn on every history entry.
  const historyIndexRef = useRef(0);
  // LAST-SAVED MARKER (app-bar lane B0, 2026-07-21): dirty = history position
  // vs this marker. -1 means "the saved state is no longer reachable in the
  // stack" (its entry was trimmed away, replaced in place, or discarded with
  // a redo tail) — permanently dirty until the next successful library save
  // calls markSaved(). State + ref pair for the same reason as historyIndex:
  // the ref is save()'s synchronous truth, the state drives the facade
  // rebuild (Save button dot, leave guard). Init/loads reset it to 0 (a
  // freshly loaded design is clean); loadJson() is NOT a reset — it pushes a
  // normal entry (template apply / draft restore = unsaved work).
  const [lastSavedIndex, setLastSavedIndex] = useState(0);
  const lastSavedIndexRef = useRef(0);
  const skipSave = useRef(false);
  // HISTORY BUDGETS (global-undo lane, 2026-07-21). Entries are full-canvas
  // JSON strings, so cost = characters (V8 stores base64/ASCII at 1 byte/char).
  // The honest math: a text-only design snapshots at ~10-200 KB, but embedded
  // rasters ride EVERY entry — addImageCapped photos are ≤3× workspace width
  // JPEG-0.9 data URLs (a 24in poster ⇒ ≤5184px ⇒ ~2-6 MB each) and baked
  // text3d/emboss stand at ≤4096px (BAKE_STAND_CAPS ⇒ ~1-4 MB each). Each
  // distinct entry is a FRESH JSON.stringify — engines do NOT share substrings
  // between separate stringify results, so "interning dedupes the data URLs"
  // is false: a 3-photo poster costs ~10-15 MB PER ENTRY. 50 such entries
  // ≈ 500-750 MB — an OOM on the school-PC target. Hence TWO caps:
  //   MAX_HISTORY       — 50 entries, oldest-drop (the pre-existing count cap);
  //   MAX_HISTORY_CHARS — ~192 MB of characters, oldest-drop, but never below
  //                       MIN_HISTORY_KEEP entries (undo must survive even
  //                       when single entries are enormous).
  // Undo depth is therefore honest, not fixed: ~50 steps on text posters,
  // proportionally fewer on raster-heavy ones (e.g. ~13 at 15 MB/entry).
  const MAX_HISTORY = 50;
  const MAX_HISTORY_CHARS = 192 * 1024 * 1024;
  const MIN_HISTORY_KEEP = 2;

  const canUndo = useCallback(() => historyIndex > 0, [historyIndex]);
  const canRedo = useCallback(() => historyIndex < canvasHistory.current.length - 1, [historyIndex]);

  // save(mode):
  //   save()          — the normal commit: push one history entry (deduped)
  //                     and run the autosave callback;
  //   save(true)      — autosave only, no history entry;
  //   save('replace') — FIDELITY-CACHE writes (re-bake-on-scale): overwrite
  //                     the CURRENT entry in place instead of pushing. A
  //                     standing-bake upgrade is not a user action — it must
  //                     not cost an undo press — but leaving the top entry
  //                     stale would turn the next redundant commit() into a
  //                     phantom entry (dedupe compares against the stored
  //                     top). Replacing keeps dedupe truthful and the
  //                     autosave sharp; undoing past the entry simply
  //                     restores the softer standing bake (the design is
  //                     identical — re-scaling re-upgrades).
  const save = useCallback((mode = false) => {
    if (!canvas) return;
    // Muted during batched operations (loads, undo/redo restore, multi-object
    // add/remove): bail BEFORE serializing — each muted per-object event would
    // otherwise still stringify the entire canvas (the O(N²) template-load
    // cost). batch.end() / the restore callback runs the one real save.
    if (skipSave.current) return;
    const currentState = JSON.stringify(canvas.toObject(JSON_KEYS));

    if (mode === 'replace') {
      if (currentState !== canvasHistory.current[historyIndexRef.current]) {
        canvasHistory.current[historyIndexRef.current] = currentState;
        // The replaced entry no longer equals what went to the library — if
        // it WAS the saved one, the marker must not false-clean (a bake
        // upgrade lands right after the scale gesture's own push, so in
        // practice the marker already sits below this index).
        if (lastSavedIndexRef.current === historyIndexRef.current) {
          lastSavedIndexRef.current = -1;
          setLastSavedIndex(-1);
        }
      }
    } else if (!mode) {
      // Dedupe: identical state means nothing user-visible changed — skip
      // the push AND the autosave callback. This is what makes commit()
      // safe to call redundantly after event-driven saves, and absorbs the
      // legacy double-save paths (deleteLayer/duplicateLayer call save()
      // while the object:removed/added event already did).
      if (currentState === canvasHistory.current[historyIndexRef.current]) {
        return;
      }
      // A new action after undo discards the orphaned redo tail. Without
      // this, redo could resurrect future states the user had abandoned —
      // and worse, undo would walk back THROUGH them.
      canvasHistory.current = canvasHistory.current.slice(0, historyIndexRef.current + 1);
      // Marker in the discarded tail (saved, undone below the save, edited
      // again) → the saved state just became unreachable. A raw index compare
      // would FALSE-CLEAN the moment a new entry lands on the same index.
      if (lastSavedIndexRef.current > historyIndexRef.current) {
        lastSavedIndexRef.current = -1;
        setLastSavedIndex(-1);
      }
      canvasHistory.current.push(currentState);
      const lenBeforeTrims = canvasHistory.current.length;
      // Cap the stack: every entry is a full-canvas JSON snapshot (including
      // base64 images), so an unbounded array OOMs on photo-heavy posters.
      if (canvasHistory.current.length > MAX_HISTORY) {
        canvasHistory.current = canvasHistory.current.slice(-MAX_HISTORY);
      }
      // Character budget (see HISTORY BUDGETS above): drop oldest entries
      // until the stack fits, but always keep MIN_HISTORY_KEEP so at least
      // one undo step survives raster-heavy designs.
      let totalChars = 0;
      let keepFrom = canvasHistory.current.length;
      while (keepFrom > 0) {
        const nextTotal = totalChars + canvasHistory.current[keepFrom - 1].length;
        // Stop once the budget is blown AND we already keep the minimum.
        if (nextTotal > MAX_HISTORY_CHARS
          && canvasHistory.current.length - keepFrom >= MIN_HISTORY_KEEP) break;
        totalChars = nextTotal;
        keepFrom -= 1;
      }
      if (keepFrom > 0) {
        canvasHistory.current = canvasHistory.current.slice(keepFrom);
      }
      // Both caps drop from the FRONT — shift the saved marker with them;
      // if the saved entry itself was dropped, it's unreachable (-1, dirty
      // until the next save) rather than pointing at someone else's entry.
      const droppedFromFront = lenBeforeTrims - canvasHistory.current.length;
      if (droppedFromFront > 0 && lastSavedIndexRef.current >= 0) {
        const shifted = lastSavedIndexRef.current - droppedFromFront;
        lastSavedIndexRef.current = shifted >= 0 ? shifted : -1;
        setLastSavedIndex(lastSavedIndexRef.current);
      }
      historyIndexRef.current = canvasHistory.current.length - 1;
      setHistoryIndex(historyIndexRef.current);
    }

    if (saveCallback) {
      const workspace = canvas.getObjects().find((obj) => obj.name === "clip");
      saveCallback({
        json: currentState,
        width: workspace?.width || defaultWidth,
        height: workspace?.height || defaultHeight,
      });
    }
  }, [canvas, saveCallback, defaultWidth, defaultHeight]);

  // Batch = coalesce a burst of object:added/removed events into ONE history
  // entry: begin() mutes the event-driven saves, end() unmutes and commits
  // the final state. Used by load paths, multi-object paste/duplicate/delete,
  // group/ungroup, icon recolor (remove+add), smart-object regenerate and the
  // baked-recipe insert/regenerate editors.
  //
  // FAILURE POSTURE (verified 2026-07-21): end() unmutes BEFORE save(), so a
  // throwing save can never wedge the mute; every begin()…end() body is
  // try/finally-ed (or generates its risky work BEFORE begin()), and the
  // async load paths .catch → abort(). NOT re-entrant by design — the flag is
  // a boolean, not a counter; no current caller nests (applyTextStyle is the
  // only engine self-batcher and composes raw internals, never other
  // batchers).
  //
  // THE HISTORY BOUNDARY (what is deliberately NOT an undo entry):
  //   · viewport — zoom/pan/fit (zoomIn/Out, setZoomPercent, autoZoom,
  //     clampViewport, space-drag, wheel);
  //   · selection — select/discard/scope-chip widening (scope.set);
  //   · panel/flyout/accordion/drawer UI state, modal open/close;
  //   · live-fx pixel caches — seam stashes, viewport patches (fxRender),
  //     drag-preview tilts (joystick3d restores the transform on release);
  //   · machine-local prefs — snapping toggle (localStorage), recents/user
  //     swatches/gradients/textures (PaintPicker rings), density, brush
  //     tool settings (width/color/type previews until a stroke lands);
  //   · standing-bake fidelity upgrades (maybeUpgradeBakedImage) — these
  //     REPLACE the current entry (save('replace')) instead of pushing;
  //   · transient overlays — the table-cell edit Textbox (begin/abort),
  //     lock badges, aligning guides.
  // Everything that changes the DOCUMENT (objects, their props/data bags,
  // workspace size/background/posterLight, z-order, lock/visibility) lands
  // exactly one entry per user action.
  const batch = useMemo(() => ({
    begin: () => { skipSave.current = true; },
    end: () => { skipSave.current = false; save(); },
    // Recovery for failed loads: unstick history WITHOUT snapshotting the
    // partially-cleared canvas (v6 loadFromJSON rejects on any dead resource).
    abort: () => { skipSave.current = false; },
  }), [save]);

  const undo = useCallback(() => {
    if (!canUndo() || !canvas) return;

    let previousState;
    try {
      previousState = JSON.parse(canvasHistory.current[historyIndex - 1]);
    } catch (err) {
      console.error('Corrupt undo state, skipping:', err);
      return;
    }

    // No explicit clear(): loadFromJSON clears internally, and the extra
    // clear+renderAll painted a white flash on every undo.
    skipSave.current = true;

    const previousIndex = historyIndex - 1;
    // v6: completion in .then(), NOT the second arg (that's the per-object
    // reviver — skipSave would unmute after the FIRST object and history
    // would flood with partial states).
    canvas.loadFromJSON(previousState).then(() => {
      // History restore re-enlivens every object — instance-level live-fx
      // patches are gone; put them back before the first paint.
      reattachFxDeep(canvas.getObjects());
      // Curve self-heal (load-path parity, global-undo lane 2026-07-21): a
      // snapshot taken while a curved text was inline-editing serialized
      // path:null (editing runs straight — see handleTextEditingEntered).
      // curveAmount/curveShape are in JSON_KEYS, so re-derive exactly as
      // loadJson does or the arc stays flat until the next touch.
      rehydrateCurvedText(canvas);
      // Poster background: snapshots carry the FLAT degrade fill by law (the
      // serialize seam), so unlike object texture fills — whose serialized
      // Pattern re-enlivens natively here — the workspace texture MUST
      // re-resolve from data.bgRecipe on every restore. The async land only
      // repaints; it never saves (skipSave timing is irrelevant to it).
      rehydrateWorkspaceBackground(canvas);
      canvas.renderAll();
      historyIndexRef.current = previousIndex;
      setHistoryIndex(previousIndex);
      skipSave.current = false;
    }).catch((err) => {
      console.error('Undo restore failed:', err);
      skipSave.current = false;
      canvas.renderOnAddRemove = true;
    });
  }, [canUndo, canvas, historyIndex]);

  const redo = useCallback(() => {
    if (!canRedo() || !canvas) return;

    let nextState;
    try {
      nextState = JSON.parse(canvasHistory.current[historyIndex + 1]);
    } catch (err) {
      console.error('Corrupt redo state, skipping:', err);
      return;
    }

    skipSave.current = true;

    const nextIndex = historyIndex + 1;
    canvas.loadFromJSON(nextState).then(() => {
      reattachFxDeep(canvas.getObjects()); // same contract as undo
      rehydrateCurvedText(canvas); // curve self-heal — same contract as undo
      rehydrateWorkspaceBackground(canvas); // poster bg re-resolve — same contract as undo
      canvas.renderAll();
      historyIndexRef.current = nextIndex;
      setHistoryIndex(nextIndex);
      skipSave.current = false;
    }).catch((err) => {
      console.error('Redo restore failed:', err);
      skipSave.current = false;
      canvas.renderOnAddRemove = true;
    });
  }, [canRedo, canvas, historyIndex]);

  // Clipboard
  const clipboardRef = useRef(null);
  // Object awaiting a single history entry after a run of arrow-key nudges
  const nudgePendingRef = useRef(null);

  const copy = useCallback(() => {
    if (!canvas) return;
    // clone(JSON_KEYS): keep the data bag (smart-object + fx recipes) on the
    // clipboard — bare clone() drops every custom key.
    canvas.getActiveObject()?.clone(JSON_KEYS).then((cloned) => {
      clipboardRef.current = cloned;
    }).catch(() => {});
  }, [canvas]);

  const paste = useCallback(() => {
    if (!canvas || !clipboardRef.current) return;
    clipboardRef.current.clone(JSON_KEYS).then((cloned) => {
      // One history entry per paste, even for multi-object selections
      // (the add-loop below fires object:added per object).
      batch.begin();
      try {
        canvas.discardActiveObject();
        cloned.set({ left: cloned.left + 10, top: cloned.top + 10, evented: true });

        if (cloned instanceof ActiveSelection) {
          cloned.canvas = canvas;
          cloned.forEachObject((obj) => canvas.add(obj));
          cloned.setCoords();
        } else {
          canvas.add(cloned);
        }

        clipboardRef.current.top += 10;
        clipboardRef.current.left += 10;
        reattachFxDeep([cloned]); // fresh instances — re-install live fx
        canvas.setActiveObject(cloned);
        canvas.requestRenderAll();
      } finally {
        batch.end(); // never leave saves muted (failure posture)
      }
    }).catch(() => {});
  }, [canvas, batch]);

  // Auto zoom using util.findScaleToFit
  const autoZoom = useCallback(() => {
    if (!canvas || !container) return;

    const width = container.offsetWidth;
    const height = container.offsetHeight;

    canvas.setWidth(width);
    canvas.setHeight(height);

    const center = canvas.getCenter();
    const zoomRatio = 0.85;

    const localWorkspace = canvas.getObjects().find((object) => object.name === "clip");
    if (!localWorkspace) return;

    // Use util.findScaleToFit for proper scaling
    const scale = util.findScaleToFit(localWorkspace, { width, height });
    const zoom = zoomRatio * scale;

    canvas.setViewportTransform(iMatrix.concat());
    canvas.zoomToPoint(new Point(center.left, center.top), zoom);

    const workspaceCenter = localWorkspace.getCenterPoint();
    const viewportTransform = canvas.viewportTransform;

    if (canvas.width === undefined || canvas.height === undefined || !viewportTransform) {
      return;
    }

    viewportTransform[4] = canvas.width / 2 - workspaceCenter.x * viewportTransform[0];
    viewportTransform[5] = canvas.height / 2 - workspaceCenter.y * viewportTransform[3];
    canvas.setViewportTransform(viewportTransform);

    localWorkspace.clone().then((cloned) => {
      canvas.clipPath = cloned;
      canvas.requestRenderAll();
    });
  }, [canvas, container]);

  // Fit is the "not manual" state: loads, changeSize, the Fit button and
  // window resizes route through this so the manual flag clears.
  const autoZoomFit = useCallback(() => {
    manualViewportRef.current = false;
    autoZoom();
  }, [autoZoom]);

  // Keep at least a quarter of the workspace on screen after any pan/zoom —
  // without this, aggressive scrolling strands the user on empty void.
  const clampViewport = useCallback(() => {
    if (!canvas) return;
    const ws = canvas.getObjects().find((o) => o.name === 'clip');
    const vpt = canvas.viewportTransform;
    if (!ws || !vpt) return;
    const zoom = canvas.getZoom();
    const br = ws.getBoundingRect(true, true);
    const wsLeft = br.left * zoom + vpt[4];
    const wsTop = br.top * zoom + vpt[5];
    const wsW = br.width * zoom;
    const wsH = br.height * zoom;
    const cw = canvas.width || 0;
    const ch = canvas.height || 0;

    const clampAxis = (pos, size, viewSize) => {
      const max = viewSize - size * 0.25;
      const min = -size * 0.75;
      return Math.min(Math.max(pos, min), max);
    };
    const newLeft = clampAxis(wsLeft, wsW, cw);
    const newTop = clampAxis(wsTop, wsH, ch);
    if (newLeft !== wsLeft || newTop !== wsTop) {
      vpt[4] += newLeft - wsLeft;
      vpt[5] += newTop - wsTop;
      canvas.setViewportTransform(vpt);
    }
  }, [canvas]);

  // Canvas events
  useEffect(() => {
    if (!canvas) return;

    const handleObjectAdded = () => save();
    const handleObjectRemoved = () => save();
    const handleObjectModified = (e) => {
      // Normalize text scaling into a real fontSize so corner-drag resizing
      // updates the size number instead of leaving a hidden scale factor
      // (this is what made text feel "hard to enlarge" — the size input
      // stayed at the old value no matter how far you dragged).
      const t = e?.target;
      // Smart objects with intrinsic width/height params: a bounding-box
      // resize IS the educator's size input — keep the params in sync with
      // the displayed size (regeneration bakes them; the panel mirrors them
      // live via the cce-smart-size-synced event).
      const soData = t && smartObjectData(t);
      if (soData) {
        const recipeDef = getRecipe(soData.tool);
        const hasW = recipeDef?.schema?.some((f) => f.key === 'width');
        const hasH = recipeDef?.schema?.some((f) => f.key === 'height');
        if (hasW || hasH) {
          const params = { ...soData.params };
          if (hasW) params.width = Math.round(t.getScaledWidth());
          if (hasH) params.height = Math.round(t.getScaledHeight());
          t.data = { ...soData, params };
          window.dispatchEvent(new CustomEvent('cce-smart-size-synced', {
            detail: { width: params.width, height: params.height },
          }));
        }
      }
      if (t && isTextType(t.type) && (t.scaleX !== 1 || t.scaleY !== 1)) {
        const newSize = Math.max(4, Math.round(t.fontSize * t.scaleY * 10) / 10);
        t.set({
          fontSize: newSize,
          width: Math.max(20, t.width * t.scaleX),
          scaleX: 1,
          scaleY: 1,
        });
        t.setCoords();
      }
      // Curve path + effect geometry re-derive from live metrics — AFTER the
      // scale→fontSize normalization above (a stale fontSize would double-
      // scale every effect), BEFORE save() so the snapshot carries it.
      if (t && isTextType(t.type)) refreshTextGeometryEffects(t);
      // Baked recipe images: interaction-end re-bake when the displayed
      // device px outgrew the standing bake (fire-and-forget; commits its
      // own snapshot when it lands).
      maybeUpgradeBakedImage(t, canvas, save);
      save();
    };
    // Locked objects stay individually selectable (that's how you reach
    // Unlock) but must not ride along in a multi-select drag: prune them from
    // mixed marquee selections; if EVERY member is locked, freeze the wrapper.
    const pruneLockedMultiSelect = () => {
      const active = canvas.getActiveObject();
      if (!(active instanceof ActiveSelection)) return;
      const members = active.getObjects();
      const lockedMembers = members.filter((o) => o.locked === true);
      if (lockedMembers.length === 0) return;
      if (lockedMembers.length < members.length) {
        // v6: removeWithUpdate() was removed with the Group rewrite —
        // remove() detaches from the selection (object stays on canvas)
        // and the layout manager recomputes the wrapper bounds itself.
        active.remove(...lockedMembers);
        canvas.requestRenderAll();
      } else {
        active.set({
          lockMovementX: true, lockMovementY: true,
          lockScalingX: true, lockScalingY: true,
          lockRotation: true, hasControls: false,
        });
      }
    };
    const handleSelectionCreated = () => {
      pruneLockedMultiSelect();
      // 3D joystick: (re)attach on the freshly selected object — covers
      // objects the walkers never saw (a just-inserted text3d bake) and
      // costs nothing when the pad is already there.
      syncJoystick3dSelection(canvas);
      setSelectedObjects(canvas.getActiveObjects() || []);
    };
    const handleSelectionUpdated = () => {
      pruneLockedMultiSelect();
      syncJoystick3dSelection(canvas);
      setSelectedObjects(canvas.getActiveObjects() || []);
    };
    const handleSelectionCleared = () => {
      setSelectedObjects([]);
      clearSelectionCallback?.();
    };

    // Ctrl/Cmd+wheel zooms toward the cursor; plain wheel scrolls (Shift for
    // horizontal — the trackpad pinch arrives as ctrl+wheel in Chromium).
    const handleMouseWheel = (opt) => {
      const e = opt.e;
      if (e.ctrlKey || e.metaKey) {
        let zoom = canvas.getZoom();
        zoom *= 0.999 ** e.deltaY;
        zoom = Math.min(Math.max(zoom, 0.1), 3);
        // v6 payload carries the viewport-space point (replaces the old
        // opt.pointer / manual offsetX math); zoomToPoint wants exactly that.
        canvas.zoomToPoint(opt.viewportPoint ?? new Point(e.offsetX, e.offsetY), zoom);
      } else {
        const dx = e.shiftKey ? -e.deltaY : -(e.deltaX || 0);
        const dy = e.shiftKey ? 0 : -e.deltaY;
        canvas.relativePan(new Point(dx, dy));
      }
      manualViewportRef.current = true;
      clampViewport();
      e.preventDefault();
      e.stopPropagation();
    };

    // Space-drag / middle-mouse pan. Skipped in drawing mode (fabric still
    // starts a brush stroke on mouse:down there — pan would paint).
    const handlePanMouseDown = (opt) => {
      const evt = opt.e;
      if (canvas.isDrawingMode) return;
      if (!(spaceDownRef.current || evt.button === 1)) return;
      panStateRef.current = { active: true, x: evt.clientX, y: evt.clientY };
      canvas.defaultCursor = 'grabbing';
      canvas.setCursor('grabbing');
      evt.preventDefault();
    };
    const handlePanMouseMove = (opt) => {
      const pan = panStateRef.current;
      if (!pan?.active) return;
      const evt = opt.e;
      canvas.relativePan(new Point(evt.clientX - pan.x, evt.clientY - pan.y));
      pan.x = evt.clientX;
      pan.y = evt.clientY;
      manualViewportRef.current = true;
      clampViewport();
    };
    const handlePanMouseUp = () => {
      if (panStateRef.current?.active) {
        panStateRef.current.active = false;
        canvas.defaultCursor = spaceDownRef.current ? 'grab' : 'default';
      }
    };

    // Live on-canvas cell editing: double-click a text cell of a smart table
    // — or a param-tagged text of a micro-object (fraction numerator etc.;
    // kind:'param' tags, stamped by posterTools/assemble.ts) —
    // to edit it in place. GUARD RAILS: only fires for smart-object groups
    // whose hit subTargets include a tableCell-tagged text child — plain
    // i-text/Textbox objects never reach startTableCellEdit, so their native
    // fabric dblclick word-select/edit behavior is completely untouched
    // (fabric 6.9.1 populates opt.subTargets from canvas.targets when the
    // group has subTargetCheck — set in prepareSmartObject).
    const handleDoubleClick = (opt) => {
      const group = opt.target;
      if (!group || !smartObjectData(group) || group.locked === true) return;
      // Baked recipe IMAGES (text3d) carry {tool, params} in data too, but
      // they are not groups: nothing to cell-edit, and group.getObjects()
      // would throw. Their dblclick surface is the 3D edit modal
      // (StudioCoreEditor seam).
      if (typeof group.getObjects !== 'function') return;
      const subs = opt.subTargets || [];
      let child =
        subs.find((o) => isTextType(o.type) && o?.data?.tableCell && o.visible !== false)
        || subs.find((o) => o?.data?.tableCell && !isTextType(o.type)); // empty fill-in cell (tagged rect)
      if (!child) {
        // Geometric fallback: decorative overlays (frames, backdrops) can win
        // fabric's topmost-only subtarget search — find the tagged child under
        // the pointer ourselves. Prefer text over rect at the same spot.
        const scene = canvas.getScenePoint(opt.e);
        const hits = [];
        const walk = (objs) => objs.forEach((o) => {
          if (typeof o.getObjects === 'function') walk(o.getObjects());
          if (o?.data?.tableCell && o.visible !== false && o.containsPoint?.(scene)) hits.push(o);
        });
        walk(group.getObjects());
        child = hits.find((o) => isTextType(o.type)) || hits[0] || null;
      }
      if (!child) return;
      if (!child) return;
      startTableCellEdit(canvas, batch, group, child);
    };

    // Curved/styled text edits in fabric v6: caret-on-path is flaky, so
    // editing always runs STRAIGHT — entering stashes the arc away, exiting
    // re-derives it from curveAmount + the new calcTextWidth (the stale-arc
    // fix). Exiting also re-cases typed edits while a letter case is active
    // (data.originalText becomes the as-displayed edit; "As typed" then
    // returns the post-edit text — documented behavior).
    const handleTextEditingEntered = (e) => {
      const t = e?.target;
      if (!t || !isTextType(t.type)) return;
      // Snapshot the displayed text for EVERY text (not just curved): the
      // exit handler must know whether the session actually changed anything
      // before it touches data.originalText.
      t.__preEditText = t.text;
      if (!t.path) return;
      t.__stashedPath = t.path;
      t.set({ path: null, dirty: true });
      t.initDimensions?.();
      t.setCoords();
      canvas.requestRenderAll();
    };
    const handleTextEditingExited = (e) => {
      const t = e?.target;
      if (!t || !isTextType(t.type)) return;
      delete t.__stashedPath;
      // Lists re-derive on EXIT (re-prefixing per keystroke would fight the
      // caret): the edited text becomes the new authored source with its
      // prefixes stripped, and re-rendering renumbers for the new line count.
      // Runs BEFORE the case block, which then re-anchors on the rendered
      // string exactly as recaseAfterTextRewrite does.
      const listMode = t.data?.list?.mode;
      if (listMode && listMode !== 'none' && t.text !== t.__preEditText) {
        const source = listSourceFrom(t.text, listMode);
        t.data = { ...t.data, listSource: source };
        t.set({ text: renderList(source, listMode) });
        t.initDimensions?.();
      }
      const mode = t.data?.letterCase;
      // Re-case ONLY when the session changed the text — an untouched edit
      // (dblclick in, Esc out) must not overwrite the as-typed original with
      // the displayed (already-cased) string, or "As typed" is destroyed.
      if (mode && mode !== 'none' && t.text !== t.__preEditText) {
        t.data = { ...t.data, originalText: t.text };
        t.set({ text: transformCase(t.text, mode) });
        t.initDimensions?.();
      }
      delete t.__preEditText;
      // Colour-coding re-derives LAST: lists and letter case both rewrite
      // t.text above, and phonics must colour the string as finally rendered.
      if (t.data?.phonics?.mode) repaintPhonics(t);
      refreshTextGeometryEffects(t);
      canvas.requestRenderAll();
    };
    const handleTextChanged = (e) => {
      const t = e?.target;
      if (!t || !isTextType(t.type)) return;
      // Re-colour per keystroke so a retyped word is phonics-correct as it is
      // typed. Safe to run mid-edit: the ranges are re-derived from the whole
      // current string, so the caret and the live selection are untouched
      // (setSelectionStyles takes EXPLICIT indices and never reads them).
      if (t.data?.phonics?.mode) repaintPhonics(t);
      // Mid-edit the curve part is skipped (isEditing guard in the helper)
      refreshTextGeometryEffects(t);
      canvas.requestRenderAll();
    };

    canvas.on("object:added", handleObjectAdded);
    canvas.on("object:removed", handleObjectRemoved);
    canvas.on("object:modified", handleObjectModified);
    canvas.on("selection:created", handleSelectionCreated);
    canvas.on("selection:updated", handleSelectionUpdated);
    canvas.on("selection:cleared", handleSelectionCleared);
    canvas.on("mouse:wheel", handleMouseWheel);
    canvas.on("mouse:down", handlePanMouseDown);
    canvas.on("mouse:move", handlePanMouseMove);
    canvas.on("mouse:up", handlePanMouseUp);
    canvas.on("mouse:dblclick", handleDoubleClick);
    canvas.on("text:editing:entered", handleTextEditingEntered);
    canvas.on("text:editing:exited", handleTextEditingExited);
    canvas.on("text:changed", handleTextChanged);

    return () => {
      canvas.off("object:added", handleObjectAdded);
      canvas.off("object:removed", handleObjectRemoved);
      canvas.off("object:modified", handleObjectModified);
      canvas.off("selection:created", handleSelectionCreated);
      canvas.off("selection:updated", handleSelectionUpdated);
      canvas.off("selection:cleared", handleSelectionCleared);
      canvas.off("mouse:wheel", handleMouseWheel);
      canvas.off("mouse:down", handlePanMouseDown);
      canvas.off("mouse:move", handlePanMouseMove);
      canvas.off("mouse:up", handlePanMouseUp);
      canvas.off("mouse:dblclick", handleDoubleClick);
      canvas.off("text:editing:entered", handleTextEditingEntered);
      canvas.off("text:editing:exited", handleTextEditingExited);
      canvas.off("text:changed", handleTextChanged);
    };
  }, [canvas, save, batch, clearSelectionCallback, clampViewport]);

  // Viewport-patch hookup (2026-07-18): the fx seam refreshes full-resolution
  // viewport patches ~250ms after the view settles. Every zoom/pan path in
  // this hook (wheel, space-drag, zoom buttons, Fit, clampViewport, probes'
  // setViewportTransform) funnels through a canvas render — comparing the
  // viewportTransform on after:render catches them ALL with six number
  // compares per frame, and a patch landing re-renders with an UNCHANGED
  // transform, so this can never self-loop. object:modified rides along:
  // moving/scaling an fx object changes its viewport coverage without any
  // viewport change. All debouncing/eligibility lives in fxRender.
  useEffect(() => {
    if (!canvas) return undefined;
    let lastVpt = '';
    const onAfterRender = () => {
      const v = canvas.viewportTransform;
      if (!v) return;
      const key = `${v[0]},${v[1]},${v[2]},${v[3]},${Math.round(v[4])},${Math.round(v[5])}`;
      if (key === lastVpt) return;
      lastVpt = key;
      fxNotifyViewportChange(canvas);
    };
    const onObjectModified = () => fxNotifyViewportChange(canvas);
    canvas.on('after:render', onAfterRender);
    canvas.on('object:modified', onObjectModified);
    return () => {
      canvas.off('after:render', onAfterRender);
      canvas.off('object:modified', onObjectModified);
    };
  }, [canvas]);

  // ResizeObserver for auto zoom
  useEffect(() => {
    if (!canvas || !container) return;

    const resizeObserver = new ResizeObserver(() => {
      if (!manualViewportRef.current) {
        autoZoom();
        return;
      }
      // Manual viewport: track the container size without re-fitting
      canvas.setWidth(container.offsetWidth);
      canvas.setHeight(container.offsetHeight);
      clampViewport();
      canvas.requestRenderAll();
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [canvas, container, autoZoom, clampViewport]);

  // Hotkeys
  useEffect(() => {
    if (!canvas) return;

    const handleKeyDown = (e) => {
      const ae = document.activeElement;
      const isInput = ["INPUT", "TEXTAREA"].includes(ae?.tagName) || ae?.isContentEditable;
      if (isInput) return;
      // Don't act while a canvas text is in inline-edit mode
      if (canvas.getActiveObject()?.isEditing) return;
      // Don't run editor hotkeys while the crop modal owns the keyboard —
      // otherwise e.g. Delete would remove the very image being cropped.
      if (document.querySelector(".cce-crop-modal-overlay")) return;

      const mod = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();

      if (mod && key === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
      if ((mod && key === "y") || (mod && e.shiftKey && key === "z")) {
        e.preventDefault();
        redo();
      }
      if (mod && key === "c") {
        e.preventDefault();
        copy();
      }
      if (mod && key === "v") {
        // No preventDefault: the native 'paste' event must fire (it carries
        // OS-clipboard images/text — see the window paste handler). Fallback
        // for platforms where no paste event arrives (the packaged app strips
        // the menu, so delivery isn't guaranteed off-Windows): if none lands
        // shortly, run the internal-clipboard paste.
        setTimeout(() => {
          if (Date.now() - lastPasteEventRef.current > 150) paste();
        }, 80);
      }
      // Zoom hotkeys
      if (mod && (key === "=" || key === "+")) {
        e.preventDefault();
        const center = canvas.getCenter();
        const z = Math.min(canvas.getZoom() + 0.1, 3);
        canvas.zoomToPoint(new Point(center.left, center.top), z);
        manualViewportRef.current = true;
        clampViewport();
      }
      if (mod && key === "-") {
        e.preventDefault();
        const center = canvas.getCenter();
        const z = Math.max(canvas.getZoom() - 0.1, 0.1);
        canvas.zoomToPoint(new Point(center.left, center.top), z);
        manualViewportRef.current = true;
        clampViewport();
      }
      if (mod && key === "0") {
        e.preventDefault();
        autoZoomFit();
      }
      // Hold Space to pan (grab cursor; targets ignored while held)
      if (e.code === "Space" && !e.repeat && !mod) {
        spaceDownRef.current = true;
        canvas.defaultCursor = "grab";
        canvas.skipTargetFind = true;
        canvas.selection = false;
        canvas.setCursor("grab");
        e.preventDefault();
      }
      if (mod && key === "d") {
        e.preventDefault();
        duplicateActiveObject(canvas, batch);
      }
      if (mod && key === "a") {
        e.preventDefault();
        const objects = canvas.getObjects().filter((o) => o.name !== "clip" && o.selectable !== false);
        if (objects.length) {
          canvas.discardActiveObject();
          const selection = new ActiveSelection(objects, { canvas });
          canvas.setActiveObject(selection);
          canvas.requestRenderAll();
        }
      }
      if (e.key === "Escape") {
        canvas.discardActiveObject();
        canvas.requestRenderAll();
      }
      if (e.key.startsWith("Arrow")) {
        const active = canvas.getActiveObject();
        if (active) {
          e.preventDefault();
          const step = e.shiftKey ? 10 : 1;
          if (e.key === "ArrowUp") active.top -= step;
          if (e.key === "ArrowDown") active.top += step;
          if (e.key === "ArrowLeft") active.left -= step;
          if (e.key === "ArrowRight") active.left += step;
          active.setCoords();
          canvas.requestRenderAll();
          // Coalesce held-key nudges into one history entry on keyup
          nudgePendingRef.current = active;
        }
      }
      if (e.key === "Delete" || e.key === "Backspace") {
        // Batched like editor.delete(): a multi-selection delete must land
        // ONE history entry, not one per member (global-undo audit).
        batch.begin();
        try {
          canvas.getActiveObjects().forEach((obj) => canvas.remove(obj));
          canvas.discardActiveObject();
          canvas.renderAll();
        } finally {
          batch.end();
        }
      }
    };

    const handleKeyUp = (e) => {
      if (e.key.startsWith("Arrow") && nudgePendingRef.current) {
        canvas.fire("object:modified", { target: nudgePendingRef.current });
        nudgePendingRef.current = null;
      }
      if (e.code === "Space") {
        spaceDownRef.current = false;
        if (panStateRef.current?.active) panStateRef.current.active = false;
        canvas.skipTargetFind = false;
        canvas.selection = true;
        canvas.defaultCursor = "default";
        canvas.setCursor("default");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [canvas, undo, redo, copy, paste, batch, clampViewport, autoZoomFit]);

  // Build editor
  const editor = useMemo(() => {
    if (!canvas) return undefined;

    const base = buildEditor({
      save,
      batch,
      undo,
      redo,
      canUndo,
      canRedo,
      // Fit-style autoZoom: loads / changeSize / the Fit button clear the
      // manual-viewport flag so window resizes re-fit again.
      autoZoom: autoZoomFit,
      copy,
      paste,
      canvas,
      fillColor,
      strokeWidth,
      strokeColor,
      setFillColor,
      setStrokeColor,
      setStrokeWidth,
      strokeDashArray,
      selectedObjects,
      setStrokeDashArray,
      fontFamily,
      setFontFamily,
    });

    // Navigation + snapping API (needs hook-level refs, so merged here)
    const setZoom = (zoomValue) => {
      const center = canvas.getCenter();
      const z = Math.min(Math.max(zoomValue, 0.1), 3);
      canvas.zoomToPoint(new Point(center.left, center.top), z);
      manualViewportRef.current = true;
      clampViewport();
      canvas.requestRenderAll();
    };

    return {
      ...base,
      zoomIn: () => setZoom(canvas.getZoom() + 0.1),
      zoomOut: () => setZoom(canvas.getZoom() - 0.1),
      setZoomPercent: (pct) => setZoom(pct / 100),
      getZoomPercent: () => Math.round(canvas.getZoom() * 100),
      zoomToFit: () => autoZoomFit(),
      // Viewport-chrome pan (RS lane: scrollbars/rulers). EXACTLY the
      // space-drag recipe: relativePan (v6 setViewportTransform renders →
      // the after:render vpt-compare feeds the fx patch debounce), the
      // manual flag so ResizeObserver refits stop stomping the pan, then
      // the quarter-on-screen clamp.
      panBy: (dx, dy) => {
        if (!dx && !dy) return;
        canvas.relativePan(new Point(dx, dy));
        manualViewportRef.current = true;
        clampViewport();
      },
      setSnapping: (value) => {
        guidesRef.current?.setEnabled(value);
        try { localStorage.setItem('cce_snapping', value ? 'on' : 'off'); } catch (_) { /* ignore */ }
      },
      isSnappingEnabled: () => guidesRef.current?.isEnabled() ?? true,
      // ---- unsaved-work truth (app-bar lane B0: Save dot + leave guard) ----
      // dirty = history POSITION vs the last-saved marker (see the marker's
      // maintenance rules in save(): redo-tail discard, front-trims and
      // save('replace') can only push it to -1 — never false-clean). Undoing
      // back to the exact saved entry reads clean again, which is the truth.
      isDirty: () => historyIndexRef.current !== lastSavedIndexRef.current,
      // Called after a SUCCESSFUL library save (and only then): the entry at
      // the current position is now exactly what the library holds.
      markSaved: () => {
        lastSavedIndexRef.current = historyIndexRef.current;
        setLastSavedIndex(historyIndexRef.current);
      },
      // ---- poster export DPI (Bar 0 Poster/Print flyouts) ----
      // Rides the workspace's data bag (JSON_KEYS serializes `data`, same
      // slot as posterLight) so the choice saves with the design; setting it
      // is a real document edit — one history entry via save()'s dedupe.
      getExportDPI: () => {
        const ws = canvas.getObjects().find((o) => o.name === 'clip');
        const v = ws?.data?.exportDPI;
        return v === 150 || v === 300 || v === 600 ? v : 300;
      },
      setExportDPI: (v) => {
        if (v !== 150 && v !== 300 && v !== 600) return;
        const ws = canvas.getObjects().find((o) => o.name === 'clip');
        if (!ws) return;
        ws.set({ data: { ...ws.data, exportDPI: v } });
        save();
      },
      // ---- ruler guides (RS2 lane: dragged from the rulers) ----
      // Same data-bag slot family as exportDPI: workspace.data.guides =
      // [{axis:'h'|'v', pos}], pos in CANVAS UNITS from the POSTER's
      // top-left corner (the ruler origin law — ruler numbers and guide
      // labels agree exactly). Rides history snapshots + library saves via
      // JSON_KEYS' `data`; stock viewers ignore data. Guides are NEVER
      // fabric objects, so they can never appear in any export. Callers
      // write ONCE per gesture (add / move-release / remove) = one history
      // entry each; save() dedupes any no-op write.
      getGuides: () => {
        const ws = canvas.getObjects().find((o) => o.name === 'clip');
        return Array.isArray(ws?.data?.guides) ? ws.data.guides : [];
      },
      setGuides: (next) => {
        const ws = canvas.getObjects().find((o) => o.name === 'clip');
        if (!ws || !Array.isArray(next)) return;
        const guides = next
          .filter((g) => g && (g.axis === 'h' || g.axis === 'v') && Number.isFinite(g.pos))
          .map((g) => ({ axis: g.axis, pos: Math.round(g.pos * 100) / 100 }));
        ws.set({ data: { ...ws.data, guides } });
        // Repaint so the viewport chrome's after:render sync sees the new
        // array identity immediately (guides are DOM chrome, not canvas
        // pixels — no fabric object is ever created for them).
        canvas.requestRenderAll();
        save();
      },
    };
  }, [
    canRedo, canUndo, undo, redo, save, batch, autoZoomFit, clampViewport,
    copy, paste, canvas, fillColor, strokeWidth, strokeColor, selectedObjects,
    strokeDashArray, fontFamily, lastSavedIndex,
  ]);

  // 3D joystick wiring: hand the pads their facade slice (patch / re-bake /
  // commit) whenever the facade rebuilds — the WeakMap entry just refreshes.
  useEffect(() => {
    if (!canvas || !editor) return;
    setJoystick3dContext(canvas, {
      patchTextFx: editor.patchTextFx,
      regenerateText3d: editor.regenerateText3d,
      commit: editor.commit,
    });
  }, [canvas, editor]);
  // …and torn down with the CANVAS, not the facade (a facade identity churn
  // must not disarm the module-level dblclick guard mid-session).
  useEffect(() => {
    if (!canvas) return undefined;
    return () => teardownJoystick3d(canvas);
  }, [canvas]);

  // OS clipboard paste: images (screenshots, copied web/PowerPoint images)
  // beat the internal fabric clipboard, which beats plain text. Guards let
  // native paste through while any input or a canvas text is being edited
  // (fabric's IText editing focuses a hidden textarea whose paste bubbles
  // here — the input guard is mandatory, not defensive).
  useEffect(() => {
    if (!canvas || !editor) return;

    const handlePaste = (e) => {
      lastPasteEventRef.current = Date.now();
      const ae = document.activeElement;
      const isInput = ["INPUT", "TEXTAREA"].includes(ae?.tagName) || ae?.isContentEditable;
      if (isInput) return;
      if (canvas.getActiveObject()?.isEditing) return;
      if (document.querySelector(".cce-crop-modal-overlay")) return;

      const items = Array.from(e.clipboardData?.items || []);
      const imageItem = items.find((it) => it.type?.startsWith('image/'));
      if (imageItem) {
        e.preventDefault();
        const blob = imageItem.getAsFile();
        if (!blob) return;
        const reader = new FileReader();
        reader.onload = (ev) => editor.addImage(ev.target?.result);
        reader.readAsDataURL(blob);
        return;
      }
      if (clipboardRef.current) {
        e.preventDefault();
        paste();
        return;
      }
      const text = e.clipboardData?.getData('text/plain');
      if (text && text.trim()) {
        e.preventDefault();
        editor.addText(text.trim().slice(0, 2000));
      }
    };

    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [canvas, editor, paste]);

  // Initialize canvas
  const init = useCallback(({ initialCanvas, initialContainer }) => {
    // LIVE cache ceilings → the RUNTIME-probed device caps (resolution lane
    // 2026-07-17). Fabric's defaults (maxCacheSideLimit 4096, perfLimit
    // ~2.1Mpx) silently DIVIDE the cache zoom above them — measured: a DPR-2
    // headline rendered at 0.56× need, zoom 2.5 at 0.4×, and every fx stash
    // inherited the crushed scale via ctx.getTransform. side is min(device
    // canvas limit, 8192); area is the ~128MB RGBA memory budget (the
    // documented cap the probe verifies as binding past it). The export
    // bracket raises further, transiently (exportUtils).
    const liveCaps = getRenderCaps();
    config.maxCacheSideLimit = liveCaps.side;
    config.perfLimitSizeTotal = liveCaps.area;
    // Configure fabric defaults. v6 reads class defaults from the static
    // ownDefaults (getDefaults()), NOT the prototype — prototype writes are
    // silently ignored (v5 idiom).
    Object.assign(FabricObject.ownDefaults, {
      cornerColor: "#FFF",
      cornerStyle: "circle",
      borderColor: "#3b82f6",
      borderScaleFactor: 1.5,
      transparentCorners: false,
      borderOpacityWhenMoving: 1,
      cornerStrokeColor: "#3b82f6",
      // Rotation snaps to 15° steps (Canva-style predictable angles)
      snapAngle: 15,
    });

    // Smart guides + lock badges (contextTop overlays; export-safe — the
    // modules null-guard the context that toCanvasElement nulls)
    guidesRef.current?.dispose?.();
    let snappingEnabled = true;
    try { snappingEnabled = localStorage.getItem('cce_snapping') !== 'off'; } catch (_) { /* ignore */ }
    guidesRef.current = initAligningGuidelines(initialCanvas, { enabled: snappingEnabled });
    initLockBadges(initialCanvas);
    manualViewportRef.current = false;

    // Create workspace rectangle
    const initialWorkspace = new Rect({
      width: initialWidth.current,
      height: initialHeight.current,
      name: "clip",
      fill: "white",
      selectable: false,
      hasControls: false,
      // Document-level defaults ride the workspace's data bag (`data` is in
      // JSON_KEYS): posterLight is the shared sun for effects with lightMode
      // 'poster' (getPosterLight/setPosterLight). Older designs load without
      // it — every reader sanitizes back to these same defaults.
      data: { posterLight: { ...POSTER_LIGHT_DEFAULTS } },
      shadow: new Shadow({
        color: "rgba(0,0,0,0.8)",
        blur: 5,
      }),
    });

    initialCanvas.setWidth(initialContainer.offsetWidth);
    initialCanvas.setHeight(initialContainer.offsetHeight);

    initialCanvas.add(initialWorkspace);
    initialCanvas.centerObject(initialWorkspace);
    initialCanvas.clipPath = initialWorkspace;

    setCanvas(initialCanvas);
    setContainer(initialContainer);

    // DEV ONLY: expose the live canvas + fabric classes for the automated
    // editor smoke harness (verify-pse-editor — asserts object counts /
    // serialized JSON / class-level ops across the fabric v5→v6 migration).
    // Dead code in production builds.
    if (import.meta.env.DEV) {
      window.__cceCanvas = initialCanvas;
      // Polygon/Path/Circle joined for the universal-effects probes (star /
      // path shapes drive the shape-kind cells).
      window.__cceFabric = { ActiveSelection, Group, Rect, Circle, Polygon, Path, Textbox, FabricImage, filters };
    }

    // Initial history state — a fresh (or freshly loaded) canvas is CLEAN:
    // the saved marker sits on the baseline entry.
    const currentState = JSON.stringify(initialCanvas.toObject(JSON_KEYS));
    canvasHistory.current = [currentState];
    historyIndexRef.current = 0;
    setHistoryIndex(0);
    lastSavedIndexRef.current = 0;
    setLastSavedIndex(0);

    // Load initial state if provided. Guarded so the per-object added/removed
    // events can't flood history (listeners may attach before the async
    // enliven completes), then re-seed history with the LOADED state as the
    // single baseline entry — undo must not walk back to the empty canvas.
    if (initialState.current) {
      const data = typeof initialState.current === 'string'
        ? JSON.parse(initialState.current)
        : initialState.current;
      // Texture fills saved elsewhere must not 404-reject this load
      retargetTexturePatterns(data);

      skipSave.current = true;
      ensureFontsForDesign(data).finally(() => {
        initialCanvas.loadFromJSON(data).then(() => {
          migrateLegacyLocks(initialCanvas);
          initialCanvas.getObjects().forEach(prepareSmartObject);
          initialCanvas.getObjects().forEach((o) => {
            if (isTextType(o.type)) {
              o.initDimensions?.();
              o.setCoords();
            }
          });
          rehydrateTextureFills(initialCanvas);
          // Poster bg: sync half (seam install + degrade color) runs BEFORE
          // the baseline snapshot below, so the baseline serializes the flat
          // fill; the pattern lands async and never saves.
          rehydrateWorkspaceBackground(initialCanvas);
          rehydrateCurvedText(initialCanvas);
          reattachFxDeep(initialCanvas.getObjects()); // live fx on fresh instances
          initialCanvas.renderAll();
          skipSave.current = false;
          const loadedState = JSON.stringify(initialCanvas.toObject(JSON_KEYS));
          canvasHistory.current = [loadedState];
          historyIndexRef.current = 0;
          setHistoryIndex(0);
          // The library design as loaded IS the saved state — clean baseline.
          lastSavedIndexRef.current = 0;
          setLastSavedIndex(0);
        }).catch((err) => {
          console.error('Initial design load failed:', err);
          skipSave.current = false;
          initialCanvas.renderOnAddRemove = true;
          window.dispatchEvent(new CustomEvent('cce-image-load-failed'));
        });
      });
    }
  }, []);

  return { init, editor };
};

export default useCanvasEditor;

// Export constants for use in other components
export {
  DEFAULT_WIDTH,
  DEFAULT_HEIGHT,
  FONT_FAMILY,
  FONT_SIZE,
  FILL_COLOR,
  STROKE_COLOR,
  JSON_KEYS,
};
